/**
 * Angel Ashes — Memorial step UI (cards, ashes, photo, engraving)
 * Split from booking-wizard.js for Shopify Theme Editor's 256 KB asset limit.
 * Loaded before booking-wizard.js from sections/booking-wizard.liquid.
 */
(function (global) {
  'use strict'

  function create(deps) {
    deps = deps || {}
    var escapeHtml = deps.escapeHtml
    var stepIntroHtml = deps.stepIntroHtml || function (headline, helperHtml, headlineClass) {
      var extra = headlineClass ? ' ' + String(headlineClass).trim() : ''
      var helper = helperHtml ? String(helperHtml) : ''
      return (
        '<div class="baw-step-intro">' +
        '<h2 class="baw-headline baw-wizard-title' + extra + '">' + escapeHtml(headline) + '</h2>' +
        helper +
        '</div>'
      )
    }
    var optionalSkipButtonHtml = deps.optionalSkipButtonHtml
    var formatMoney = deps.formatMoney
    var firstAvailableVariant = deps.firstAvailableVariant
    var stepCopyText = deps.stepCopyText
    var DEFAULT_STEP_COPY = deps.DEFAULT_STEP_COPY
    var parseLineList = deps.parseLineList
    var possessiveName = deps.possessiveName
    var isUsablePetName = deps.isUsablePetName
    var sizedShopifyImageUrl = deps.sizedShopifyImageUrl || function (url, width) {
      if (typeof AngelAshesBookingMemorials !== 'undefined' && AngelAshesBookingMemorials.sizedShopifyImageUrl) {
        return AngelAshesBookingMemorials.sizedShopifyImageUrl(url, width)
      }
      return url || ''
    }
    function memorialCdnPx(key, fallback) {
      var Mem = typeof AngelAshesBookingMemorials !== 'undefined' ? AngelAshesBookingMemorials : null
      var n = Mem && Mem[key]
      return n || fallback
    }

  /** Fixed photo-step GuidanceBar copy — never from Theme Editor. */
  var PHOTO_PRINT_GUIDANCE_LINE =
    'Preview only \u2014 final print placement may vary slightly.'

  /** Default caption for the TE engraving example lightbox. */
  var ENGRAVING_EXAMPLE_CAPTION_DEFAULT =
    'Example of our hand engraving. Lettering and placement will vary slightly.'

  var PHOTO_UPLOAD_MAX_BYTES = 8 * 1024 * 1024
  var PHOTO_UPLOAD_ACCEPT =
    'image/jpeg,image/png,image/webp,image/heic,image/heif,image/jpg'
  var PHOTO_UPLOAD_ACCEPT_SET = {
    'image/jpeg': true,
    'image/jpg': true,
    'image/png': true,
    'image/webp': true,
    'image/heic': true,
    'image/heif': true,
  }

  var MEMORIAL_CATEGORY_SUMMARY_LABELS = {
    urn: 'Urn',
    ink_clay: 'Ink & Clay',
    fur_feather: 'Fur or feather',
    jewellery: 'Jewellery',
    outdoor: 'Outdoor',
    portrait: 'Portrait',
  }

  function truncateEngravingLine(value, max) {
    var s = String(value == null ? '' : value)
    if (!max || s.length <= max) return s
    return s.slice(0, max)
  }

  /**
   * Hand-engraving input: strip newlines, keep letters (incl. accents), numbers,
   * spaces, and '&.,/- / apostrophes. No auto-case. Cap at max.
   */

  function sanitizeEngravingLine(value, max) {
    var s = String(value == null ? '' : value)
      .replace(/[\r\n\u2028\u2029]/g, '')
      // Latin letters + accents, digits, space, and the listed punctuation.
      .replace(/[^A-Za-z0-9\u00C0-\u024F\u1E00-\u1EFF\s'’&.,/-]/g, '')
    return truncateEngravingLine(s, max)
  }

  /** Combined Engraving Text for ops / cart backward compatibility. */

  function formatEngravingCombinedText(line1, line2) {
    var a = String(line1 || '').trim()
    var b = String(line2 || '').trim()
    if (a && b) return a + '\n' + b
    return a || b || ''
  }

  /** Split a legacy single-field engraving string into two lines. */

  function splitLegacyEngravingText(text) {
    var raw = String(text || '').replace(/\r\n/g, '\n').trim()
    if (!raw) return { line1: '', line2: '' }
    var nl = raw.indexOf('\n')
    if (nl === -1) {
      return { line1: sanitizeEngravingLine(raw, ENGRAVING_LINE1_MAX), line2: '' }
    }
    return {
      line1: sanitizeEngravingLine(raw.slice(0, nl).trim(), ENGRAVING_LINE1_MAX),
      line2: sanitizeEngravingLine(raw.slice(nl + 1).replace(/\n/g, ' ').trim(), ENGRAVING_LINE2_MAX),
    }
  }

  /**
   * Theme Editor hand-engraving example photo (section settings).
   * Link renders only when imageUrl is set — never hard-coded.
   */

  function normalizeEngravingExampleVisuals(raw) {
    var src = raw && typeof raw === 'object' ? raw : {}
    var imageUrl = src.imageUrl != null ? String(src.imageUrl).trim() : ''
    var linkLabel = src.linkLabel != null ? String(src.linkLabel).trim() : ''
    if (!linkLabel) linkLabel = 'See an engraving example'
    var caption
    if (Object.prototype.hasOwnProperty.call(src, 'caption')) {
      caption = src.caption == null ? '' : String(src.caption).trim()
    } else {
      caption = ENGRAVING_EXAMPLE_CAPTION_DEFAULT
    }
    return {
      imageUrl: imageUrl,
      linkLabel: linkLabel,
      caption: caption,
    }
  }

  /** Client-side limits mirrored from `/api/booking/upload-photo`. */
  var PHOTO_UPLOAD_MAX_BYTES = 8 * 1024 * 1024
  /**
   * Netlify Functions base64-encode binary bodies (~30% overhead), so the
   * platform cap is ~4.5MB of image bytes even though we advertise 8MB.
   * Shrink to this size before POST so A4 600dpi JPEGs (~5.3MB) succeed.
   */
  var PHOTO_UPLOAD_SAFE_BYTES = 3 * 1024 * 1024
  /** Long edge for client resize — 7in at 600 DPI, enough for 5×7 print. */
  var PHOTO_UPLOAD_MAX_EDGE = 4200
  var PHOTO_UPLOAD_TIMEOUT_MS = 45000
  var PHOTO_UPLOAD_ACCEPT =
    'image/jpeg,image/png,image/webp,image/heic,image/heif,image/jpg'
  var PHOTO_UPLOAD_ACCEPT_SET = {
    'image/jpeg': true,
    'image/jpg': true,
    'image/png': true,
    'image/webp': true,
    'image/heic': true,
    'image/heif': true,
  }

  function normalizeMemorialAddonItemPossessive(template) {
    if (!template) return template
    return String(template).replace(/\bfor your \{item\}/g, 'for the {item}')
  }

  /** Fill {item} in memorial add-on headlines; fall back when no product label.
   *  Photo/nameplate may still use {pet}/{pet_possessive} from Theme Editor —
   *  this helper resolves {item} when present.
   *  opts.included — included-nameplate ask (personalise voice, not “Add”). */

  function memorialAddonHeadlineFromCopy(type, itemLabel, config, opts) {
    var template
    var fallback
    var includedNameplate = !!(opts && opts.included)
    if (type === 'photo') {
      template = normalizeMemorialAddonItemPossessive(
        stepCopyText(config, 'memorialAddonPhotoHeadline')
      )
      fallback = 'Add a photo for the urn?'
    } else if (type === 'nameplate') {
      if (includedNameplate) {
        // Fixed short voice — no {item}; do not reuse the paid “Add a nameplate…” TE string.
        template = DEFAULT_STEP_COPY.memorialAddonNameplateIncludedHeadline
        fallback = 'Personalise the nameplate?'
      } else {
        template = normalizeMemorialAddonItemPossessive(
          stepCopyText(config, 'memorialAddonNameplateHeadline')
        )
        fallback = 'Add a nameplate?'
      }
    } else if (type === 'engraving') {
      // Ask copy is fixed (photo-like question) — ignore TE / stepCopy so stale
      // section settings (“Personalise the {item}”, etc.) cannot override.
      template = DEFAULT_STEP_COPY.memorialAddonEngravingHeadline
      fallback = 'Add engraving for the urn?'
    } else {
      return itemLabel ? 'Personalise the ' + itemLabel : 'Personalise'
    }
    // Blank Theme Editor copy stays blank — only fall back when {item} cannot resolve.
    if (!template) return ''
    if (type === 'photo' && template.indexOf('{item}') === -1) {
      return template
    }
    if (itemLabel && template.indexOf('{item}') !== -1) {
      return template.replace(/\{item\}/g, itemLabel)
    }
    if (itemLabel && template.indexOf('{item}') === -1) return template
    if (template.indexOf('{item}') !== -1) return fallback
    return template
  }

  function getMemorialCatalog(state, config) {
    if (typeof AngelAshesBookingMemorials === 'undefined' || !AngelAshesBookingMemorials.buildCatalog) {
      return { settings: config.memorials || {}, categories: [], byKey: {} }
    }
    if (!config.memorials || config.memorials.enabled === false) {
      return { settings: config.memorials || {}, categories: [], byKey: {} }
    }
    var catalog = AngelAshesBookingMemorials.buildCatalog(config, state)
    AngelAshesBookingMemorials.ensureCategoryDefaults(catalog, state, config)
    return catalog
  }

  /* ------------------------------- state ---------------------------------- */

  function getMemorialEntry(state, groupKey, mode) {
    if (typeof AngelAshesBookingMemorials !== 'undefined' && AngelAshesBookingMemorials.getEntry) {
      return AngelAshesBookingMemorials.getEntry(state, groupKey, mode)
    }
    if (!state.memorials[groupKey]) {
      state.memorials[groupKey] = {
        mode: mode || 'optional_single',
        items: [],
        addons: {},
        itemAddons: {},
        skippedOptional: false,
        _focusedAddonProductId: null,
        ashesPlacementMode: null,
        ashSplit: null,
        ashDestinationProductId: null,
        ashesReceivingProductIds: null,
        ashesPlacementUrnIds: null,
        _ashesPlacementError: null,
        _ashesPlacementModalOpen: false,
        _urnChangeOrAddPending: null,
      }
    }
    return state.memorials[groupKey]
  }

  function memorialUrnVesselCount(entry) {
    if (typeof AngelAshesBookingMemorials !== 'undefined' && AngelAshesBookingMemorials.urnVesselCount) {
      return AngelAshesBookingMemorials.urnVesselCount(entry)
    }
    var items = (entry && entry.items) || []
    var n = 0
    var i
    for (i = 0; i < items.length; i++) {
      var it = items[i]
      if (!it) continue
      if (it.included) {
        n += 1
        continue
      }
      var q = Math.floor(Number(it.quantity) || 1)
      n += q > 0 ? q : 1
    }
    return n
  }

  function memorialUrnDistinctCount(entry) {
    if (typeof AngelAshesBookingMemorials !== 'undefined' && AngelAshesBookingMemorials.urnDistinctCount) {
      return AngelAshesBookingMemorials.urnDistinctCount(entry)
    }
    var items = (entry && entry.items) || []
    var n = 0
    var i
    for (i = 0; i < items.length; i++) {
      if (items[i] && items[i].productId != null) n += 1
    }
    return n
  }

  /** Legacy `Yes` / `No` when 2+ urn vessels have an explicit placement; otherwise null. */

  function urnAshSplitDisplayValue(state) {
    var entry = state && state.memorials && state.memorials.urn
    if (!entry) return null
    if (typeof AngelAshesBookingMemorials !== 'undefined' && AngelAshesBookingMemorials.ashSplitDisplayValue) {
      if (AngelAshesBookingMemorials.bindPlacementWeight) {
        AngelAshesBookingMemorials.bindPlacementWeight(entry, state)
      }
      return AngelAshesBookingMemorials.ashSplitDisplayValue(entry)
    }
    if (memorialFullCapacityCount(entry, state) < 2) {
      entry.ashesPlacementMode = null
      entry.ashSplit = null
      entry.ashDestinationProductId = null
      return null
    }
    var mode = entry.ashesPlacementMode
    if (mode !== 'divided' && mode !== 'single_urn') {
      if (entry.ashSplit === 'yes') mode = 'divided'
      else if (entry.ashSplit === 'no') mode = 'single_urn'
      else return null
    }
    return mode === 'single_urn' ? 'No' : 'Yes'
  }

  /** Human ashes-placement summary for cart / review; null until answered. */

  function urnAshesPlacementDisplayValue(state) {
    var entry = state && state.memorials && state.memorials.urn
    if (!entry) return null
    if (typeof AngelAshesBookingMemorials !== 'undefined' && AngelAshesBookingMemorials.ashesPlacementDisplayValue) {
      return AngelAshesBookingMemorials.ashesPlacementDisplayValue(entry, null, state)
    }
    if (memorialFullCapacityCount(entry, state) < 2) return null
    var mode = entry.ashesPlacementMode
    if (mode !== 'divided' && mode !== 'single_urn') {
      if (entry.ashSplit === 'yes') mode = 'divided'
      else if (entry.ashSplit === 'no') mode = 'single_urn'
      else return null
    }
    if (mode === 'divided') return 'Ashes divided'
    var dest = urnAshDestinationDisplayValue(state)
    return dest ? 'Ashes in ' + dest + ' only' : null
  }

  /** Label of the vessel that receives ashes when mode is single_urn; otherwise null. */

  function urnAshDestinationDisplayValue(state) {
    var entry = state && state.memorials && state.memorials.urn
    if (!entry) return null
    if (typeof AngelAshesBookingMemorials !== 'undefined' && AngelAshesBookingMemorials.ashDestinationDisplayValue) {
      return AngelAshesBookingMemorials.ashDestinationDisplayValue(entry)
    }
    var mode = entry.ashesPlacementMode
    if (mode !== 'divided' && mode !== 'single_urn') {
      mode = entry.ashSplit === 'no' ? 'single_urn' : entry.ashSplit === 'yes' ? 'divided' : null
    }
    if (memorialFullCapacityCount(entry) < 2 || mode !== 'single_urn') {
      return null
    }
    var dest = entry.ashDestinationProductId
    if (dest == null) return null
    var item = (entry.items || []).find(function (it) {
      return it && String(it.productId) === String(dest)
    })
    if (!item) {
      entry.ashDestinationProductId = null
      return null
    }
    var label = item.label ? String(item.label).trim() : ''
    return label || null
  }

  /** Quiet ashes-placement copy for the summary rail / review (cart props stay verbose). */

  function memorialFullCapacityCount(entry, stateForWeight) {
    if (typeof AngelAshesBookingMemorials !== 'undefined') {
      if (AngelAshesBookingMemorials.bindPlacementWeight && stateForWeight) {
        AngelAshesBookingMemorials.bindPlacementWeight(entry, stateForWeight)
      }
      if (AngelAshesBookingMemorials.urnRoleVesselCount) {
        return AngelAshesBookingMemorials.urnRoleVesselCount(entry)
      }
      if (AngelAshesBookingMemorials.fullCapacityVesselCount) {
        return AngelAshesBookingMemorials.fullCapacityVesselCount(entry)
      }
    }
    return memorialUrnVesselCount(entry)
  }

  function ashesPlacementRailDisplayValue(state) {
    var entry = state && state.memorials && state.memorials.urn
    if (!entry) return null
    var M = typeof AngelAshesBookingMemorials !== 'undefined' ? AngelAshesBookingMemorials : null
    if (M && M.syncAshesPlacement) M.syncAshesPlacement(entry, null, state)
    if (M && M.ashesArrangementSummaryLines) {
      var arranged = M.ashesArrangementSummaryLines(entry, null, state)
      if (arranged && arranged.length) return arranged.join(' · ')
    }
    var mode = M && M.normalizeAshesPlacementMode ? M.normalizeAshesPlacementMode(entry) : null
    if (!mode) {
      mode = entry.ashesPlacementMode
      if (mode !== 'divided' && mode !== 'single_urn') {
        if (entry.ashSplit === 'yes') mode = 'divided'
        else if (entry.ashSplit === 'no') mode = 'single_urn'
        else return null
      }
    }
    if (mode === 'divided') return 'Divided between urns'
    var dest = urnAshDestinationDisplayValue(state)
    if (!dest) return null
    return 'In the ' + dest + ' only'
  }

  function ashesPlacementSummaryLines(state) {
    var entry = state && state.memorials && state.memorials.urn
    if (!entry) return []
    var M = typeof AngelAshesBookingMemorials !== 'undefined' ? AngelAshesBookingMemorials : null
    if (M && M.syncAshesPlacement) M.syncAshesPlacement(entry, null, state)
    var texts = []
    if (M && M.ashesArrangementSummaryLines) {
      texts = M.ashesArrangementSummaryLines(entry, null, state) || []
    }
    if (!texts.length) {
      var fallback = ashesPlacementRailDisplayValue(state)
      if (fallback) texts = [fallback]
    }
    var canEdit = memorialFullCapacityCount(entry) >= 2
    return texts.map(function (text, idx) {
      return {
        id: 'memorial:urn:ashes-placement' + (idx ? ':' + idx : ''),
        editStepId: 'memorial:urn',
        groupLabel: 'Memorials',
        memorialGroupKey: 'urn',
        memorialCategoryLabel: 'Urn',
        valueLabel: text,
        price: 0,
        hidePrice: true,
        isPlacement: true,
        showPlacementEdit: canEdit && idx === texts.length - 1,
      }
    })
  }

  function ashesPlacementSummaryLine(state) {
    var lines = ashesPlacementSummaryLines(state)
    return lines.length ? lines[0] : null
  }

  /**
   * Anchor ashes-placement after the last Urn product/add-on (not after all
   * keepsakes). Call after orderMemorialLinesForSummary.
   */
  function withAshesPlacementSummaryLine(lines, state) {
    var out = (lines || []).slice()
    var placement = ashesPlacementSummaryLine(state)
    if (!placement) return out
    var insertAt = -1
    for (var i = 0; i < out.length; i++) {
      var line = out[i]
      if (!line) continue
      if (line.isPlacement || String(line.id || '').indexOf(':ashes-placement') !== -1) continue
      var key = line.memorialGroupKey != null ? String(line.memorialGroupKey) : ''
      var id = String(line.id || '')
      if (key === 'urn' || id.indexOf('memorial:urn:') === 0) {
        insertAt = i + 1
      }
    }
    if (insertAt < 0) out.push(placement)
    else out.splice(insertAt, 0, placement)
    return out
  }

  /** Quiet summary/review subhead for a memorial category (TE review_label preferred). */
  var MEMORIAL_CATEGORY_SUMMARY_LABELS = {
    urn: 'Urn',
    ink_clay: 'Ink & Clay',
    fur_feather: 'Fur or feather',
    jewellery: 'Jewellery',
    outdoor: 'Outdoor',
    portrait: 'Portrait',
  }

  /**
   * Species-aware sidebar / review label for a memorial category.
   * Fur or feather and paw/nose/clay adapt from breed (birds soften paw/nose).
   */

  function memorialCategorySummaryLabel(cat, state) {
    if (!cat) return 'Keepsakes'
    var M = typeof AngelAshesBookingMemorials !== 'undefined' ? AngelAshesBookingMemorials : null
    if (
      M &&
      M.isFurFeatherMemorialCategory &&
      M.resolveFurFeatherPresentation &&
      M.isFurFeatherMemorialCategory(cat)
    ) {
      var ff = M.resolveFurFeatherPresentation(cat, state || {})
      if (ff && ff.reviewLabel) return ff.reviewLabel
    }
    if (
      M &&
      M.isPawNoseClayMemorialCategory &&
      M.resolvePawNoseClayPresentation &&
      M.isPawNoseClayMemorialCategory(cat)
    ) {
      var pnc = M.resolvePawNoseClayPresentation(cat, state || {})
      if (pnc && pnc.reviewLabel) return pnc.reviewLabel
    }
    var explicit = cat.reviewLabel ? String(cat.reviewLabel).trim() : ''
    if (explicit) return explicit
    if (MEMORIAL_CATEGORY_SUMMARY_LABELS[cat.key]) return MEMORIAL_CATEGORY_SUMMARY_LABELS[cat.key]
    var title = cat.title ? String(cat.title).trim() : ''
    return title || 'Keepsakes'
  }

  function findMemorialProductBlock(config, groupKey, productId) {
    var blocks = (config && config.memorialItemsByGroup && config.memorialItemsByGroup[groupKey]) || []
    var pid = String(productId)
    for (var i = 0; i < blocks.length; i++) {
      var b = blocks[i]
      var s = b && b.settings
      if (!s) continue
      if (s.product && String(s.product.id) === pid) return b
      if (s.product && s.product.handle && String(s.product.handle) === pid) return b
      // Colour 2–8 on the same Product block share Variant max kg overrides.
      for (var n = 2; n <= 8; n++) {
        var cp = s['colour_' + n + '_product']
        if (cp && String(cp.id) === pid) return b
        if (cp && cp.handle && String(cp.handle) === pid) return b
      }
    }
    return null
  }

  /* ------------------------------ nameplate fonts ------------------------------ */

  /** Name-line fonts offered to the family. Checkout shows `label` on visible
   *  `Nameplate Font` (Print / Script); hidden `_Nameplate_Font` still carries
   *  `id` (`alice` / `lavanderia`) for the ops print renderer.
   *  Sacramento was retired from the picker — `normalizeNameplateFont` and the ops
   *  print font map (`lib/nameplate-inscription.ts`) both fall an old `sacramento`
   *  cart value through to Alice, so past orders still render safely. */
  // Both @font-face declarations (sections/booking-wizard.liquid) load the exact
  // theme assets `Alice-Regular.ttf`/`.woff2` and `Lavanderia-Regular.woff2` — the
  // cssFamily names below must keep matching those @font-face `font-family` values.
  var NAMEPLATE_FONTS = [
    { id: 'alice', label: 'Print', cssFamily: "'Alice', Georgia, serif" },
    { id: 'lavanderia', label: 'Script', cssFamily: "'Lavanderia', cursive" },
  ]

  /** Second line is always the same body face — shown as helper text, not a picker. */
  var NAMEPLATE_BODY_FONT = 'source-serif'

  /** Default Second line dropdown copy when Theme Editor list is empty. */
  var NAMEPLATE_SECOND_LINE_SUGGESTIONS_DEFAULT = [
    'Forever loved',
    'Always in our hearts',
    'Gone but not forgotten',
    'Until we meet again',
    'Rest in peace',
    'Loved beyond words',
    'Forever in our hearts',
  ]
  var NAMEPLATE_SECOND_LINE_CUSTOM_VALUE = '__custom__'
  /** Engraving empty L2 option (first). Nameplate uses One/Two toggle instead. */
  var ENGRAVING_SECOND_LINE_NONE_OPTION_LABEL = 'No second line'

  function normalizePhotoPrintSize(value) {
    return String(value || '').trim() === '5x7' ? '5x7' : '4x6'
  }

  /** Optional PNG overlay for the photo step (transparent window). Blank = 4×6 preview. */
  function normalizePhotoMockupFrame(value) {
    var s = String(value == null ? '' : value).trim()
    return s || null
  }

  /** `client_choice` defers portrait/landscape to the family on the photo step. */

  function normalizePhotoOrientation(value) {
    var v = String(value || '').trim()
    if (v === 'landscape') return 'landscape'
    if (v === 'client_choice') return 'client_choice'
    return 'portrait'
  }

  /** Theme Editor default side of the toggle when orientation is `client_choice`.
   *  Unconfigured / unknown → landscape (matches Theme Editor schema default). */

  function normalizePhotoOrientationDefault(value) {
    var v = String(value || '').trim()
    if (v === 'portrait') return 'portrait'
    return 'landscape'
  }

  /** Short inches of the configured print size (long side is always 6 or 7). */

  function photoSizeInches(size) {
    return normalizePhotoPrintSize(size) === '5x7' ? { short: 5, long: 7 } : { short: 4, long: 6 }
  }

  /** CSS aspect-ratio (width / height) for the frame preview. */

  function photoAspectRatio(size, orientation) {
    var inches = photoSizeInches(size)
    return orientation === 'landscape'
      ? inches.long / inches.short
      : inches.short / inches.long
  }

  function photoSizeLabel(size) {
    var inches = photoSizeInches(size)
    return inches.short + ' × ' + inches.long + ' in'
  }

  /**
   * Orientation the family effectively gets.
   * Priority: draft/saved or manual choice → Theme Editor default → landscape.
   * Locked Portrait/Landscape settings win outright (no toggle).
   */

  function effectivePhotoOrientation(flags, photo) {
    var configured = normalizePhotoOrientation(flags && flags.photoOrientation)
    if (configured !== 'client_choice') return configured
    var chosen = String((photo && photo.orientation) || '').trim()
    if (chosen === 'landscape' || chosen === 'portrait') return chosen
    return normalizePhotoOrientationDefault(flags && flags.photoOrientationDefault)
  }

  /** Capitalized orientation prefix for summary / review (e.g. "Portrait "). */

  function memorialPhotoOrientationPrefix(addon) {
    var raw = String((addon && addon.orientation) || '').trim().toLowerCase()
    if (raw === 'landscape') return 'Landscape '
    if (raw === 'portrait') return 'Portrait '
    return ''
  }

  /** Sidebar summary copy for a memorial photo add-on — never show bare "Photo". */

  function memorialPhotoSummaryDisplay(addon) {
    addon = addon || {}
    var choiceId = String(addon.choiceId || '')
    var label = (addon.choiceLabel || '').toLowerCase().trim()
    var orient = memorialPhotoOrientationPrefix(addon)

    if (
      choiceId === 'no' ||
      label.indexOf('no thanks') !== -1 ||
      label === 'no' ||
      (label.indexOf('not') !== -1 && label.indexOf('photo') !== -1)
    ) {
      return { valueLabel: 'Photo not required', hidePrice: true, photoPreviewUrl: null }
    }

    // Prefer remote URL; keep blob local preview for sidebar/nameplate/review
    // while upload is in flight (otherwise thumbs show an empty white cutout).
    var previewUrl = addon.photoUrl || addon.localPreviewUrl || null
    if (previewUrl) {
      var rawOrient = String(addon.orientation || '').trim().toLowerCase()
      return {
        valueLabel: addon.uploading
          ? orient + 'Photo uploading\u2026'
          : orient + 'Photo',
        // Always preview from the original upload + orientation crop — never the
        // pre-cropped print master (same contract as the in-step stage).
        photoPreviewUrl: String(previewUrl).trim() || null,
        photoOrientation: rawOrient === 'landscape' ? 'landscape' : 'portrait',
        hidePrice: false,
      }
    }

    if (addon.uploading) {
      return { valueLabel: orient + 'Photo uploading\u2026', hidePrice: false, photoPreviewUrl: null }
    }

    return {
      valueLabel: orient + 'Photo \u2014 upload needed',
      hidePrice: false,
      photoPreviewUrl: null,
    }
  }

  /** Resolve photo/nameplate/engraving follow-ups + fee products from Product blocks / metafields. */

  function resolveMemorialItemAddonFlags(config, groupKey, item, catalog) {
    var flags = {
      needsPhoto: !!(item && item.needsPhoto),
      needsNameplate: !!(item && item.needsNameplate),
      needsEngraving: !!(item && item.needsEngraving),
      needsNameplateText: !!(item && item.needsNameplateText),
      nameplateIncluded: !!(item && item.nameplateIncluded),
      photoFeeProduct: (item && item.photoFeeProduct) || null,
      engravingFeeProduct: (item && item.engravingFeeProduct) || null,
      nameplateProductGold: (item && item.nameplateProductGold) || null,
      nameplateProductSilver: (item && item.nameplateProductSilver) || null,
      nameplateGoldLabel: (item && item.nameplateGoldLabel) || '',
      nameplateSilverLabel: (item && item.nameplateSilverLabel) || '',
      nameplatePreviewImageGold: (item && item.nameplatePreviewImageGold) || null,
      nameplatePreviewImageSilver: (item && item.nameplatePreviewImageSilver) || null,
      personaliseImage: (item && item.personaliseImage) || null,
      personaliseImageGold: (item && item.personaliseImageGold) || null,
      personaliseImageSilver: (item && item.personaliseImageSilver) || null,
      cardImageGold: (item && item.cardImageGold) || null,
      cardImageSilver: (item && item.cardImageSilver) || null,
      reviewBlankCardImage: (item && item.reviewBlankCardImage) || null,
      photoPrintSize: normalizePhotoPrintSize(item && item.photoPrintSize),
      photoOrientation: normalizePhotoOrientation(item && item.photoOrientation),
      photoOrientationDefault: normalizePhotoOrientationDefault(item && item.photoOrientationDefault),
      photoMockupFrame: normalizePhotoMockupFrame(item && item.photoMockupFrame),
      photoCardTitle: (item && item.photoCardTitle) || '',
      photoCardSubtitle: (item && item.photoCardSubtitle) || '',
      engravingCardTitle: (item && item.engravingCardTitle) || '',
      engravingCardSubtitle: (item && item.engravingCardSubtitle) || '',
    }
    if (!item || !item.productId) return flags

    var card = null
    if (catalog && catalog.byKey && catalog.byKey[groupKey]) {
      var cat = catalog.byKey[groupKey]
      if (typeof AngelAshesBookingMemorials !== 'undefined' && AngelAshesBookingMemorials.findCardInCategory) {
        card = AngelAshesBookingMemorials.findCardInCategory(cat, item.productId)
      } else {
        card = (cat.allCards || []).find(function (c) {
          return String(c.productId) === String(item.productId)
        }) || null
      }
    }
    if (card) {
      flags.needsPhoto = !!card.needsPhoto
      flags.needsNameplate = !!card.needsNameplate
      flags.needsEngraving = !!card.needsEngraving
      flags.needsNameplateText = !!card.needsNameplateText
      flags.nameplateIncluded = !!card.nameplateIncluded && flags.needsNameplate
      flags.photoFeeProduct = card.photoFeeProduct || flags.photoFeeProduct
      flags.engravingFeeProduct = card.engravingFeeProduct || flags.engravingFeeProduct
      flags.nameplateProductGold = card.nameplateProductGold || flags.nameplateProductGold
      flags.nameplateProductSilver = card.nameplateProductSilver || flags.nameplateProductSilver
      flags.nameplateGoldLabel = card.nameplateGoldLabel || flags.nameplateGoldLabel
      flags.nameplateSilverLabel = card.nameplateSilverLabel || flags.nameplateSilverLabel
      flags.nameplatePreviewImageGold = card.nameplatePreviewImageGold || flags.nameplatePreviewImageGold
      flags.nameplatePreviewImageSilver = card.nameplatePreviewImageSilver || flags.nameplatePreviewImageSilver
      flags.personaliseImage = card.personaliseImage || flags.personaliseImage
      flags.personaliseImageGold = card.personaliseImageGold || flags.personaliseImageGold
      flags.personaliseImageSilver = card.personaliseImageSilver || flags.personaliseImageSilver
      flags.cardImageGold = card.cardImageGold || flags.cardImageGold
      flags.cardImageSilver = card.cardImageSilver || flags.cardImageSilver
      flags.reviewBlankCardImage = card.reviewBlankCardImage || flags.reviewBlankCardImage
      flags.photoPrintSize = normalizePhotoPrintSize(card.photoPrintSize)
      flags.photoOrientation = normalizePhotoOrientation(card.photoOrientation)
      flags.photoOrientationDefault = normalizePhotoOrientationDefault(card.photoOrientationDefault)
      flags.photoMockupFrame = normalizePhotoMockupFrame(card.photoMockupFrame)
      flags.photoCardTitle = String(card.photoCardTitle || '').trim() || flags.photoCardTitle
      // Blank subtitle is intentional (hides the desc line) — do not fall through.
      flags.photoCardSubtitle = String(card.photoCardSubtitle || '').trim()
      flags.engravingCardTitle = String(card.engravingCardTitle || '').trim() || flags.engravingCardTitle
      flags.engravingCardSubtitle = String(card.engravingCardSubtitle || '').trim()
      if (!flags.photoFeeProduct || !flags.engravingFeeProduct) {
        var feeBlock = findMemorialProductBlock(config, groupKey, item.productId)
        if (feeBlock && feeBlock.settings) {
          if (!flags.photoFeeProduct) {
            flags.photoFeeProduct = feeBlock.settings.photo_fee_product || null
          }
          if (!flags.engravingFeeProduct) {
            flags.engravingFeeProduct = feeBlock.settings.engraving_fee_product || null
          }
        }
      }
      return flags
    }

    var block = findMemorialProductBlock(config, groupKey, item.productId)
    var product = (block && block.settings && block.settings.product) || null
    if (typeof AngelAshesBookingMemorials !== 'undefined' && AngelAshesBookingMemorials.addonFlagsForProduct) {
      return AngelAshesBookingMemorials.addonFlagsForProduct(product, block)
    }
    if (block && block.settings) {
      flags.needsPhoto = !!block.settings.needs_photo
      flags.needsNameplate = !!block.settings.needs_nameplate
      flags.needsEngraving = !!block.settings.needs_engraving
      flags.nameplateIncluded = !!block.settings.nameplate_included && flags.needsNameplate
      flags.needsNameplateText = !!block.settings.needs_nameplate_text && flags.needsNameplate && !flags.needsEngraving
      flags.photoFeeProduct = block.settings.photo_fee_product || null
      flags.engravingFeeProduct = block.settings.engraving_fee_product || null
      flags.nameplateProductGold = block.settings.nameplate_product_gold || null
      flags.nameplateProductSilver = block.settings.nameplate_product_silver || null
      flags.nameplateGoldLabel = String(block.settings.nameplate_gold_label || '').trim()
      flags.nameplateSilverLabel = String(block.settings.nameplate_silver_label || '').trim()
      flags.nameplatePreviewImageGold = block.settings.nameplate_preview_gold || null
      flags.nameplatePreviewImageSilver = block.settings.nameplate_preview_silver || null
      flags.personaliseImage = block.settings.personalise_image
        ? String(block.settings.personalise_image).trim() || null
        : null
      flags.personaliseImageGold = block.settings.personalise_image_gold
        ? String(block.settings.personalise_image_gold).trim() || null
        : null
      flags.personaliseImageSilver = block.settings.personalise_image_silver
        ? String(block.settings.personalise_image_silver).trim() || null
        : null
      flags.cardImageGold = block.settings.card_image_gold
        ? String(block.settings.card_image_gold).trim() || null
        : null
      flags.cardImageSilver = block.settings.card_image_silver
        ? String(block.settings.card_image_silver).trim() || null
        : null
      flags.reviewBlankCardImage = block.settings.review_blank_card_image
        ? String(block.settings.review_blank_card_image).trim() || null
        : null
      flags.photoPrintSize = normalizePhotoPrintSize(block.settings.photo_print_size)
      flags.photoOrientation = normalizePhotoOrientation(block.settings.photo_orientation)
      flags.photoOrientationDefault = normalizePhotoOrientationDefault(block.settings.photo_orientation_default)
      flags.photoMockupFrame = normalizePhotoMockupFrame(block.settings.photo_mockup_frame)
      flags.photoCardTitle = String(block.settings.photo_card_title || '').trim()
      flags.photoCardSubtitle = String(block.settings.photo_card_subtitle || '').trim()
      flags.engravingCardTitle = String(block.settings.engraving_card_title || '').trim()
      flags.engravingCardSubtitle = String(block.settings.engraving_card_subtitle || '').trim()
    }
    return flags
  }

  /** Nameplate products to search for a chosen variant (Gold and/or Silver). */

  function findVariantById(product, variantId) {
    if (!product || !Array.isArray(product.variants) || variantId == null) return null
    for (var i = 0; i < product.variants.length; i++) {
      var v = product.variants[i]
      if (v && String(v.id) === String(variantId) && v.available !== false) return v
    }
    return null
  }

  /**
   * Photo / engraving fee SKU for display (summary / Printed photo card).
   * Prefer an in-stock variant, but never hide a configured fee — sold-out /
   * unpublished fee products still have a price the family should see.
   * Cart submit posts via `sellableVariantId` in booking-wizard-cart.js:
   * inventory that would sell (untracked / continue / qty) is posted even when
   * Liquid `available` is false (Unlisted). Tracked + deny + 0 is not posted.
   */
  function stampFeeVariant(variant, product) {
    if (!variant) return variant
    var out = variant
    if (variant.productStatus == null && product && product.status != null) {
      out = Object.assign({}, variant, { productStatus: product.status })
    }
    return out
  }

  function firstPricedVariant(product) {
    if (!product || typeof product !== 'object') return null
    var variants = Array.isArray(product.variants) ? product.variants : []
    var i
    var v
    var priced = null
    for (i = 0; i < variants.length; i++) {
      v = variants[i]
      if (!v) continue
      if (v.available !== false) return stampFeeVariant(v, product)
      if (!priced && v.price != null) priced = v
    }
    if (priced) return stampFeeVariant(priced, product)
    if (variants[0]) return stampFeeVariant(variants[0], product)
    if (product.price != null) {
      return stampFeeVariant(
        { id: product.variantId || null, price: product.price, available: false },
        product
      )
    }
    return null
  }

  function findFeeVariantById(product, variantId) {
    if (!product || !Array.isArray(product.variants) || variantId == null) return null
    for (var i = 0; i < product.variants.length; i++) {
      var v = product.variants[i]
      if (v && String(v.id) === String(variantId)) return v
    }
    return null
  }

  function resolvePhotoAddonVariant(addon, flags) {
    if (!addon || String(addon.choiceId) === 'no') return null
    var feeProduct = flags && flags.photoFeeProduct
    var id = (addon.variant && addon.variant.id) || addon.variantId || null
    if (id && feeProduct && typeof feeProduct === 'object') {
      var found = findFeeVariantById(feeProduct, id)
      if (found) return stampFeeVariant(found, feeProduct)
    }
    if (addon.variant && (addon.variant.id != null || addon.variant.price != null)) {
      return stampFeeVariant(addon.variant, feeProduct)
    }
    if (!feeProduct || typeof feeProduct !== 'object') return null
    return firstPricedVariant(feeProduct)
  }

  /** Sidebar / review label — "Brushed Gold Nameplate", not the short tile title. */

  function isMemorialMultiSelectMode(mode) {
    if (typeof AngelAshesBookingMemorials !== 'undefined' && AngelAshesBookingMemorials.isMultiSelectMode) {
      return AngelAshesBookingMemorials.isMultiSelectMode(mode)
    }
    return mode === 'optional_multiple' || mode === 'required_multiple'
  }

  function isMemorialRequiredSelectionMode(mode) {
    if (typeof AngelAshesBookingMemorials !== 'undefined' && AngelAshesBookingMemorials.isRequiredSelectionMode) {
      return AngelAshesBookingMemorials.isRequiredSelectionMode(mode)
    }
    return mode === 'required_single' || mode === 'required_multiple'
  }

  /** Parse `memorial:{group}:addon:{type}` or `memorial:{group}:addon:{type}:{productId}`. */

  function parseMemorialAddonStepId(stepId) {
    var parts = String(stepId || '').split(':')
    if (parts[0] !== 'memorial' || parts[2] !== 'addon') return null
    return {
      groupKey: parts[1] || '',
      type: parts[3] || '',
      productId: parts[4] || null,
    }
  }

  function memorialAddonStepId(groupKey, type, productId) {
    var id = 'memorial:' + groupKey + ':addon:' + type
    if (productId != null && String(productId) !== '') id += ':' + productId
    return id
  }

  /** Retarget entry.addons to a per-product bucket. Mutating handlers must call
   *  this before writing — summary/ledger walks can leave focus on another urn. */

  function focusMemorialAddonProduct(entry, productId) {
    if (typeof AngelAshesBookingMemorials !== 'undefined' && AngelAshesBookingMemorials.focusAddonProduct) {
      return AngelAshesBookingMemorials.focusAddonProduct(entry, productId)
    }
    if (!entry) return null
    if (!entry.addons) entry.addons = {}
    return entry.addons
  }

  /** Selected item (+ flags) for an addon type, optionally scoped to one product. */

  function memorialAddonSourceForProduct(state, config, groupKey, catalog, type, productId) {
    var entry = getMemorialEntry(state, groupKey)
    var items = entry.items || []
    for (var i = 0; i < items.length; i++) {
      if (productId != null && String(items[i].productId) !== String(productId)) continue
      if (items[i].included) continue
      var flags = resolveMemorialItemAddonFlags(config, groupKey, items[i], catalog)
      if (type === 'photo' && flags.needsPhoto) return { item: items[i], flags: flags, index: i }
      if (type === 'nameplate' && flags.needsNameplate) return { item: items[i], flags: flags, index: i }
      if (type === 'engraving' && flags.needsEngraving) return { item: items[i], flags: flags, index: i }
    }
    return null
  }

  /** First selected non-included item that needs the given addon type. */

  function firstMemorialAddonSource(state, config, groupKey, catalog, type) {
    return memorialAddonSourceForProduct(state, config, groupKey, catalog, type, null)
  }

  function resolveMemorialAddonProductId(state, config, groupKey, type, productId) {
    if (productId != null && String(productId) !== '') return String(productId)
    var catalog = getMemorialCatalog(state, config)
    var source = firstMemorialAddonSource(state, config, groupKey, catalog, type)
    return source && source.item && source.item.productId != null
      ? String(source.item.productId)
      : null
  }

  /** Ledger line id of the memorial product an addon nests under (summary / review). */

  function memorialAddonParentLineId(state, config, cat, catalog, type, productId) {
    var entry = getMemorialEntry(state, cat.key, cat.selectionMode)
    var items = entry.items || []
    if (!items.length) return null
    var source = memorialAddonSourceForProduct(state, config, cat.key, catalog, type, productId)
    if (source && typeof source.index === 'number' && source.index >= 0) {
      return 'memorial:' + cat.key + ':' + source.index
    }
    if (productId != null) {
      for (var i = 0; i < items.length; i++) {
        if (String(items[i].productId) === String(productId)) {
          return 'memorial:' + cat.key + ':' + i
        }
      }
    }
    return 'memorial:' + cat.key + ':0'
  }

  function isMemorialAddonLineId(id) {
    return String(id || '').indexOf(':addon:') !== -1
  }

  /**
   * Reorder memorial ledger lines so photo / nameplate / engraving sit directly
   * under their parent product (supports multiple memorials each with add-ons).
   */

  function nestMemorialLines(lines) {
    var products = []
    var addonsByParent = {}
    var orphanAddons = []
    ;(lines || []).forEach(function (line) {
      if (!line) return
      if (isMemorialAddonLineId(line.id)) {
        var parent = line.nestedUnder || null
        var nested = Object.assign({}, line, { nested: true })
        if (parent) {
          if (!addonsByParent[parent]) addonsByParent[parent] = []
          addonsByParent[parent].push(nested)
        } else {
          orphanAddons.push(nested)
        }
        return
      }
      products.push(line)
    })
    var out = []
    var seen = {}
    products.forEach(function (p) {
      out.push(p)
      seen[p.id] = true
      ;(addonsByParent[p.id] || []).forEach(function (a) { out.push(a) })
    })
    Object.keys(addonsByParent).forEach(function (pid) {
      if (seen[pid]) return
      addonsByParent[pid].forEach(function (a) { out.push(a) })
    })
    orphanAddons.forEach(function (a) { out.push(a) })
    return out
  }

  /**
   * Product keepsakes that show as Included / free in summary & review.
   * Add-ons and hidePrice placement rows are excluded (sorted separately).
   */

  function isMemorialIncludedProductLine(line) {
    if (!line || isMemorialAddonLineId(line.id)) return false
    if (String(line.id || '').indexOf(':ashes-placement') !== -1) return false
    if (line.hidePrice && !line.included) return false
    if (line.included) return true
    var price = Number(line.price)
    return !isFinite(price) || price <= 0
  }

  /**
   * Summary/review memorial order: group by memorial category (catalog order),
   * keep multi-urn vessels in selection order so badges read 1→2→3 within Urn,
   * and nest add-ons under parents. Callers then run withAshesPlacementSummaryLine
   * so the ashes chip anchors under Urn (not after every keepsake).
   */

  function orderMemorialLinesForSummary(lines) {
    var products = []
    var addons = []
    ;(lines || []).forEach(function (line) {
      if (!line) return
      if (isMemorialAddonLineId(line.id)) {
        addons.push(line)
        return
      }
      if (String(line.id || '').indexOf(':ashes-placement') !== -1) return
      products.push(line)
    })

    var groupOrder = []
    var byGroup = {}
    products.forEach(function (line) {
      var key = line.memorialGroupKey != null && String(line.memorialGroupKey) !== ''
        ? String(line.memorialGroupKey)
        : '_other'
      if (!byGroup[key]) {
        byGroup[key] = []
        groupOrder.push(key)
      }
      byGroup[key].push(line)
    })

    var orderedProducts = []
    groupOrder.forEach(function (key) {
      var group = byGroup[key] || []
      var numbered = []
      var included = []
      var rest = []
      group.forEach(function (line) {
        if (line.selectionIndex != null && Number(line.selectionIndex) > 0) {
          numbered.push(line)
          return
        }
        if (isMemorialIncludedProductLine(line)) included.push(line)
        else rest.push(line)
      })
      if (numbered.length) {
        numbered.sort(function (a, b) {
          return Number(a.selectionIndex) - Number(b.selectionIndex)
        })
        orderedProducts = orderedProducts.concat(numbered, included, rest)
      } else {
        orderedProducts = orderedProducts.concat(included, rest)
      }
    })

    return nestMemorialLines(orderedProducts.concat(addons))
  }

  function resolvePersonaliseStepImage(source, colour) {
    if (typeof AngelAshesBookingMemorials !== 'undefined' && AngelAshesBookingMemorials.resolvePersonaliseStepImage) {
      return AngelAshesBookingMemorials.resolvePersonaliseStepImage(source, colour)
    }
    // Fallback: metal slot / Gold↔Silver derive / shared — never Photo mockup.
    return explicitPersonaliseMockupFrame(source, colour)
  }

  function resolvePersonaliseMockupFrame(source, colour) {
    if (typeof AngelAshesBookingMemorials !== 'undefined' && AngelAshesBookingMemorials.resolvePersonaliseMockupFrame) {
      return AngelAshesBookingMemorials.resolvePersonaliseMockupFrame(source, colour)
    }
    var metal = String(colour || '').trim().toLowerCase()
    var gold = source && (source.personaliseImageGold || source.personalise_image_gold)
    var silver = source && (source.personaliseImageSilver || source.personalise_image_silver)
    var shared = source && (source.personaliseImage || source.personalise_image)
    var mockup = source && (source.photoMockupFrame || source.photo_mockup_frame)
    gold = gold ? String(gold).trim() : ''
    silver = silver ? String(silver).trim() : ''
    shared = shared ? String(shared).trim() : ''
    if (metal === 'gold') {
      if (gold) return gold
      if (silver && /silver/i.test(silver)) {
        return silver.replace(/Silver/g, 'Gold').replace(/silver/g, 'gold')
      }
      if (shared && /gold/i.test(shared)) return shared
      if (shared && /silver/i.test(shared)) {
        return shared.replace(/Silver/g, 'Gold').replace(/silver/g, 'gold')
      }
    }
    if (metal === 'silver') {
      if (silver) return silver
      if (gold && /gold/i.test(gold)) {
        return gold.replace(/Gold/g, 'Silver').replace(/gold/g, 'silver')
      }
      if (shared && /silver/i.test(shared)) return shared
      if (shared && /gold/i.test(shared)) {
        return shared.replace(/Gold/g, 'Silver').replace(/gold/g, 'silver')
      }
    }
    if (shared) return shared
    if (!metal && gold) return gold
    if (!metal && silver) return silver
    return mockup ? String(mockup).trim() || null : null
  }

  /**
   * Photo-step / summary / review frame — committed metal personalise first
   * (incl. Gold↔Silver filename derivation), else Photo mockup, else personalise.
   */
  function resolvePhotoStepMockupFrame(source, colour) {
    if (typeof AngelAshesBookingMemorials !== 'undefined' && AngelAshesBookingMemorials.resolvePhotoStepMockupFrame) {
      return AngelAshesBookingMemorials.resolvePhotoStepMockupFrame(source, colour)
    }
    var metal = String(colour || '').trim().toLowerCase()
    var personalise = resolvePersonaliseMockupFrame(source, colour)
    if (metal && personalise) return personalise
    var mockup = source && (source.photoMockupFrame || source.photo_mockup_frame)
    if (mockup) return String(mockup).trim() || null
    return personalise
  }

  /** Gold/Silver/shared personalise artboards only — never Photo mockup fallback. */
  function explicitPersonaliseMockupFrame(source, colour) {
    if (!source) return null
    if (typeof AngelAshesBookingMemorials !== 'undefined' && AngelAshesBookingMemorials.resolveMetalPersonaliseUrl) {
      var metalUrl = AngelAshesBookingMemorials.resolveMetalPersonaliseUrl({
        shared: source.personaliseImage || source.personalise_image || null,
        gold: source.personaliseImageGold || source.personalise_image_gold || null,
        silver: source.personaliseImageSilver || source.personalise_image_silver || null,
      }, colour)
      if (metalUrl) return metalUrl
    }
    var metal = String(colour || '').trim().toLowerCase()
    var gold = source.personaliseImageGold || source.personalise_image_gold
    var silver = source.personaliseImageSilver || source.personalise_image_silver
    var shared = source.personaliseImage || source.personalise_image
    gold = gold ? String(gold).trim() : ''
    silver = silver ? String(silver).trim() : ''
    shared = shared ? String(shared).trim() : ''
    if (metal === 'gold') {
      if (gold) return gold
      if (shared && /gold/i.test(shared)) return shared
      if (shared && /silver/i.test(shared)) {
        return shared.replace(/Silver/g, 'Gold').replace(/silver/g, 'gold')
      }
    }
    if (metal === 'silver') {
      if (silver) return silver
      if (gold && /gold/i.test(gold)) {
        return gold.replace(/Gold/g, 'Silver').replace(/gold/g, 'silver')
      }
      if (shared && /silver/i.test(shared)) return shared
      if (shared && /gold/i.test(shared)) {
        return shared.replace(/Gold/g, 'Silver').replace(/gold/g, 'silver')
      }
    }
    if (shared) return shared
    if (!metal && gold) return gold
    if (!metal && silver) return silver
    return null
  }

  /** Preview opts for Ink/Clay dual-window personalise artboards on the photo step. */
  function photoStepMockupPreviewOpts(flags, colour) {
    flags = flags || {}
    var mockupFrame = resolvePhotoStepMockupFrame(flags, colour)
    if (!mockupFrame) return null
    var previewOpts = {
      printSize: normalizePhotoPrintSize(flags.photoPrintSize),
      frameUrl: mockupFrame,
    }
    var canChoose = normalizePhotoOrientation(flags.photoOrientation) === 'client_choice'
    // Photo Tribute (client_choice / tribute artboard): always native-tall —
    // rotate 90° for landscape. Must win over personalise-URL equality /
    // missing dedicated mockup, or the dual-window right-pane inset clips
    // the photo beside the wood in thumbs.
    // Ink/Clay (locked orientation): native-wide dual-window artboards.
    // Ink cutout is locked; do not retune.
    var tributeFrame = /photo[-_.\s]?tribute/i.test(String(mockupFrame || ''))
    if (canChoose || tributeFrame) {
      previewOpts.nativeTall = true
    } else {
      previewOpts.nativeWide = true
      if (/clay/i.test(String(mockupFrame || ''))) {
        previewOpts.clayWide = true
      } else if (
        /ink[-_.\s]?photo/i.test(String(mockupFrame || '')) ||
        (/ink/i.test(String(mockupFrame || '')) && /photo/i.test(String(mockupFrame || '')))
      ) {
        previewOpts.inkWide = true
      }
    }
    return previewOpts
  }

  /** Desktop split workspace (orient column | upload stage) for dual-window frames. */
  function photoStepUsesDualWindowLayout(flags, colour) {
    if (normalizePhotoOrientation(flags && flags.photoOrientation) === 'client_choice') return false
    var opts = photoStepMockupPreviewOpts(flags, colour)
    return !!(opts && opts.frameUrl && opts.nativeWide)
  }

  function memorialAddonItemContext(catalog, groupKey, source, colour) {
    if (!source || !source.item) {
      return {
        label: null,
        image: null,
        personaliseImage: null,
        personaliseImageGold: null,
        personaliseImageSilver: null,
        photoMockupFrame: null,
      }
    }
    var item = source.item
    var label = item.label || null
    var image = item.image || null
    // TE personalise thumbs — nameplate / engraving only (grid uses image).
    var personaliseImage = item.personaliseImage || null
    var personaliseImageGold = item.personaliseImageGold || null
    var personaliseImageSilver = item.personaliseImageSilver || null
    var cardImageGold = item.cardImageGold || null
    var cardImageSilver = item.cardImageSilver || null
    var photoMockupFrame = item.photoMockupFrame || null
    if (catalog && catalog.byKey && catalog.byKey[groupKey]) {
      var cat = catalog.byKey[groupKey]
      var card = null
      var Mfind = typeof AngelAshesBookingMemorials !== 'undefined' ? AngelAshesBookingMemorials : null
      if (Mfind && Mfind.findDisplayCardInCategory) {
        card = Mfind.findDisplayCardInCategory(cat, item.productId)
      }
      if (!card) {
        card = (cat.allCards || []).find(function (c) {
          return String(c.productId) === String(item.productId)
        })
      }
      if (card) {
        label = card.title || label
        if (Mfind && Mfind.resolveCardImage) {
          image =
            Mfind.resolveCardImage(card, card.activeOptionId || item.variantId) ||
            card.image ||
            image
        } else {
          image = card.image || image
        }
        personaliseImage = card.personaliseImage || personaliseImage
        personaliseImageGold = card.personaliseImageGold || personaliseImageGold
        personaliseImageSilver = card.personaliseImageSilver || personaliseImageSilver
        cardImageGold = card.cardImageGold || cardImageGold
        cardImageSilver = card.cardImageSilver || cardImageSilver
        photoMockupFrame = card.photoMockupFrame || photoMockupFrame
      }
    }
    if (source.flags) {
      if (!personaliseImage && source.flags.personaliseImage) {
        personaliseImage = source.flags.personaliseImage
      }
      if (!personaliseImageGold && source.flags.personaliseImageGold) {
        personaliseImageGold = source.flags.personaliseImageGold
      }
      if (!personaliseImageSilver && source.flags.personaliseImageSilver) {
        personaliseImageSilver = source.flags.personaliseImageSilver
      }
      if (!cardImageGold && source.flags.cardImageGold) {
        cardImageGold = source.flags.cardImageGold
      }
      if (!cardImageSilver && source.flags.cardImageSilver) {
        cardImageSilver = source.flags.cardImageSilver
      }
      if (!photoMockupFrame && source.flags.photoMockupFrame) {
        photoMockupFrame = source.flags.photoMockupFrame
      }
    }
    var M = typeof AngelAshesBookingMemorials !== 'undefined' ? AngelAshesBookingMemorials : null
    if (M && M.personaliseItemLabel) label = M.personaliseItemLabel(label)
    else if (label) label = String(label).split('|')[0].trim()
    var resolvedPersonalise = resolvePersonaliseStepImage({
      personaliseImage: personaliseImage,
      personaliseImageGold: personaliseImageGold,
      personaliseImageSilver: personaliseImageSilver,
    }, colour)
    if (M && M.resolveMetalCardImage && colour) {
      var metalCard = M.resolveMetalCardImage({
        card_image: image,
        card_image_gold: cardImageGold,
        card_image_silver: cardImageSilver,
        personalise_image: personaliseImage,
        personalise_image_gold: personaliseImageGold,
        personalise_image_silver: personaliseImageSilver,
      }, colour)
      if (metalCard) image = metalCard
    }
    return {
      label: label || null,
      image: image || null,
      personaliseImage: resolvedPersonalise || null,
      personaliseImageGold: personaliseImageGold || null,
      personaliseImageSilver: personaliseImageSilver || null,
      photoMockupFrame: photoMockupFrame || null,
    }
  }

  function memorialAddonHeadline(type, itemLabel, config, opts) {
    return memorialAddonHeadlineFromCopy(type, itemLabel, config, opts)
  }

  function renderMemorialPersonaliseIntroHtml(context) {
    // Prefer TE personalise/context image (e.g. clay back) over the grid product photo.
    var src = context && (context.personaliseImage || context.image)
    if (!src) return ''
    var alt = context.label ? escapeHtml(context.label) : ''
    // Same width as warmWizardImages personalise display URLs (cache hit on step mount).
    var thumbW =
      (typeof AngelAshesBookingMemorials !== 'undefined' &&
        AngelAshesBookingMemorials.MEMORIAL_GRID_THUMB_PX) ||
      memorialCdnPx('MEMORIAL_GRID_THUMB_PX', 360)
    return (
      '<div class="baw-personalise-product">' +
      '<img class="baw-personalise-product-img" src="' +
      escapeHtml(sizedShopifyImageUrl(src, thumbW)) +
      '" alt="' +
      alt +
      '" loading="eager" fetchpriority="high" decoding="async" width="168" height="168" />' +
      '</div>'
    )
  }

  /* ---------------------------- step sequence ------------------------------ */

  // Family-facing intake copy. Internal id `lonsdale` / productKey stay unchanged for ops.
  var LONSDALE_FAMILY_LABEL = 'Bring to Lonsdale'
  var LONSDALE_PRODUCT_DISPLAY_TITLE = 'Goodbye Room Time'
  var LONSDALE_SERVICE_HEADLINE = 'How long would you like with {pet}?'

  var TRANSPORT_CATEGORY_DEFS = [
    {
      id: 'lonsdale',
      label: LONSDALE_FAMILY_LABEL,
      productKey: 'transportLonsdale',
      // Quiet method-tile line — Theme Editor `lonsdale_hours_helper` overrides at runtime.
      tileDesc: 'Weekdays · 10am–1pm',
      requires_date: true,
      requires_time_window: true,
      requires_vet_clinic: false,
      requires_address_choice: false,
      serviceHeadline: LONSDALE_SERVICE_HEADLINE,
      // Default; Theme Editor `lonsdale_details_headline` overrides via config at runtime.
      detailsHeadline: 'When would you prefer to arrive?',
      dateLabel: 'Drop-off date',
      timeWindowLabel: 'Preferred arrival time',
    },
    {
      id: 'vet',
      label: 'Vet Collection',
      productKey: 'transportVet',
      tileDesc: 'We collect from your clinic',
      requires_date: true,
      requires_time_window: false,
      requires_vet_clinic: true,
      requires_address_choice: false,
      serviceHeadline: 'How would you like us to collect {pet}?',
      detailsHeadline: 'When will {pet} be ready for collection?',
      dateLabel: 'Ready for collection from',
    },
    {
      id: 'home',
      label: 'Home Collection',
      productKey: 'transportHome',
      tileDesc: 'We come to you',
      requires_date: true,
      requires_time_window: false,
      requires_vet_clinic: false,
      requires_address_choice: true,
      serviceHeadline: 'Choose your collection service',
      noteKey: 'home',
      defaultSmsNote: 'We\u2019ll SMS you to confirm your requested date and arrange a suitable time.',
      detailsHeadline: 'When and where should we collect {pet}?',
      dateLabel: 'Preferred collection date',
    },
  ]

  function isNoKeepsakeLabel(label) {
    var s = String(label || '').toLowerCase()
    return !s || /\bno\b/.test(s) && /keepsake|thanks|required|not/.test(s) || /^not\b/.test(s) || s === 'none'
  }

  // Review display only — Theme Editor memorial titles stay intact on the step / cart.
  // Kept local so this module does not depend on Cart (Cart is created later).
  function memorialReviewGroupLabel(label) {
    return String(label || '').replace(/^choose\s+a\s+keepsake\b/i, 'Keepsake')
  }

  function isTangibleMemorialItem(item) {
    if (!item || String(item.id || '').indexOf('memorial:') !== 0) return false
    if (String(item.id).indexOf(':addon:') !== -1) return false
    if (item.included) return true
    if (!item.variantId && isNoKeepsakeLabel(item.valueLabel)) return false
    return !!(item.variantId || item.included)
  }

  function buildMemorialGalleryItems(items, state, config) {
    var out = []
    ;(items || []).forEach(function (item) {
      if (!isTangibleMemorialItem(item)) return
      out.push({
        id: item.id,
        title: item.valueLabel || item.groupLabel || 'Memorial',
        subtitle: memorialReviewGroupLabel(item.groupLabel),
        image: item.image ? String(item.image) : '',
        price: item.included ? 0 : (Number(item.price) || 0),
        variantId: item.variantId,
        editStepId: item.editStepId,
        included: !!item.included,
      })
    })
    out.sort(function (a, b) {
      var aInc = isMemorialIncludedProductLine(a)
      var bInc = isMemorialIncludedProductLine(b)
      if (aInc && !bInc) return -1
      if (!aInc && bInc) return 1
      return 0
    })
    return out
  }

  function photoFileLooksHeic(file) {
    var type = String((file && file.type) || '').toLowerCase()
    if (type === 'image/heic' || type === 'image/heif') return true
    return /\.hei[cf]$/i.test(String((file && file.name) || ''))
  }

  function jpegFileFromBlob(blob, original) {
    var base = String((original && original.name) || 'photo').replace(/\.[^.]+$/, '')
    var name = base + '.jpg'
    try {
      return new File([blob], name, { type: 'image/jpeg', lastModified: Date.now() })
    } catch (err) {
      return blob
    }
  }

  function closePhotoSource(source) {
    if (source && typeof source.close === 'function') {
      try { source.close() } catch (err) { /* ignore */ }
    }
  }

  function canvasToJpegBlob(canvas, quality) {
    return new Promise(function (resolve, reject) {
      if (typeof canvas.toBlob === 'function') {
        canvas.toBlob(function (blob) {
          if (!blob || blob.size <= 0) reject(new Error('empty jpeg'))
          else resolve(blob)
        }, 'image/jpeg', quality)
        return
      }
      try {
        var dataUrl = canvas.toDataURL('image/jpeg', quality)
        var comma = dataUrl.indexOf(',')
        var bin = atob(dataUrl.slice(comma + 1))
        var bytes = new Uint8Array(bin.length)
        var i
        for (i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
        resolve(new Blob([bytes], { type: 'image/jpeg' }))
      } catch (err) {
        reject(err)
      }
    })
  }

  function drawPhotoToCanvas(source, maxEdge) {
    var sw = source.width || source.naturalWidth || 0
    var sh = source.height || source.naturalHeight || 0
    if (!sw || !sh) throw new Error('empty image')
    var longEdge = Math.max(sw, sh)
    var scale = longEdge > maxEdge ? maxEdge / longEdge : 1
    var dw = Math.max(1, Math.round(sw * scale))
    var dh = Math.max(1, Math.round(sh * scale))
    var canvas = document.createElement('canvas')
    canvas.width = dw
    canvas.height = dh
    var ctx2d = canvas.getContext('2d')
    if (!ctx2d) throw new Error('no canvas')
    ctx2d.drawImage(source, 0, 0, dw, dh)
    return canvas
  }

  function loadPhotoSourceFromImage(file) {
    return new Promise(function (resolve, reject) {
      var url = URL.createObjectURL(file)
      var img = new Image()
      img.onload = function () {
        URL.revokeObjectURL(url)
        resolve(img)
      }
      img.onerror = function () {
        URL.revokeObjectURL(url)
        reject(new Error('Could not read that photo.'))
      }
      img.src = url
    })
  }

  function loadPhotoSource(file) {
    if (typeof createImageBitmap !== 'function') {
      return loadPhotoSourceFromImage(file)
    }
    function fromBitmap(opts) {
      return createImageBitmap(file, opts)
    }
    return fromBitmap({ imageOrientation: 'from-image' }).catch(function () {
      return fromBitmap({})
    }).then(function (bmp) {
      var longEdge = Math.max(bmp.width, bmp.height)
      if (longEdge <= PHOTO_UPLOAD_MAX_EDGE) return bmp
      var scale = PHOTO_UPLOAD_MAX_EDGE / longEdge
      var rw = Math.max(1, Math.round(bmp.width * scale))
      var rh = Math.max(1, Math.round(bmp.height * scale))
      var resized = {
        imageOrientation: 'from-image',
        resizeWidth: rw,
        resizeHeight: rh,
        resizeQuality: 'high',
      }
      bmp.close()
      return fromBitmap(resized).catch(function () {
        return fromBitmap({ imageOrientation: 'from-image' }).catch(function () {
          return fromBitmap({})
        })
      })
    }).catch(function () {
      return loadPhotoSourceFromImage(file)
    })
  }

  function compressPhotoForUpload(file) {
    var qualities = [0.88, 0.8, 0.72]
    var edges = [PHOTO_UPLOAD_MAX_EDGE, 3200, 2400, 1800]
    return loadPhotoSource(file).then(function (source) {
      function attempt(edgeIndex, qualityIndex) {
        if (edgeIndex >= edges.length) {
          closePhotoSource(source)
          return Promise.reject(new Error('still too large'))
        }
        var canvas
        try {
          canvas = drawPhotoToCanvas(source, edges[edgeIndex])
        } catch (err) {
          closePhotoSource(source)
          return Promise.reject(err)
        }
        return canvasToJpegBlob(canvas, qualities[qualityIndex]).then(function (blob) {
          if (blob.size <= PHOTO_UPLOAD_SAFE_BYTES) {
            closePhotoSource(source)
            return jpegFileFromBlob(blob, file)
          }
          if (qualityIndex + 1 < qualities.length) {
            return attempt(edgeIndex, qualityIndex + 1)
          }
          return attempt(edgeIndex + 1, 0)
        })
      }
      return attempt(0, 0)
    })
  }

  /**
   * Keep files that already fit the Netlify binary cap as-is. Larger JPEGs
   * (typical A4 600dpi prints) are downscaled so the POST actually reaches
   * `/api/booking/upload-photo`. HEIC cannot be canvas-encoded in most browsers.
   */
  function preparePhotoForUpload(file) {
    if (!file) return Promise.reject(new Error('Please choose a photo to continue.'))
    if (file.size <= PHOTO_UPLOAD_SAFE_BYTES) return Promise.resolve(file)
    if (photoFileLooksHeic(file)) {
      return Promise.reject(new Error(
        'That HEIC photo is too large to upload. Please export it as a JPEG and try again.'
      ))
    }
    return compressPhotoForUpload(file).then(function (out) {
      if (out && out.size > 0 && out.size <= PHOTO_UPLOAD_MAX_BYTES) return out
      return Promise.reject(new Error(
        'We couldn\u2019t shrink that photo enough for upload. Please export a smaller JPEG and try again.'
      ))
    }, function () {
      return Promise.reject(new Error(
        'We couldn\u2019t prepare that high-resolution photo for upload. Please export a smaller JPEG and try again.'
      ))
    })
  }

    var api = {
      truncateEngravingLine: truncateEngravingLine,
      sanitizeEngravingLine: sanitizeEngravingLine,
      formatEngravingCombinedText: formatEngravingCombinedText,
      splitLegacyEngravingText: splitLegacyEngravingText,
      normalizeEngravingExampleVisuals: normalizeEngravingExampleVisuals,
      normalizeMemorialAddonItemPossessive: normalizeMemorialAddonItemPossessive,
      memorialAddonHeadlineFromCopy: memorialAddonHeadlineFromCopy,
      getMemorialCatalog: getMemorialCatalog,
      getMemorialEntry: getMemorialEntry,
      urnAshSplitDisplayValue: urnAshSplitDisplayValue,
      urnAshesPlacementDisplayValue: urnAshesPlacementDisplayValue,
      urnAshDestinationDisplayValue: urnAshDestinationDisplayValue,
      ashesPlacementRailDisplayValue: ashesPlacementRailDisplayValue,
      ashesPlacementSummaryLine: ashesPlacementSummaryLine,
      ashesPlacementSummaryLines: ashesPlacementSummaryLines,
      withAshesPlacementSummaryLine: withAshesPlacementSummaryLine,
      memorialCategorySummaryLabel: memorialCategorySummaryLabel,
      findMemorialProductBlock: findMemorialProductBlock,
      normalizePhotoPrintSize: normalizePhotoPrintSize,
      normalizePhotoOrientation: normalizePhotoOrientation,
      normalizePhotoOrientationDefault: normalizePhotoOrientationDefault,
      photoSizeInches: photoSizeInches,
      photoAspectRatio: photoAspectRatio,
      photoSizeLabel: photoSizeLabel,
      effectivePhotoOrientation: effectivePhotoOrientation,
      memorialPhotoOrientationPrefix: memorialPhotoOrientationPrefix,
      memorialPhotoSummaryDisplay: memorialPhotoSummaryDisplay,
      resolveMemorialItemAddonFlags: resolveMemorialItemAddonFlags,
      firstPricedVariant: firstPricedVariant,
      resolvePhotoAddonVariant: resolvePhotoAddonVariant,
      isMemorialMultiSelectMode: isMemorialMultiSelectMode,
      isMemorialRequiredSelectionMode: isMemorialRequiredSelectionMode,
      parseMemorialAddonStepId: parseMemorialAddonStepId,
      memorialAddonStepId: memorialAddonStepId,
      focusMemorialAddonProduct: focusMemorialAddonProduct,
      memorialAddonSourceForProduct: memorialAddonSourceForProduct,
      firstMemorialAddonSource: firstMemorialAddonSource,
      resolveMemorialAddonProductId: resolveMemorialAddonProductId,
      memorialAddonParentLineId: memorialAddonParentLineId,
      isMemorialAddonLineId: isMemorialAddonLineId,
      nestMemorialLines: nestMemorialLines,
      isMemorialIncludedProductLine: isMemorialIncludedProductLine,
      orderMemorialLinesForSummary: orderMemorialLinesForSummary,
      memorialAddonItemContext: memorialAddonItemContext,
      resolvePersonaliseStepImage: resolvePersonaliseStepImage,
      resolvePersonaliseMockupFrame: resolvePersonaliseMockupFrame,
      resolvePhotoStepMockupFrame: resolvePhotoStepMockupFrame,
      // LOCKED — memorial grid Continue → :addon:photo. install() runs under
      // with(ctx); these must stay on create() → __bawCtx or renderPhotoSetupHtml
      // throws and goTo rolls back (Continue looks like a no-op). Do not remove
      // unless intentionally rewriting the photo-step ctx contract.
      photoStepMockupPreviewOpts: photoStepMockupPreviewOpts,
      photoStepUsesDualWindowLayout: photoStepUsesDualWindowLayout,
      memorialAddonHeadline: memorialAddonHeadline,
      renderMemorialPersonaliseIntroHtml: renderMemorialPersonaliseIntroHtml,
      isTangibleMemorialItem: isTangibleMemorialItem,
      buildMemorialGalleryItems: buildMemorialGalleryItems,
      PHOTO_PRINT_GUIDANCE_LINE: PHOTO_PRINT_GUIDANCE_LINE,
      ENGRAVING_EXAMPLE_CAPTION_DEFAULT: ENGRAVING_EXAMPLE_CAPTION_DEFAULT,
      PHOTO_UPLOAD_MAX_BYTES: PHOTO_UPLOAD_MAX_BYTES,
      PHOTO_UPLOAD_SAFE_BYTES: PHOTO_UPLOAD_SAFE_BYTES,
      PHOTO_UPLOAD_MAX_EDGE: PHOTO_UPLOAD_MAX_EDGE,
      PHOTO_UPLOAD_TIMEOUT_MS: PHOTO_UPLOAD_TIMEOUT_MS,
      PHOTO_UPLOAD_ACCEPT: PHOTO_UPLOAD_ACCEPT,
      PHOTO_UPLOAD_ACCEPT_SET: PHOTO_UPLOAD_ACCEPT_SET,
      preparePhotoForUpload: preparePhotoForUpload,
      MEMORIAL_CATEGORY_SUMMARY_LABELS: MEMORIAL_CATEGORY_SUMMARY_LABELS,
      memorialUrnVesselCount: memorialUrnVesselCount,
      memorialUrnDistinctCount: memorialUrnDistinctCount
    }
    // Defense-in-depth: mirror onto the module so INSTALL_SRC can recover if a
    // future edit drops the ctx export (same Continue failure mode as above).
    if (typeof global.AngelAshesBookingMemorialUI === 'object' && global.AngelAshesBookingMemorialUI) {
      global.AngelAshesBookingMemorialUI.photoStepMockupPreviewOpts = photoStepMockupPreviewOpts
      global.AngelAshesBookingMemorialUI.photoStepUsesDualWindowLayout = photoStepUsesDualWindowLayout
      global.AngelAshesBookingMemorialUI.resolvePhotoStepMockupFrame = resolvePhotoStepMockupFrame
      global.AngelAshesBookingMemorialUI.resolvePersonaliseMockupFrame = resolvePersonaliseMockupFrame
      global.AngelAshesBookingMemorialUI.preparePhotoForUpload = preparePhotoForUpload
      global.AngelAshesBookingMemorialUI.firstPricedVariant = firstPricedVariant
      global.AngelAshesBookingMemorialUI.resolvePhotoAddonVariant = resolvePhotoAddonVariant
    }
    return api
  }

  var INSTALL_SRC = `    function firstPricedFeeVariant(product) {
      if (typeof AngelAshesBookingMemorialUI !== 'undefined' && AngelAshesBookingMemorialUI.firstPricedVariant) {
        return AngelAshesBookingMemorialUI.firstPricedVariant(product)
      }
      if (!product || typeof product !== 'object') return null
      var variants = Array.isArray(product.variants) ? product.variants : []
      var i
      var v
      var priced = null
      for (i = 0; i < variants.length; i++) {
        v = variants[i]
        if (!v) continue
        if (v.available !== false) return v
        if (!priced && v.price != null) priced = v
      }
      if (priced) return priced
      if (variants[0]) return variants[0]
      if (product.price != null) {
        return { id: product.variantId || null, price: product.price, available: false }
      }
      return null
    }

    function clearAshesPlacementModalHost() {
      root.querySelectorAll(
        '[data-baw-ashes-placement-modal], [data-baw-urn-change-or-add-modal]'
      ).forEach(function (el) {
        if (el.parentNode) el.parentNode.removeChild(el)
      })
    }

    function memorialUrnVesselCount(entry) {
      if (typeof AngelAshesBookingMemorials !== 'undefined' && AngelAshesBookingMemorials.urnVesselCount) {
        return AngelAshesBookingMemorials.urnVesselCount(entry)
      }
      var items = (entry && entry.items) || []
      var n = 0
      var i
      for (i = 0; i < items.length; i++) {
        var it = items[i]
        if (!it) continue
        if (it.included) {
          n += 1
          continue
        }
        var q = Math.floor(Number(it.quantity) || 1)
        n += q > 0 ? q : 1
      }
      return n
    }

    function memorialUrnDistinctCount(entry) {
      if (typeof AngelAshesBookingMemorials !== 'undefined' && AngelAshesBookingMemorials.urnDistinctCount) {
        return AngelAshesBookingMemorials.urnDistinctCount(entry)
      }
      var items = (entry && entry.items) || []
      var n = 0
      var i
      for (i = 0; i < items.length; i++) {
        if (items[i] && items[i].productId != null) n += 1
      }
      return n
    }

    function memorialFullCapacityCount(entry) {
      if (typeof AngelAshesBookingMemorials !== 'undefined') {
        if (AngelAshesBookingMemorials.bindPlacementWeight) {
          AngelAshesBookingMemorials.bindPlacementWeight(entry, state)
        }
        if (AngelAshesBookingMemorials.urnRoleVesselCount) {
          return AngelAshesBookingMemorials.urnRoleVesselCount(entry)
        }
        if (AngelAshesBookingMemorials.fullCapacityVesselCount) {
          return AngelAshesBookingMemorials.fullCapacityVesselCount(entry)
        }
      }
      return memorialUrnVesselCount(entry)
    }

    function memorialFullCapacityDistinctCount(entry) {
      if (typeof AngelAshesBookingMemorials !== 'undefined' && AngelAshesBookingMemorials.placementUrnItems) {
        return AngelAshesBookingMemorials.placementUrnItems(entry).length
      }
      if (typeof AngelAshesBookingMemorials !== 'undefined' && AngelAshesBookingMemorials.fullCapacityItems) {
        return AngelAshesBookingMemorials.fullCapacityItems(entry).length
      }
      return memorialUrnDistinctCount(entry)
    }

    function memorialMoney(cents) {
      if (cents == null) return ''
      return money(cents)
    }

    /**
     * One face per option — the catalog decides the face type, the card never
     * branches on product titles.
     */

    function memorialOptionFaceHtml(opt) {
      var face = (opt && opt.swatch) || {}
      if (face.type === 'quantity' || face.type === 'number') {
        var qtyLabel = face.label || opt.label || String(face.value || '')
        return (
          '<span class="baw-mpc-opt__face baw-mpc-opt__face--qty" aria-hidden="true">' +
          '<span class="baw-mpc-opt__qty">' + escapeHtml(qtyLabel) + '</span>' +
          '</span>'
        )
      }
      if (face.type === 'image' || face.type === 'thumb') {
        return (
          '<span class="baw-mpc-opt__face baw-mpc-opt__face--img" aria-hidden="true">' +
          '<img src="' + escapeHtml((typeof AngelAshesBookingMemorials !== 'undefined' && AngelAshesBookingMemorials.sizedShopifyImageUrl ? AngelAshesBookingMemorials.sizedShopifyImageUrl(String(face.value), AngelAshesBookingMemorials.MEMORIAL_GRID_THUMB_PX || 360) : String(face.value))) + '" alt="" width="40" height="40" loading="eager" decoding="async">' +
          '</span>'
        )
      }
      if (face.type === 'colour') {
        return (
          '<span class="baw-mpc-opt__face baw-mpc-opt__face--hex" style="background:' +
          escapeHtml(String(face.value)) + '" aria-hidden="true"></span>'
        )
      }
      /*
       * Last-resort face: a fixed two-character initialism. The face is only ~16px
       * wide at dense desktop widths, so anything longer ellipsises and colours
       * that share a first letter stop being distinguishable. The button's
       * aria-label still carries the full option label.
       */
      var labelText = String(face.value || opt.label || 'Option').trim()
      var shortLabel = labelText.slice(0, 2)
      return (
        '<span class="baw-mpc-opt__face baw-mpc-opt__face--label" aria-hidden="true">' +
        '<span class="baw-mpc-opt__label">' + escapeHtml(shortLabel) + '</span>' +
        '</span>'
      )
    }

    /**
     * Colour trays sit on the photo. Quantity / pack trays render in the card
     * body (same band as Qty) so the image can use the full stage.
     */

    function memorialOptionTrayHtml(card) {
      var opts = (card.options || []).filter(Boolean)
      if (opts.length < 2) return ''
      var activeId = String(card.activeOptionId || card.productId)
      var groupLabel =
        card.title + ' ' + String(card.optionLabel || 'option').toLowerCase()
      var isQty = String(card.optionType || '') === 'quantity'
      return (
        '<div class="baw-mpc-tray' + (isQty ? ' baw-mpc-tray--quantity' : '') + '" data-baw-mpc-tray role="radiogroup"' +
        ' data-baw-option-count="' + escapeHtml(String(opts.length)) + '"' +
        (isQty ? ' data-baw-option-type="quantity"' : '') +
        ' aria-label="' + escapeHtml(groupLabel) + '">' +
        opts.map(function (opt) {
          var active = String(opt.id) === activeId
          var optDisabled = opt.selectable === false
          var optIsQty = isQty || (opt.swatch && opt.swatch.type === 'quantity')
          return (
            '<button type="button" class="baw-mpc-opt' +
            (optIsQty ? ' baw-mpc-opt--qty' : '') +
            (active ? ' is-active' : '') +
            (optDisabled ? ' is-disabled' : '') +
            '" data-baw-mpc-option="' + escapeHtml(String(opt.id)) + '"' +
            ' data-baw-mpc-option-family="' + escapeHtml(String(card.swatchGroup || '')) + '"' +
            ' role="radio" aria-checked="' + (active ? 'true' : 'false') + '"' +
            ' tabindex="' + (active ? '0' : '-1') + '"' +
            (optDisabled ? ' disabled' : '') +
            ' aria-label="' + escapeHtml('Select ' + (opt.label || 'option')) + '">' +
            memorialOptionFaceHtml(opt) +
            '</button>'
          )
        }).join('') +
        '</div>'
      )
    }

    function memorialOrderQtyHtml(selected, quantity, maxQty) {
      var cap = Number(maxQty) || 9
      if (!selected) {
        return '<span class="baw-mpc-qty baw-mpc-qty--slot" aria-hidden="true"></span>'
      }
      var qty = Math.floor(Number(quantity) || 1)
      if (qty < 1) qty = 1
      var atMax = qty >= cap
      return (
        '<div class="baw-mpc-qty" data-baw-mpc-qty>' +
        '<span class="baw-mpc-qty-label">Qty</span>' +
        '<button type="button" class="baw-mpc-qty-btn" data-baw-mpc-qty-delta="-1" aria-label="Decrease quantity">\u2212</button>' +
        '<span class="baw-mpc-qty-value">' + qty + '</span>' +
        '<button type="button" class="baw-mpc-qty-btn" data-baw-mpc-qty-delta="1"' +
        (atMax ? ' disabled' : '') +
        ' aria-label="Increase quantity">+</button>' +
        '</div>'
      )
    }

    /**
     * Single card component for every memorial / keepsake product.
     * Shell is a div so the tray's real buttons are never nested inside the
     * select control; a stretched transparent button covers the rest of the card.
     */

    function memorialProductCardHtml(card, selected, options) {
      options = options || {}
      var allowQty = !!options.allowQuantity
      var priceHtml = card.included
        ? '<span class="baw-mpc-price baw-mpc-price--included">Included</span>'
        : '<span class="baw-mpc-price">' + escapeHtml(memorialMoney(card.price)) + '</span>'
      // Meta line = colour / quantity the family chose (or why a card is unavailable).
      // Weight-tier size is auto-picked; the card photo follows that variant.
      var metaText = ''
      var metaKind = ''
      var Mem =
        typeof AngelAshesBookingMemorials !== 'undefined' ? AngelAshesBookingMemorials : null
      if (!card.selectable && card.eligibilityNote) {
        metaText = String(card.eligibilityNote).trim()
        metaKind = 'eligibility'
      } else if (options.autoRemainingPouch) {
        metaText = 'Remaining ashes'
        metaKind = 'capacity'
      }
      if (!metaText && card.supporting) {
        metaText = String(card.supporting).trim()
        metaKind = 'description'
      }
      var variantForAria = String(card.variantMeta || '').trim()
      var gridImage =
        Mem && Mem.resolveCardImage ? Mem.resolveCardImage(card) || card.image : card.image
      var thumbPx =
        Mem && Mem.MEMORIAL_GRID_THUMB_PX ? Mem.MEMORIAL_GRID_THUMB_PX : 360
      var eagerImg = options.eagerImage !== false
      var img = gridImage
        ? '<img class="baw-mpc-img" src="' +
          escapeHtml(sizedShopifyImageUrl(gridImage, thumbPx)) +
          '" alt="' +
          escapeHtml(card.title) +
          '" loading="' +
          (eagerImg ? 'eager' : 'lazy') +
          '" decoding="' +
          (eagerImg ? 'sync' : 'async') +
          '" width="320" height="320"' +
          (options.highPriority ? ' fetchpriority="high"' : '') +
          '>'
        : '<span class="baw-mpc-img baw-mpc-img--empty" aria-hidden="true"></span>'
      var trayHtml = memorialOptionTrayHtml(card)
      var isQtyTray = !!(trayHtml && String(card.optionType || '') === 'quantity')
      var qtyHtml = allowQty
        ? memorialOrderQtyHtml(selected, options.quantity, options.maxQuantity)
        : ''
      var ariaBase =
        card.title +
        (variantForAria ? ', ' + variantForAria : '') +
        (metaText ? ', ' + metaText : '') +
        (card.included ? ', Included' : card.price != null ? ', ' + memorialMoney(card.price) : '')
      if (selected) ariaBase += ', selected'
      if (selected && allowQty && Number(options.quantity) > 1) {
        ariaBase += ', quantity ' + String(options.quantity)
      }
      return (
        '<div class="baw-mpc' +
        (trayHtml && !isQtyTray ? ' has-tray' : '') +
        (isQtyTray ? ' has-qty-options' : '') +
        (allowQty ? ' has-qty' : '') +
        (selected ? ' is-selected' : '') +
        (card.selectable ? '' : ' is-disabled') +
        '" data-baw-mpc="' + escapeHtml(String(card.productId)) + '"' +
        (card.swatchGroup ? ' data-baw-mpc-family="' + escapeHtml(String(card.swatchGroup)) + '"' : '') +
        ' data-baw-mpc-option-type="' + escapeHtml(String(card.optionType || 'none')) + '">' +
        '<button type="button" class="baw-mpc-select" data-baw-mpc-select' +
        (card.selectable ? '' : ' disabled') +
        ' aria-pressed="' + (selected ? 'true' : 'false') + '"' +
        ' aria-label="' + escapeHtml(ariaBase) + '"></button>' +
        '<span class="baw-mpc-check" aria-hidden="true"></span>' +
        '<span class="baw-mpc-stage">' + img + (isQtyTray ? '' : trayHtml) + '</span>' +
        '<span class="baw-mpc-body">' +
        '<span class="baw-mpc-info">' +
        '<span class="baw-mpc-title">' + escapeHtml(card.title) + '</span>' +
        priceHtml +
        '</span>' +
        (metaText ? '<span class="baw-mpc-meta' + (metaKind ? ' baw-mpc-meta--' + metaKind : '') + '">' + escapeHtml(metaText) + '</span>' : '') +
        (isQtyTray ? trayHtml : '') +
        qtyHtml +
        '</span>' +
        '</div>'
      )
    }

    function openMemorialOverlay(html, onClose) {
      var host = root.querySelector('[data-baw-memorial-overlay]') || document.querySelector('[data-baw-memorial-overlay]')
      if (!host) return
      // Replacing overlay content (e.g. View All → detail) — drop prior Escape listener.
      if (typeof host.__bawMemorialOverlayTeardown === 'function') {
        host.__bawMemorialOverlayTeardown()
        host.__bawMemorialOverlayTeardown = null
      }
      host.hidden = false
      host.innerHTML = html
      root.classList.add('has-memorial-overlay')
      function close() {
        host.hidden = true
        host.innerHTML = ''
        root.classList.remove('has-memorial-overlay')
        document.removeEventListener('keydown', onKey)
        host.__bawMemorialOverlayTeardown = null
        if (typeof onClose === 'function') onClose()
      }
      function onKey(e) {
        if (e.key === 'Escape') close()
      }
      document.addEventListener('keydown', onKey)
      host.__bawMemorialOverlayTeardown = function () {
        document.removeEventListener('keydown', onKey)
      }
      host.querySelectorAll('[data-baw-memorial-close]').forEach(function (btn) {
        btn.addEventListener('click', close)
      })
      var panel = host.querySelector('[data-baw-memorial-panel]')
      if (panel) panel.focus()
      return { close: close, host: host }
    }

    function renderMemorialItemStep(groupKey) {
      var M = typeof AngelAshesBookingMemorials !== 'undefined' ? AngelAshesBookingMemorials : null
      var catalog = getMemorialCatalog(state, config)
      // Drop weight-/stock-incompatible ledger items (e.g. 10 kg max urn after pet → 20 kg).
      if (M && M.validateSelections && M.validateSelections(catalog, state)) {
        state.memorialUnavailableNotice = true
      }
      var cat = catalog.byKey[groupKey]
      if (!cat) {
        els.step.innerHTML = '<div class="baw-step-inner"><p class="baw-helper">This keepsake category is unavailable.</p></div>'
        return
      }
      var entry = getMemorialEntry(state, groupKey, cat.selectionMode)
      // Over-capacity keepsakes auto-select the included pouch; empty step stays empty.
      if (cat.key === 'urn' && M && M.syncAshesPlacement) {
        M.syncAshesPlacement(entry, catalog, state)
      }
      if (cat.key === 'urn') {
        entry.catalogSort = 'price_asc'
      }
      var isDesktop = window.matchMedia('(min-width: 1200px)').matches
      var featured = isDesktop ? cat.featuredDesktop : cat.featuredMobile
      if (cat.layout !== 'dual_groups' && M && M.visibleCategoryCards) {
        featured = M.visibleCategoryCards(cat, entry, catalog.settings, isDesktop)
      }
      var sortedAllCards = (M && M.sortCatalogCards)
        ? M.sortCatalogCards(cat.allCards, entry && entry.catalogSort)
        : (cat.allCards || [])
      // Keep already-selected urns visible even if weight/swatch collapse dropped
      // them from the featured/all list (sidebar can show Paws Tealight while the
      // grid only had the included pouch).
      if (M && M.ensureSelectedCatalogCards) {
        sortedAllCards = M.ensureSelectedCatalogCards(
          sortedAllCards,
          cat,
          entry,
          cat.allCardsFlat || cat.allCards || sortedAllCards
        )
        if (M.sortCatalogCards) {
          sortedAllCards = M.sortCatalogCards(sortedAllCards, entry && entry.catalogSort)
        }
      }
      var isUrnCategory = cat.key === 'urn'
      var interp = M ? M.interpolatePet : function (t) { return t }
      var furFeatherCopy = null
      if (
        M &&
        M.isFurFeatherMemorialCategory &&
        M.resolveFurFeatherPresentation &&
        M.isFurFeatherMemorialCategory(cat)
      ) {
        furFeatherCopy = M.resolveFurFeatherPresentation(cat, state)
      }
      var pawNoseClayCopy = null
      if (
        M &&
        M.isPawNoseClayMemorialCategory &&
        M.resolvePawNoseClayPresentation &&
        M.isPawNoseClayMemorialCategory(cat)
      ) {
        pawNoseClayCopy = M.resolvePawNoseClayPresentation(cat, state)
      }

      function cardSelectOptions(card) {
        var allowQty = !!(
          card &&
          card.selectable &&
          !card.included &&
          String(card.optionType || '') !== 'quantity' &&
          M &&
          M.categoryAllowsOrderQuantity &&
          M.categoryAllowsOrderQuantity(cat)
        )
        var qty = 1
        if (allowQty && M && M.selectedItemForCard) {
          var selectedItem = M.selectedItemForCard(entry, card)
          if (selectedItem) {
            qty = M.itemQuantity ? M.itemQuantity(selectedItem) : (selectedItem.quantity || 1)
          }
        }
        return {
          allowQuantity: allowQty,
          quantity: qty,
          maxQuantity: (M && M.MEMORIAL_ORDER_QTY_MAX) || 9,
        }
      }

      function cardListHtml(cards, listOpts) {
        listOpts = listOpts || {}
        var belowFoldLazy = !!listOpts.belowFoldLazy
        return cards.map(function (card, index) {
          var selected = M
            ? (M.isFamilyMemberSelected
              ? M.isFamilyMemberSelected(entry, card)
              : M.isSelected(entry, card.productId))
            : false
          var opts = cardSelectOptions(card)
          // First screen of urns / keepsakes must paint TE thumbs immediately.
          // View-all may lazy-load cards past the first two rows; src is still set.
          var eager = !belowFoldLazy || index < 8
          opts.eagerImage = eager
          opts.highPriority = eager && index < 8
          opts.autoRemainingPouch = !!(
            selected &&
            card.included &&
            entry &&
            entry._autoIncludedPouch
          )
          return memorialProductCardHtml(card, selected, opts)
        }).join('')
      }

      function cardsWithIncludedFirst(cards) {
        if (!cat.includedCard) return cards
        return [cat.includedCard].concat(cards)
      }

      /**
       * Grid wrapper. 2–4 products fill one row. 5+ wrap at 4 (two even rows).
       * Never force 1 column — a lone included pouch at cols=1 becomes a
       * full-bleed square with a floating check (looks like a broken grid).
       */
      function memorialGridHtml(cards, extraClass, listOpts) {
        var n = (cards && cards.length) || 0
        var preferred = Number(config.memorials && config.memorials.desktopColumns) || 3
        if (!isFinite(preferred) || preferred < 2) preferred = 3
        if (preferred > 8) preferred = 8
        var cols
        if (n <= 1) cols = preferred
        else if (n <= 4) cols = n
        else cols = 4
        return (
          '<div class="baw-mpc-grid' + (extraClass ? ' ' + extraClass : '') +
          '" style="--baw-mpc-desktop-cols:' + cols + '">' +
          cardListHtml(cards, listOpts) +
          '</div>'
        )
      }

      var gridHtml = ''
      if (cat.layout === 'dual_groups') {
        var featA = featured.filter(function (c) { return c.group === 'a' })
        var featB = featured.filter(function (c) { return c.group === 'b' })
        if (!featA.length) featA = cat.groupACards.slice(0, isDesktop ? 4 : 3)
        if (!featB.length) featB = cat.groupBCards.slice(0, isDesktop ? 4 : 3)
        gridHtml +=
          '<div class="baw-memorial-subgroup">' +
          '<h3 class="baw-memorial-subgroup-title">' + escapeHtml(cat.groupAHeading) + '</h3>' +
          memorialGridHtml(cardsWithIncludedFirst(featA)) + '</div>' +
          '<div class="baw-memorial-subgroup">' +
          '<h3 class="baw-memorial-subgroup-title">' + escapeHtml(cat.groupBHeading) + '</h3>' +
          memorialGridHtml(featB) + '</div>'
      } else {
        var inlineCards = isUrnCategory ? sortedAllCards : featured
        gridHtml += memorialGridHtml(cardsWithIncludedFirst(inlineCards))
      }

      var viewAllLabel = cat.viewAllLabel || 'View all'
      if (isUrnCategory && (!viewAllLabel || viewAllLabel === 'View all')) {
        viewAllLabel = 'View more'
      }
      // "Urn count" on the UI includes the always-present included pouch card.
      // Featured slicing for urns caps the non-included cards; this threshold
      // keeps behaviour unchanged for <= 8 while enabling the button at >= 9.
      var urnFeaturedLimit = isDesktop
        ? ((cat.featuredLimitDesktop || (catalog.settings && catalog.settings.featuredLimitDesktop)) || featured.length)
        : ((cat.featuredLimitMobile || (catalog.settings && catalog.settings.featuredLimitMobile)) || featured.length)
      var urnIncludedCount = cat.includedCard ? 1 : 0
      var urnTotalCountForThreshold = (sortedAllCards.length || 0) + urnIncludedCount
      var showViewAll = !isUrnCategory && (cat.layout === 'dual_groups'
        ? !!(cat.showViewAll && cat.hasMoreThanFeatured)
        : !!(
          cat.showViewAll &&
          sortedAllCards.length > featured.length
        ))
      var viewAllBtnBase = showViewAll
        ? '<button type="button" class="baw-memorial-viewall" data-baw-memorial-viewall>' +
          escapeHtml(viewAllLabel) + '</button>'
        : ''
      var viewAllIntroBtn = ''
      var viewAllBtn = viewAllBtnBase

      // Skip is only for fully optional categories — never on required picks (e.g. urn).
      // Under the option cards (not the footer); hidden once a keepsake is selected.
      var hasKeepsakeSelected = !!(entry.items && entry.items.length)
      var skipBtn = ''
      if (!isMemorialRequiredSelectionMode(cat.selectionMode) && !hasKeepsakeSelected) {
        var skipLabel =
          (pawNoseClayCopy && pawNoseClayCopy.skipLabel) ||
          (furFeatherCopy && furFeatherCopy.skipLabel) ||
          (catalog.settings && catalog.settings.skipLabel) ||
          'Continue without optional keepsakes'
        skipBtn = optionalSkipButtonHtml(skipLabel, 'data-baw-memorial-skip')
      }

      // Ashes placement when 2+ vessels are selected — quiet confirm under the
      // grid; the placement question lives in a Start-over-style modal.
      var splitNote = ''
      var ashesPlacementModalHtml = ''
      var urnChangeOrAddModalHtml = ''
      var changeOrAddPending = entry._urnChangeOrAddPending
      if (
        cat.key === 'urn' &&
        changeOrAddPending &&
        changeOrAddPending.productId != null &&
        (entry.items || []).length >= 1
      ) {
        var pendingUrnLabel =
          changeOrAddPending.label ||
          (changeOrAddPending.card &&
            (changeOrAddPending.card.selectionLabel ||
              changeOrAddPending.card.title ||
              (changeOrAddPending.card.product && changeOrAddPending.card.product.title))) ||
          'this urn'
        var currentUrnCount = (entry.items || []).length
        // Thumb helper — same CDN width as memorial grid / summary (warm cache).
        function urnChangeOrAddThumbHtml(imageUrl) {
          var thumbW =
            (typeof AngelAshesBookingMemorials !== 'undefined' &&
              AngelAshesBookingMemorials.MEMORIAL_SUMMARY_THUMB_PX) ||
            360
          return imageUrl
            ? '<img class="baw-ashes-dest-card__thumb" src="' +
              escapeHtml(sizedShopifyImageUrl(imageUrl, thumbW)) +
              '" alt="" width="60" height="60" loading="eager" decoding="async">'
            : '<span class="baw-ashes-dest-card__thumb baw-ashes-dest-card__thumb--empty" aria-hidden="true"></span>'
        }
        function urnChangeOrAddImageForProduct(productId, fallbackItem, fallbackCard) {
          var pid = productId != null ? String(productId) : null
          var catalogCard = null
          if (pid && M && M.findCardInCategory) {
            catalogCard = M.findCardInCategory(cat, pid)
          } else if (pid && cat.includedCard && String(cat.includedCard.productId) === pid) {
            catalogCard = cat.includedCard
          } else if (pid) {
            catalogCard = (cat.allCards || []).find(function (c) {
              return String(c.productId) === pid
            }) || null
          }
          return (
            (fallbackCard && fallbackCard.image) ||
            (catalogCard && catalogCard.image) ||
            (fallbackItem && fallbackItem.image) ||
            (fallbackCard && fallbackCard.product && fallbackCard.product.image) ||
            (catalogCard && catalogCard.product && catalogCard.product.image) ||
            null
          )
        }
        var newUrnCard = changeOrAddPending.card || null
        var newUrnImage = urnChangeOrAddImageForProduct(
          changeOrAddPending.productId,
          null,
          newUrnCard
        )
        var currentUrnThumbsHtml = (entry.items || [])
          .map(function (it) {
            if (!it) return ''
            return urnChangeOrAddThumbHtml(
              urnChangeOrAddImageForProduct(it.productId, it, null)
            )
          })
          .join('')
        var replaceThumbsHtml =
          '<span class="baw-urn-change-or-add-thumbs" aria-hidden="true">' +
          urnChangeOrAddThumbHtml(newUrnImage) +
          '</span>'
        var addThumbsHtml =
          '<span class="baw-urn-change-or-add-thumbs baw-urn-change-or-add-thumbs--pair" aria-hidden="true">' +
          currentUrnThumbsHtml +
          urnChangeOrAddThumbHtml(newUrnImage) +
          '</span>'
        var changeOrAddFollow =
          currentUrnCount >= 2
            ? 'Would you like to use this urn instead, or add it to your selection?'
            : 'Would you like to use this urn instead, or keep both?'
        var replaceSupport =
          currentUrnCount >= 2
            ? 'Replace your previous urn choices'
            : 'Replace your previous urn choice'
        urnChangeOrAddModalHtml =
          '<div class="baw-reset-modal baw-urn-change-or-add-modal" data-baw-urn-change-or-add-modal="1" role="dialog" aria-modal="true" aria-labelledby="baw-urn-change-or-add-title">' +
          '<div class="baw-modal-backdrop baw-reset-modal-backdrop" data-baw-urn-change-or-add-dismiss tabindex="-1"></div>' +
          '<div class="baw-reset-modal-card baw-reset-modal-card--ashes-placement">' +
          '<button type="button" class="baw-reset-modal-close" data-baw-urn-change-or-add-dismiss aria-label="Close">' +
          '×</button>' +
          '<h2 class="baw-reset-modal-title" id="baw-urn-change-or-add-title" tabindex="-1">' +
          'Change urn or add another?</h2>' +
          '<p class="baw-reset-modal-body">' +
          'You chose <strong>' + escapeHtml(String(pendingUrnLabel)) + '</strong>.</p>' +
          '<p class="baw-reset-modal-body baw-reset-modal-body--follow">' +
          changeOrAddFollow +
          '</p>' +
          '<div class="baw-ashes-placement" data-baw-urn-change-or-add>' +
          '<div class="baw-ashes-placement__choices" role="group" aria-labelledby="baw-urn-change-or-add-title">' +
          '<button type="button" class="baw-ashes-placement-card" data-baw-urn-change-or-add-choice="replace">' +
          replaceThumbsHtml +
          '<span class="baw-ashes-placement-card__body">' +
          '<span class="baw-ashes-placement-card__title">Use this urn instead</span>' +
          '<span class="baw-ashes-placement-card__support">' +
          replaceSupport +
          '</span>' +
          '</span></button>' +
          '<button type="button" class="baw-ashes-placement-card" data-baw-urn-change-or-add-choice="add">' +
          addThumbsHtml +
          '<span class="baw-ashes-placement-card__body">' +
          '<span class="baw-ashes-placement-card__title">Add this urn too</span>' +
          '<span class="baw-ashes-placement-card__support">We\\u2019ll ask how to place the ashes</span>' +
          '</span></button>' +
          '</div></div>' +
          '</div></div>'
      }
      if (cat.key === 'urn' && memorialFullCapacityCount(entry) >= 2) {
        var placementCopy =
          (catalog.settings && catalog.settings.ashesPlacement) || {}
        var placementMode =
          typeof AngelAshesBookingMemorials !== 'undefined' && AngelAshesBookingMemorials.syncAshesPlacement
            ? AngelAshesBookingMemorials.syncAshesPlacement(entry, null, state)
            : (entry.ashesPlacementMode === 'divided' || entry.ashesPlacementMode === 'single_urn'
              ? entry.ashesPlacementMode
              : entry.ashSplit === 'yes'
                ? 'divided'
                : entry.ashSplit === 'no'
                  ? 'single_urn'
                  : null)
        var placementReady =
          typeof AngelAshesBookingMemorials !== 'undefined' && AngelAshesBookingMemorials.ashesPlacementReady
            ? AngelAshesBookingMemorials.ashesPlacementReady(entry)
            : !!(placementMode === 'divided' ||
              (placementMode === 'single_urn' && entry.ashDestinationProductId != null))
        var placementSummary =
          typeof AngelAshesBookingMemorials !== 'undefined' && AngelAshesBookingMemorials.ashesPlacementDisplayValue
            ? AngelAshesBookingMemorials.ashesPlacementDisplayValue(entry, null, state)
            : urnAshesPlacementDisplayValue(state)
        // Placement lives in the booking summary sidebar — do not repeat a chip under the cards.
        if (entry._ashesPlacementModalOpen) {
          var urnCount = memorialFullCapacityCount(entry)
          var distinctUrns = memorialFullCapacityDistinctCount(entry)
          var ashDestId =
            placementMode === 'single_urn' &&
            typeof AngelAshesBookingMemorials !== 'undefined' &&
            AngelAshesBookingMemorials.syncAshDestination
              ? AngelAshesBookingMemorials.syncAshDestination(entry)
              : (placementMode === 'single_urn' && entry.ashDestinationProductId != null
                ? String(entry.ashDestinationProductId)
                : null)
          var placementAsk = interp(
            placementCopy.question || 'How would you like us to place {pet}\\u2019s ashes?',
            state
          )
          // 2 urns: “Between both urns”. 3+: inject count so families notice over-selection.
          var divideTitle
          if (urnCount === 2) {
            divideTitle = placementCopy.divideTitleBoth || 'Between both urns'
          } else {
            var divideAllTpl = placementCopy.divideTitleAll || 'Between all {count} urns'
            // Migrate legacy TE defaults that said “selected urns” (keep one line).
            divideAllTpl = String(divideAllTpl).replace(/\\s+selected(?=\\s+urns\\b)/gi, '')
            if (divideAllTpl === 'Between all selected urns') {
              divideAllTpl = 'Between all {count} urns'
            }
            divideTitle = String(divideAllTpl).replace(/\\{count\\}/gi, String(urnCount))
          }
          var divideSupport =
            placementCopy.divideSupport || 'We\\u2019ll carefully divide the ashes between them.'
          var singleTitle = placementCopy.singleTitle || 'All in one urn'
          // Migrate legacy TE default (“Together…”) without requiring a merchant re-save.
          if (singleTitle === 'Together in one urn') singleTitle = 'All in one urn'
          var singleSupport =
            distinctUrns < 2
              ? (placementCopy.emptyUrnSingular || 'We\u2019ll return the extra urn empty.')
              : (placementCopy.singleSupport || 'Choose which urn should hold them.')
          if (singleSupport === 'You can choose which urn should hold them.') {
            singleSupport = 'Choose which urn should hold them.'
          }
          var placementError = entry._ashesPlacementError === 'mode'
            ? '<p class="baw-error" id="baw-ashes-placement-error" role="alert">' +
              escapeHtml(
                placementCopy.modeError ||
                'Please tell us how you would like the ashes placed.'
              ) +
              '</p>'
            : ''
          var destinationHtml = ''
          var emptyUrnGuidanceHtml = ''
          if (placementMode === 'single_urn') {
            var destAsk = interp(
              placementCopy.destinationQuestion || 'Which urn should hold {pet}\\u2019s ashes?',
              state
            )
            var destSupport = urnCount === 2
              ? (placementCopy.emptyUrnSingular || 'We\\u2019ll return the other urn empty.')
              : (placementCopy.emptyUrnPlural || 'We\\u2019ll return the other urns empty.')
            emptyUrnGuidanceHtml = wrapGuidanceBarHtml(
              '<p class="baw-guidance-line baw-step-footer-note">' +
              escapeHtml(destSupport) +
              '</p>',
              { variant: 'info', className: 'baw-ashes-placement-guidance' }
            )
            var destError = entry._ashesPlacementError === 'destination'
              ? '<p class="baw-error" id="baw-ashes-destination-error" role="alert">' +
                escapeHtml(
                  placementCopy.destinationError ||
                  'Please choose which urn should hold the ashes.'
                ) +
                '</p>'
              : ''
            var destItems =
              M && M.placementUrnItems
                ? M.placementUrnItems(entry)
                : M && M.fullCapacityItems
                  ? M.fullCapacityItems(entry)
                  : (entry.items || [])
            var destChoices = distinctUrns < 2 ? '' : destItems.map(function (it, destIdx) {
              if (!it || it.productId == null) return ''
              var pid = String(it.productId)
              var selected = ashDestId != null && String(ashDestId) === pid
              var label = it.styleTitle || it.label || 'Urn'
              var catalogCard = null
              if (M && M.findDisplayCardInCategory) {
                catalogCard = M.findDisplayCardInCategory(cat, pid)
              } else if (M && M.findCardInCategory) {
                catalogCard = M.findCardInCategory(cat, pid)
              } else if (cat.includedCard && String(cat.includedCard.productId) === pid) {
                catalogCard = cat.includedCard
              } else {
                catalogCard = (cat.allCards || []).find(function (c) {
                  return String(c.productId) === pid
                }) || null
              }
              var imageUrl =
                (catalogCard && catalogCard.image) ||
                it.image ||
                (catalogCard && catalogCard.product && catalogCard.product.image) ||
                null
              if (catalogCard) {
                label = catalogCard.title || catalogCard.styleTitle || catalogCard.selectionLabel || label
              }
              var thumbW =
                (typeof AngelAshesBookingMemorials !== 'undefined' &&
                  AngelAshesBookingMemorials.MEMORIAL_SUMMARY_THUMB_PX) ||
                360
              var thumb = imageUrl
                ? '<img class="baw-ashes-dest-card__thumb" src="' + escapeHtml(sizedShopifyImageUrl(imageUrl, thumbW)) + '" alt="" width="56" height="56" loading="eager" decoding="async">'
                : '<span class="baw-ashes-dest-card__thumb baw-ashes-dest-card__thumb--empty" aria-hidden="true"></span>'
              var destN = destIdx + 1
              return (
                '<button type="button" class="baw-ashes-dest-card' +
                (selected ? ' is-selected' : '') +
                '" data-baw-ash-destination="' + escapeHtml(pid) + '"' +
                ' role="radio" aria-checked="' + (selected ? 'true' : 'false') + '"' +
                ' aria-label="' + escapeHtml('Urn ' + destN + ', ' + label) + '">' +
                '<span class="baw-tile-check" aria-hidden="true"></span>' +
                thumb +
                '<span class="baw-ashes-dest-card__label">' +
                '<span class="baw-select-n" aria-hidden="true">' +
                escapeHtml(String(destN)) +
                '</span>' +
                '<span class="baw-ashes-dest-card__title">' + escapeHtml(label) + '</span>' +
                '</span>' +
                '</button>'
              )
            }).join('')
            destinationHtml = destChoices
              ? (
                '<div class="baw-memorial-ash-destination" data-baw-memorial-ash-destination role="radiogroup" aria-labelledby="baw-ash-destination-ask">' +
                '<p class="baw-ashes-placement__ask" id="baw-ash-destination-ask" tabindex="-1">' +
                escapeHtml(destAsk) +
                '</p>' +
                destError +
                '<div class="baw-ashes-dest-grid">' +
                destChoices +
                '</div>' +
                '</div>'
              )
              : ''
          }
          ashesPlacementModalHtml =
            '<div class="baw-reset-modal baw-ashes-placement-modal" data-baw-ashes-placement-modal="1" role="dialog" aria-modal="true" aria-labelledby="baw-ash-split-ask">' +
            '<div class="baw-modal-backdrop baw-reset-modal-backdrop" data-baw-ashes-placement-dismiss tabindex="-1"></div>' +
            '<div class="baw-reset-modal-card baw-reset-modal-card--ashes-placement">' +
            '<button type="button" class="baw-reset-modal-close" data-baw-ashes-placement-dismiss aria-label="Close">' +
            '<span aria-hidden="true">&times;</span></button>' +
            '<h2 class="baw-reset-modal-title" id="baw-ash-split-ask" tabindex="-1">' +
            escapeHtml(placementAsk) +
            '</h2>' +
            placementError +
            '<div class="baw-ashes-placement" data-baw-memorial-ash-split>' +
            '<div class="baw-ashes-placement__choices" role="radiogroup" aria-labelledby="baw-ash-split-ask">' +
            '<button type="button" class="baw-ashes-placement-card' +
            (placementMode === 'divided' ? ' is-selected' : '') +
            '" data-baw-ash-placement="divided" role="radio" aria-checked="' +
            (placementMode === 'divided' ? 'true' : 'false') + '">' +
            '<span class="baw-tile-check" aria-hidden="true"></span>' +
            '<span class="baw-ashes-placement-card__body">' +
            '<span class="baw-ashes-placement-card__title">' + escapeHtml(divideTitle) + '</span>' +
            '<span class="baw-ashes-placement-card__support">' + escapeHtml(divideSupport) + '</span>' +
            '</span>' +
            '</button>' +
            '<button type="button" class="baw-ashes-placement-card' +
            (placementMode === 'single_urn' ? ' is-selected' : '') +
            '" data-baw-ash-placement="single_urn" role="radio" aria-checked="' +
            (placementMode === 'single_urn' ? 'true' : 'false') + '">' +
            '<span class="baw-tile-check" aria-hidden="true"></span>' +
            '<span class="baw-ashes-placement-card__body">' +
            '<span class="baw-ashes-placement-card__title">' + escapeHtml(singleTitle) + '</span>' +
            '<span class="baw-ashes-placement-card__support">' + escapeHtml(singleSupport) + '</span>' +
            '</span>' +
            '</button>' +
            '</div>' +
            destinationHtml +
            '</div>' +
            emptyUrnGuidanceHtml +
            '<div class="baw-reset-modal-actions">' +
            '<button type="button" class="baw-reset-modal-confirm" data-baw-ashes-placement-confirm' +
            (placementReady ? '' : ' disabled aria-disabled="true"') +
            '>Confirm</button>' +
            '</div>' +
            '</div>' +
            '</div>'
        }
      }

      var notice = state.memorialUnavailableNotice
        ? wrapGuidanceBarHtml('<p class="baw-guidance-line">One of your previous keepsake selections is no longer available. Please choose another option.</p>')
        : ''

      var stepTitle = cat.title
      var stepSupporting = String(cat.supportingCopy || '').trim()
      var stepHelper = cat.helperText
      if (furFeatherCopy) {
        if (furFeatherCopy.title) stepTitle = furFeatherCopy.title
        if (furFeatherCopy.supportingCopy != null) {
          stepSupporting = String(furFeatherCopy.supportingCopy || '').trim()
        }
      } else if (pawNoseClayCopy) {
        if (pawNoseClayCopy.title) stepTitle = pawNoseClayCopy.title
        if (pawNoseClayCopy.supportingCopy != null) {
          stepSupporting = String(pawNoseClayCopy.supportingCopy || '').trim()
        }
      }
      if (
        M &&
        M.isGenericOptionalMemorialHelper &&
        M.isGenericOptionalMemorialHelper(stepHelper)
      ) {
        stepHelper = ''
      }

      var keepsakeNoticeHtml = ''
      if (cat.key === 'urn' && M && M.keepsakeNoticeCopy) {
        var knCopy = M.keepsakeNoticeCopy(entry, catalog, catalog.settings, state)
        if (knCopy && knCopy.body) {
          keepsakeNoticeHtml = wrapGuidanceBarHtml(
            '<p class="baw-guidance-line baw-guidance-heading">' + escapeHtml(knCopy.heading) + '</p>' +
            '<p class="baw-guidance-line">' + escapeHtml(knCopy.body) + '</p>',
            { variant: 'info', className: 'baw-guidance--keepsake', live: true, footer: true }
          )
        }
      }

      var stepHelperHtml =
        (stepSupporting ? '<p class="baw-helper">' + escapeHtml(interp(stepSupporting, state)) + '</p>' : '') +
        (stepHelper ? '<p class="baw-helper baw-helper--quiet">' + escapeHtml(interp(stepHelper, state)) + '</p>' : '') +
        viewAllIntroBtn
      var introHtml = stepIntroHtml(interp(stepTitle, state), stepHelperHtml)

      els.step.innerHTML =
        '<div class="baw-step-inner baw-memorial-step' +
        (cat.key === 'urn' ? ' baw-memorial-step--urn' : '') +
        '">' +
        introHtml +
        notice +
        (cat.emptyReason && (
          !cat.emptyReasonMerchant ||
          (typeof window !== 'undefined' && window.Shopify && window.Shopify.designMode)
        )
          ? wrapGuidanceBarHtml('<p class="baw-guidance-line">' + escapeHtml(cat.emptyReason) + '</p>')
          : '') +
        gridHtml +
        splitNote +
        viewAllBtn +
        skipBtn +
        keepsakeNoticeHtml +
        '<div class="baw-actions">' +
        '<button type="button" class="baw-continue" data-baw-continue>Continue</button>' +
        '</div>' +
        ashesPlacementModalHtml +
        urnChangeOrAddModalHtml +
        '</div>'

      function findMemorialCard(pid) {
        if (M && M.findCardInCategory) return M.findCardInCategory(cat, pid)
        if (cat.includedCard && String(cat.includedCard.productId) === String(pid)) {
          return cat.includedCard
        }
        if (cat.cardByProductId && cat.cardByProductId[String(pid)]) {
          return cat.cardByProductId[String(pid)]
        }
        return (cat.allCards || []).find(function (c) {
          return String(c.productId) === String(pid)
        }) || null
      }

      function findMemorialFamilyCard(familyKey, productId) {
        var cards = cat.allCards || []
        for (var i = 0; i < cards.length; i++) {
          var c = cards[i]
          if (!c || !c.swatches) continue
          if (familyKey && c.swatchGroup && String(c.swatchGroup) === String(familyKey)) return c
          if (productId != null && c.swatchMembers) {
            for (var j = 0; j < c.swatchMembers.length; j++) {
              if (c.swatchMembers[j] && String(c.swatchMembers[j].productId) === String(productId)) {
                return c
              }
            }
          }
        }
        return null
      }

      function cardNeedsDetail(card) {
        if (!card || card.included) return false
        if (config.memorials && config.memorials.enableDetailSheets === false) return false
        var personalise = !!(card.product && card.product.booking && card.product.booking.requiresPersonalisation)
        // Pack-size / finish variants already sit on the card tray.
        if (card.optionKind === 'variant' && (card.options || []).length > 1) return personalise
        // Colour / quantity families pick on the card — not a sheet.
        if (card.optionKind === 'family' && (card.options || []).length > 1) return personalise
        var variants = (card.product && card.product.variants) || []
        var pickerVariants =
          M && M.variantsForDetailPicker
            ? M.variantsForDetailPicker(card)
            : variants.filter(function (v) {
                return v && v.available !== false
              })
        var multiVariant = pickerVariants.length > 1
        // Weight cutoffs (TE override / title “up to X kg” / metafield) auto-picked
        // a size — skip the Options sheet (still show if product metafield requires
        // a personalisation detail pass).
        if (multiVariant && (card.weightAutoSelected || card.hasWeightCutoffs)) {
          return personalise
        }
        return multiVariant || personalise
      }

      function memorialItemBlockForCard(card) {
        if (!card) return null
        return findMemorialProductBlock(config, cat.key, card.productId)
      }

      function pickVariantForPet(card, variants) {
        var weight = state.pet && state.pet.weightKg != null ? Number(state.pet.weightKg) : null
        var block = memorialItemBlockForCard(card)
        var cutoffSettings =
          (card && card._variantCutoffSettings) ||
          (block && block.settings) ||
          block
        if (M && typeof M.resolveMemorialVariant === 'function' && card && card.product) {
          var resolved = M.resolveMemorialVariant(card.product, weight, cutoffSettings)
          if (resolved && resolved.variant) return resolved.variant
        }
        var list = (variants || []).filter(function (v) { return v && v.available !== false })
        if (!list.length) list = variants || []
        if (!list.length) return null
        if (!(weight > 0)) return list[0]
        var matched = null
        list.forEach(function (v) {
          var max = M && M.parseMaxKgFromVariantLabel
            ? M.parseMaxKgFromVariantLabel(v && v.title)
            : null
          if (max == null || !(max >= weight)) return
          if (!matched || max < matched.max) matched = { variant: v, max: max }
        })
        return (matched && matched.variant) || list[0]
      }

      function applyNameplateMetalToVariant(card, preferred) {
        if (!M || !card) return preferred
        var metal =
          (M.nameplateMetalForProduct && M.nameplateMetalForProduct(entry, card.productId)) ||
          'gold'
        if (M.variantForNameplateColour) {
          return M.variantForNameplateColour(card, metal, preferred) || preferred
        }
        return preferred
      }

      function memorialVariantAutoSelected(card, preferred) {
        if (card && card.weightAutoSelected) return true
        var block = memorialItemBlockForCard(card)
        var cutoffSettings =
          (card && card._variantCutoffSettings) ||
          (block && block.settings) ||
          block
        if (!M || typeof M.resolveMemorialVariant !== 'function' || !card || !card.product) return false
        var weight = state.pet && state.pet.weightKg != null ? Number(state.pet.weightKg) : null
        var resolved = M.resolveMemorialVariant(card.product, weight, cutoffSettings)
        return !!(resolved && resolved.autoSelected && preferred &&
          resolved.variant && String(resolved.variant.id) === String(preferred.id))
      }

      function afterUrnSelectionChanged() {
        if (cat.key !== 'urn' || !entry) return
        if (M && M.syncAshesPlacement) M.syncAshesPlacement(entry, null, state)
        if (memorialFullCapacityCount(entry) >= 2) {
          var ready =
            M && M.ashesPlacementReady
              ? M.ashesPlacementReady(entry)
              : !!(entry.ashesPlacementMode === 'divided' ||
                (entry.ashesPlacementMode === 'single_urn' && entry.ashDestinationProductId != null))
          if (!ready) entry._ashesPlacementModalOpen = true
        } else {
          entry._ashesPlacementModalOpen = false
        }
      }

      function bindCards(scope, opts) {
        opts = opts || {}
        function promptUrnChangeOrAdd(targetCard, familyCard) {
          var label =
            (targetCard && (targetCard.title || targetCard.selectionLabel || targetCard.styleTitle)) ||
            (targetCard && targetCard.product && targetCard.product.title) ||
            'this urn'
          entry._urnChangeOrAddPending = {
            productId: targetCard.productId,
            label: label,
            card: targetCard,
            swatchGroup: (familyCard && familyCard.swatchGroup) || targetCard.swatchGroup || null,
          }
          entry._ashesPlacementModalOpen = false
          a11yRestoreFocus = '#baw-urn-change-or-add-title'
          if (typeof opts.onSelected === 'function') opts.onSelected(targetCard)
          render()
        }

        function selectMemorialCard(card, selectOpts) {
          if (!card || !M) return
          selectOpts = selectOpts || {}
          // Family cards: toggle the selected sibling if any, else the active colour.
          var target = card
          if (card.swatchMembers && card.swatchMembers.length) {
            var selectedMember = null
            for (var smi = 0; smi < card.swatchMembers.length; smi++) {
              var mem = card.swatchMembers[smi]
              if (mem && M.isSelected(entry, mem.productId)) {
                selectedMember = mem
                break
              }
            }
            target = selectedMember || findMemorialCard(card.productId) || card
          }
          // N→N+1 on urn multi-select: ask replace vs add before committing.
          if (
            cat.key === 'urn' &&
            M.shouldPromptChangeOrAdd &&
            M.shouldPromptChangeOrAdd(entry, target.productId, cat.selectionMode, target)
          ) {
            if (cardNeedsDetail(target) && !selectOpts.skipDetail) {
              openMemorialDetail(cat, target, entry, { promptChangeOrAdd: true })
              return
            }
            promptUrnChangeOrAdd(target, card)
            return
          }
          // Option-tray taps commit the variant in one interaction — never a sheet.
          if (cardNeedsDetail(target) && !selectOpts.skipDetail) {
            openMemorialDetail(cat, target, entry)
            return
          }
          M.toggleSelect(entry, target, cat.selectionMode)
          M.track('memorial_product_selected', {
            category_key: cat.key,
            product_id: target.productId,
            variant_id: target.variantId,
            cremation_type: state.cremationType || config.flow,
            swatch_group: selectOpts.swatchGroup || card.swatchGroup || null,
          })
          state.memorialUnavailableNotice = false
          afterUrnSelectionChanged()
          if (typeof opts.onSelected === 'function') opts.onSelected(target)
          render()
        }

        /** Card shell for a select / option control (shell is a div, not a button). */
        function cardShellFor(el) {
          return el && el.closest ? el.closest('[data-baw-mpc]') : null
        }

        function cardFromShell(shell) {
          if (!shell) return null
          var pid = shell.getAttribute('data-baw-mpc')
          var familyKey = shell.getAttribute('data-baw-mpc-family')
          var card = familyKey
            ? findMemorialFamilyCard(familyKey, pid)
            : findMemorialCard(pid)
          if (!card && familyKey) card = findMemorialCard(pid)
          return card
        }

        /**
         * One tap on an option = pick the product AND that variant.
         * Already-selected families migrate in selectSwatchColour (ledger item,
         * personalisation addons, ashes destination ids); unselected families fall
         * through to the normal selection path with the chosen variant as target.
         */
        function selectMemorialOption(familyCard, optionProductId) {
          if (!M || !M.selectSwatchColour || !familyCard) return
          var alreadySelected = M.isFamilyMemberSelected
            ? M.isFamilyMemberSelected(entry, familyCard)
            : M.isSelected(entry, familyCard.productId)
          var next = M.selectSwatchColour(entry, familyCard, optionProductId, cat.selectionMode)
          if (!next) return
          M.track('memorial_swatch_selected', {
            category_key: cat.key,
            product_id: next.productId,
            option_type: familyCard.optionType || 'colour',
            swatch_group: familyCard.swatchGroup || null,
            cremation_type: state.cremationType || config.flow,
          })
          state.memorialUnavailableNotice = false
          // Keep focus on the option the family just tapped across the re-render.
          var focusOptionId =
            familyCard.optionKind === 'variant' ? optionProductId : next.productId
          a11yRestoreFocus =
            '[data-baw-mpc-option="' +
            String(focusOptionId).replace(/"/g, '\\\\"') +
            '"]'
          if (!alreadySelected) {
            selectMemorialCard(next, {
              skipDetail: true,
              swatchGroup: familyCard.swatchGroup || null,
            })
            return
          }
          afterUrnSelectionChanged()
          render()
        }

        ;(scope || els.step).querySelectorAll('[data-baw-mpc-select]').forEach(function (btn) {
          btn.addEventListener('click', function () {
            selectMemorialCard(cardFromShell(cardShellFor(btn)))
          })
        })

        ;(scope || els.step).querySelectorAll('[data-baw-mpc-qty-delta]').forEach(function (btn) {
          btn.addEventListener('click', function (e) {
            e.preventDefault()
            e.stopPropagation()
            if (btn.disabled || !M || !M.setItemQuantity) return
            var target = cardFromShell(cardShellFor(btn))
            if (!target) return
            var selectedItem = M.selectedItemForCard
              ? M.selectedItemForCard(entry, target)
              : null
            var current = selectedItem
              ? (M.itemQuantity ? M.itemQuantity(selectedItem) : (selectedItem.quantity || 1))
              : 0
            var delta = Number(btn.getAttribute('data-baw-mpc-qty-delta')) || 0
            M.setItemQuantity(entry, target, current + delta, cat.selectionMode)
            a11yRestoreFocus =
              '[data-baw-mpc="' +
              String(target.productId).replace(/"/g, '\\\\"') +
              '"] [data-baw-mpc-qty-delta="' +
              (delta > 0 ? '1' : '-1') +
              '"]'
            M.track('memorial_quantity_changed', {
              category_key: cat.key,
              product_id: target.productId,
              quantity: current + delta,
            })
            state.memorialUnavailableNotice = false
            afterUrnSelectionChanged()
            render()
          })
        })

        ;(scope || els.step).querySelectorAll('[data-baw-mpc-option]').forEach(function (optBtn) {
          optBtn.addEventListener('click', function (ev) {
            ev.preventDefault()
            ev.stopPropagation()
            var optionPid = optBtn.getAttribute('data-baw-mpc-option')
            var familyKey = optBtn.getAttribute('data-baw-mpc-option-family')
            selectMemorialOption(findMemorialFamilyCard(familyKey, optionPid), optionPid)
          })
          // Radio-group semantics: arrows move to the next enabled option and pick it.
          optBtn.addEventListener('keydown', function (ev) {
            var forward = ev.key === 'ArrowRight' || ev.key === 'ArrowDown'
            var back = ev.key === 'ArrowLeft' || ev.key === 'ArrowUp'
            if (!forward && !back) return
            var tray = optBtn.closest('[data-baw-mpc-tray]')
            if (!tray) return
            ev.preventDefault()
            var all = Array.prototype.filter.call(
              tray.querySelectorAll('[data-baw-mpc-option]'),
              function (b) { return !b.disabled }
            )
            var idx = all.indexOf(optBtn)
            if (idx < 0 || all.length < 2) return
            var nextBtn = all[(idx + (forward ? 1 : all.length - 1)) % all.length]
            var optionPid = nextBtn.getAttribute('data-baw-mpc-option')
            var familyKey = nextBtn.getAttribute('data-baw-mpc-option-family')
            selectMemorialOption(findMemorialFamilyCard(familyKey, optionPid), optionPid)
          })
        })
      }

      function openMemorialDetail(category, card, ent, detailOpts) {
        detailOpts = detailOpts || {}
        var variants = (card.product.variants || []).filter(function (v) { return v && v.available !== false })
        if (!variants.length) variants = card.product.variants || []
        var pickerVariants =
          M && M.variantsForDetailPicker ? M.variantsForDetailPicker(card) : variants
        var preferred = applyNameplateMetalToVariant(card, pickVariantForPet(card, variants))
        var selectedVid = preferred ? preferred.id : card.variantId
        var selectedPrice = preferred ? preferred.price : card.price
        var hideVariantPicker =
          memorialVariantAutoSelected(card, preferred) || pickerVariants.length <= 1
        var html =
          '<div class="baw-memorial-sheet baw-memorial-sheet--detail" data-baw-memorial-panel tabindex="-1" role="dialog" aria-modal="true" aria-label="' + escapeHtml(card.title) + '">' +
          '<button type="button" class="baw-memorial-sheet-close" data-baw-memorial-close aria-label="Close">×</button>' +
          '<div class="baw-memorial-sheet-body">' +
          '<div class="baw-memorial-sheet-media">' +
          (function () {
            var sheetImg =
              M && M.resolveCardImage ? M.resolveCardImage(card) || card.image : card.image
            return sheetImg
              ? '<img src="' + escapeHtml(sizedShopifyImageUrl(sheetImg, memorialCdnPx('MEMORIAL_ARTBOARD_PX', 800))) + '" alt="' + escapeHtml(card.title) + '">'
              : ''
          })() +
          '</div>' +
          '<h3 class="baw-memorial-sheet-title">' + escapeHtml(card.title) + '</h3>' +
          '<p class="baw-memorial-sheet-price" data-baw-detail-price>' + escapeHtml(memorialMoney(selectedPrice)) + '</p>' +
          (!hideVariantPicker && pickerVariants.length > 1
            ? '<div class="baw-memorial-sheet-field">' +
              '<label class="baw-label" for="baw-memorial-variant">Options</label>' +
              '<select class="baw-input baw-memorial-variant-select" id="baw-memorial-variant" data-baw-detail-variant>' +
              pickerVariants.map(function (v) {
                return '<option value="' + escapeHtml(String(v.id)) + '"' +
                  (String(v.id) === String(selectedVid) ? ' selected' : '') + '>' +
                  escapeHtml(v.title) + ' — ' + escapeHtml(memorialMoney(v.price)) + '</option>'
              }).join('') +
              '</select></div>'
            : '') +
          '</div>' +
          '<div class="baw-memorial-sheet-actions">' +
          '<button type="button" class="baw-continue" data-baw-detail-add>Add to booking</button>' +
          '</div></div>' +
          '<button type="button" class="baw-memorial-backdrop" data-baw-memorial-close aria-label="Close"></button>'
        var overlay = openMemorialOverlay(
          '<div class="baw-memorial-overlay-inner">' + html + '</div>',
          null
        )
        if (!overlay) return
        M.track('memorial_detail_opened', {
          category_key: category.key,
          product_id: card.productId,
          weight_auto_selected: hideVariantPicker,
        })
        var sel = overlay.host.querySelector('[data-baw-detail-variant]')
        var priceEl = overlay.host.querySelector('[data-baw-detail-price]')
        if (sel) {
          sel.addEventListener('change', function () {
            var v = variants.find(function (x) { return String(x.id) === String(sel.value) })
            if (v && priceEl) priceEl.textContent = memorialMoney(v.price)
          })
        }
        overlay.host.querySelector('[data-baw-detail-add]').addEventListener('click', function () {
          var v = sel
            ? pickerVariants.find(function (x) { return String(x.id) === String(sel.value) })
            : preferred || variants[0]
          v = applyNameplateMetalToVariant(card, v)
          var detailCard = Object.assign({}, card, {
            variant: v,
            variantId: v ? v.id : null,
            price: v ? v.price : card.price,
            image: (v && v.image) || card.image,
          })
          // Ambiguous N→N+1 on urn: close detail and ask replace vs add.
          if (
            detailOpts.promptChangeOrAdd &&
            category.key === 'urn' &&
            M.shouldPromptChangeOrAdd &&
            M.shouldPromptChangeOrAdd(ent, detailCard.productId, category.selectionMode, detailCard)
          ) {
            var pendingLabel =
              detailCard.selectionLabel ||
              detailCard.title ||
              (detailCard.product && detailCard.product.title) ||
              'this urn'
            ent._urnChangeOrAddPending = {
              productId: detailCard.productId,
              label: pendingLabel,
              card: detailCard,
              swatchGroup: detailCard.swatchGroup || null,
            }
            ent._ashesPlacementModalOpen = false
            a11yRestoreFocus = '#baw-urn-change-or-add-title'
            overlay.close()
            render()
            return
          }
          if (isMemorialMultiSelectMode(category.selectionMode)) {
            if (!M.isSelected(ent, detailCard.productId)) {
              ent.items.push(M.selectionFromCard(detailCard))
            } else {
              ent.items = ent.items.map(function (it) {
                if (String(it.productId) !== String(detailCard.productId)) return it
                return M.selectionFromCard(detailCard)
              })
            }
            if (M.pruneItemAddons) M.pruneItemAddons(ent)
            if (M.syncAshesPlacement) M.syncAshesPlacement(ent, null, state)
            else if (M.syncAshSplitChoice) M.syncAshSplitChoice(ent)
            afterUrnSelectionChanged()
          } else {
            ent.itemAddons = {}
            ent.addons = {}
            ent._focusedAddonProductId = null
            if (M.clearAshesPlacement) M.clearAshesPlacement(ent)
            else {
              ent.ashesPlacementMode = null
              ent.ashSplit = null
              ent.ashDestinationProductId = null
              ent._ashesPlacementModalOpen = false
            }
            ent.items = [M.selectionFromCard(detailCard)]
          }
          overlay.close()
          render()
        })
      }

      function openViewAll() {
        var scrollY = window.scrollY || 0
        var sheetTitle = cat.key === 'urn' ? 'All urns' : viewAllLabel
        var allHtml =
          '<div class="baw-memorial-sheet baw-memorial-sheet--viewall" data-baw-memorial-panel tabindex="-1" role="dialog" aria-modal="true" aria-label="' +
          escapeHtml(sheetTitle) + '">' +
          '<div class="baw-memorial-sheet-head">' +
          '<h3 class="baw-memorial-sheet-title">' + escapeHtml(sheetTitle) + '</h3>' +
          '<button type="button" class="baw-memorial-sheet-close" data-baw-memorial-close aria-label="Close">×</button>' +
          '</div>' +
          memorialGridHtml(
            cardsWithIncludedFirst(sortedAllCards),
            'baw-mpc-grid--sheet',
            { belowFoldLazy: true }
          ) +
          '</div>' +
          '<button type="button" class="baw-memorial-backdrop" data-baw-memorial-close aria-label="Close"></button>'
        var overlay = openMemorialOverlay(
          '<div class="baw-memorial-overlay-inner">' + allHtml + '</div>',
          function () { window.scrollTo(0, scrollY) }
        )
        if (!overlay) return
        M.track('memorial_view_all_opened', { category_key: cat.key })
        // Close View All after a simple select — not when opening a detail sheet
        // (detail replaces this overlay; a deferred close was wiping the detail UI).
        bindCards(overlay.host, {
          onSelected: function () {
            if (!overlay.host.hidden) overlay.close()
          },
        })
      }

      bindCards(els.step)

      function dismissUrnChangeOrAddModal() {
        if (M && M.clearUrnChangeOrAddPending) M.clearUrnChangeOrAddPending(entry)
        else entry._urnChangeOrAddPending = null
        render()
      }

      function resolveUrnChangeOrAddCard(pending) {
        if (!pending) return null
        if (pending.card && pending.card.productId != null) return pending.card
        return findMemorialCard(pending.productId)
      }

      function commitUrnChangeOrAdd(choice) {
        var pending = entry._urnChangeOrAddPending
        var toCard = resolveUrnChangeOrAddCard(pending)
        if (!pending || !toCard || !M) {
          dismissUrnChangeOrAddModal()
          return
        }
        var fromItem = (entry.items || [])[0]
        var fromId = fromItem && fromItem.productId != null ? fromItem.productId : null
        if (choice === 'replace') {
          if (fromId == null) {
            if (M.addSelect) M.addSelect(entry, toCard)
            else M.toggleSelect(entry, toCard, cat.selectionMode)
          } else if (M.replaceSelect) {
            // Migrate addons from the first vessel, then drop any remaining
            // selections so "Use this urn instead" always ends as a single urn.
            M.replaceSelect(entry, fromId, toCard)
            var toId = String(toCard.productId)
            if ((entry.items || []).length > 1) {
              entry.items = entry.items.filter(function (it) {
                return it && String(it.productId) === toId
              })
              if (M.pruneItemAddons) M.pruneItemAddons(entry)
              if (M.clearAshesPlacement) M.clearAshesPlacement(entry)
            }
          } else {
            entry.items = [M.selectionFromCard(toCard)]
            if (M.clearAshesPlacement) M.clearAshesPlacement(entry)
          }
          M.track('memorial_product_selected', {
            category_key: cat.key,
            product_id: toCard.productId,
            variant_id: toCard.variantId,
            cremation_type: state.cremationType || config.flow,
            swatch_group: pending.swatchGroup || toCard.swatchGroup || null,
            selection_action: 'replace',
          })
        } else if (choice === 'add') {
          if (M.addSelect) M.addSelect(entry, toCard)
          else M.toggleSelect(entry, toCard, cat.selectionMode)
          M.track('memorial_product_selected', {
            category_key: cat.key,
            product_id: toCard.productId,
            variant_id: toCard.variantId,
            cremation_type: state.cremationType || config.flow,
            swatch_group: pending.swatchGroup || toCard.swatchGroup || null,
            selection_action: 'add',
          })
        } else {
          dismissUrnChangeOrAddModal()
          return
        }
        if (M.clearUrnChangeOrAddPending) M.clearUrnChangeOrAddPending(entry)
        else entry._urnChangeOrAddPending = null
        state.memorialUnavailableNotice = false
        afterUrnSelectionChanged()
        if (
          choice === 'add' &&
          memorialFullCapacityCount(entry) >= 2 &&
          entry._ashesPlacementModalOpen
        ) {
          a11yRestoreFocus = '#baw-ash-split-ask'
        }
        render()
      }

      var changeOrAddModal = els.step.querySelector('[data-baw-urn-change-or-add-modal]')
      if (changeOrAddModal) {
        root.appendChild(changeOrAddModal)
        changeOrAddModal.querySelectorAll('[data-baw-urn-change-or-add-choice]').forEach(function (btn) {
          btn.addEventListener('click', function () {
            commitUrnChangeOrAdd(btn.getAttribute('data-baw-urn-change-or-add-choice'))
          })
        })
        changeOrAddModal.querySelectorAll('[data-baw-urn-change-or-add-dismiss]').forEach(function (btn) {
          btn.addEventListener('click', dismissUrnChangeOrAddModal)
        })
        changeOrAddModal.addEventListener('keydown', function (e) {
          if (e.key === 'Escape') {
            e.preventDefault()
            dismissUrnChangeOrAddModal()
          }
        })
        if (!a11yRestoreFocus) {
          var changeTitle = changeOrAddModal.querySelector('#baw-urn-change-or-add-title')
          if (changeTitle) {
            try { changeTitle.focus({ preventScroll: true }) } catch (errFocusAdd) {
              try { changeTitle.focus() } catch (errFocusAdd2) { /* ignore */ }
            }
          }
        }
      }

      function dismissAshesPlacementModal() {
        entry._ashesPlacementModalOpen = false
        render()
      }

      function confirmAshesPlacementModal() {
        var Mloc = typeof AngelAshesBookingMemorials !== 'undefined' ? AngelAshesBookingMemorials : null
        if (Mloc && Mloc.syncAshesPlacement) Mloc.syncAshesPlacement(entry, null, state)
        var mode =
          Mloc && Mloc.normalizeAshesPlacementMode
            ? Mloc.normalizeAshesPlacementMode(entry)
            : (entry.ashesPlacementMode === 'divided' || entry.ashesPlacementMode === 'single_urn'
              ? entry.ashesPlacementMode
              : null)
        if (mode !== 'divided' && mode !== 'single_urn') {
          entry._ashesPlacementError = 'mode'
          entry._ashesPlacementModalOpen = true
          a11yRestoreFocus = '#baw-ash-split-ask'
          render()
          return
        }
        if (mode === 'single_urn') {
          var destOk =
            Mloc && Mloc.syncAshDestination
              ? !!Mloc.syncAshDestination(entry)
              : !!(entry.ashDestinationProductId &&
                (entry.items || []).some(function (it) {
                  return it && String(it.productId) === String(entry.ashDestinationProductId)
                }))
          if (!destOk) {
            entry._ashesPlacementError = 'destination'
            entry._ashesPlacementModalOpen = true
            a11yRestoreFocus = '#baw-ash-destination-ask'
            render()
            return
          }
        }
        entry._ashesPlacementError = null
        entry._ashesPlacementModalOpen = false
        render()
      }

      var changePlacement = els.step.querySelector('[data-baw-ashes-placement-change]')
      if (changePlacement) {
        changePlacement.addEventListener('click', function () {
          entry._ashesPlacementModalOpen = true
          entry._ashesPlacementError = null
          a11yRestoreFocus = '[data-baw-ashes-placement-confirm]'
          render()
        })
      }
      // Portal onto root so fixed backdrop covers the full wizard (step-inner
      // fade animation uses transform, which would otherwise trap position:fixed).
      var placementModal = els.step.querySelector('[data-baw-ashes-placement-modal]')
      if (placementModal) {
        root.appendChild(placementModal)
        placementModal.querySelectorAll('[data-baw-ash-placement]').forEach(function (btn) {
          btn.addEventListener('click', function () {
            var choice = btn.getAttribute('data-baw-ash-placement')
            if (choice !== 'divided' && choice !== 'single_urn') return
            if (M && M.setAshesPlacementMode) {
              M.setAshesPlacementMode(entry, choice)
            } else {
              entry.ashesPlacementMode = choice
              entry.ashSplit = choice === 'divided' ? 'yes' : 'no'
              entry._ashesPlacementError = null
            }
            entry._ashesPlacementModalOpen = true
            a11yRestoreFocus = '[data-baw-ash-placement="' + choice + '"]'
            if (M && M.track) {
              M.track('memorial_ashes_placement_selected', {
                category_key: cat.key,
                ashes_placement_mode: choice,
              })
            }
            render()
          })
        })
        placementModal.querySelectorAll('[data-baw-ash-destination]').forEach(function (btn) {
          btn.addEventListener('click', function () {
            var pid = btn.getAttribute('data-baw-ash-destination')
            if (!pid) return
            if (M && M.setAshDestination) {
              M.setAshDestination(entry, pid)
            } else {
              entry.ashDestinationProductId = pid
              entry._ashesPlacementError = null
            }
            entry._ashesPlacementModalOpen = true
            a11yRestoreFocus = '[data-baw-ash-destination="' + pid + '"]'
            if (M && M.track) {
              M.track('memorial_ash_destination_selected', {
                category_key: cat.key,
                product_id: pid,
              })
            }
            render()
          })
        })
        placementModal.querySelectorAll('[data-baw-ashes-placement-dismiss]').forEach(function (btn) {
          btn.addEventListener('click', dismissAshesPlacementModal)
        })
        var confirmBtn = placementModal.querySelector('[data-baw-ashes-placement-confirm]')
        if (confirmBtn) {
          confirmBtn.addEventListener('click', confirmAshesPlacementModal)
          if (!a11yRestoreFocus) {
            // Prefer Confirm when ready; otherwise land on the title (not a disabled CTA).
            var initialFocus = !confirmBtn.disabled
              ? confirmBtn
              : (placementModal.querySelector('#baw-ash-split-ask') ||
                placementModal.querySelector('[data-baw-ash-placement]'))
            if (initialFocus) {
              try { initialFocus.focus({ preventScroll: true }) } catch (errFocus) {
                try { initialFocus.focus() } catch (errFocus2) { /* ignore */ }
              }
            }
          }
        }
        placementModal.addEventListener('keydown', function (e) {
          if (e.key === 'Escape') {
            e.preventDefault()
            dismissAshesPlacementModal()
          }
        })
      }
      // Continue is handled by capture-phase delegation on the wizard root
      // (see booking-wizard.js) so the first click always advances after re-renders.
      var va = els.step.querySelector('[data-baw-memorial-viewall]')
      if (va) va.addEventListener('click', openViewAll)
      var skip = els.step.querySelector('[data-baw-memorial-skip]')
      if (skip) {
        skip.addEventListener('click', function () {
          if (M) {
            M.skipOptionalKeepsakes(catalog, state, cat.key)
            M.track('memorial_optional_step_skipped', { category_key: cat.key })
          }
          var steps = computeSteps(state, config)
          var idx = -1
          steps.forEach(function (s, i) {
            if (s.id === 'memorial:' + cat.key) idx = i
          })
          var next = idx >= 0 ? steps[idx + 1] : null
          if (!next) next = steps[steps.length - 1]
          state.currentStepId = next.id
          render()
        })
      }
      mountStepFooter()
      setContinueEnabled(canAdvanceFrom('memorial:' + groupKey))
      // After footer mount (step height final), show calm fade if grid overflows.
      window.requestAnimationFrame(function () {
        syncMemorialStepScrollAffordance()
      })
    }

    /**
     * Desktop memorial grids: toggle .has-more-below / .is-scrollable when
     * product cards are clipped below the step — not when a selected card
     * grows (qty) or a keepsake/capacity GuidanceBar appears under the grid.
     * Same GuidanceBar classes/markup as SMS bars, sticky flush on Continue.
     * Urn step: never for fewer than 9 cards (two rows of four).
     * Latch is-scrollable until the bottom so cue padding / Mini Heart footer
     * air cannot flip clip detection mid-scroll and flash the bar.
     */
    function memorialStepGridCards(inner) {
      return inner.querySelectorAll('.baw-mpc-grid:not(.baw-mpc-grid--sheet) > .baw-mpc')
    }

    function memorialCardsClippedBelow(step, cards) {
      if (!step || !cards || !cards.length) return false
      var limit = step.getBoundingClientRect().bottom
      for (var i = 0; i < cards.length; i++) {
        if (cards[i].getBoundingClientRect().bottom > limit + 2) return true
      }
      return false
    }

    function ensureMemorialMoreBelowHint(inner, step) {
      var hint = inner.querySelector(':scope > [data-baw-more-below]')
      var markup =
        '<div class="baw-guidance-inner baw-step-footer-notes-inner">' +
        '<span class="baw-more-below__hit" aria-hidden="true">\u25BE</span>' +
        '<div class="baw-guidance-body baw-step-footer-notes-body">' +
        '<p class="baw-guidance-line baw-step-footer-note baw-more-below__label">Scroll for more</p>' +
        '</div>' +
        '</div>'
      var hintClass = 'baw-guidance baw-step-footer-notes baw-more-below'
      if (hint) {
        hint.className = hintClass
        hint.setAttribute('aria-label', 'Scroll for more')
        if (!hint.querySelector('.baw-guidance-inner')) hint.innerHTML = markup
        // Keep the cue last so sticky bottom sits on Continue, not between
        // the grid and a later in-step callout.
        if (hint.parentNode === inner && inner.lastElementChild !== hint) {
          inner.appendChild(hint)
        }
        return hint
      }
      hint = document.createElement('button')
      hint.type = 'button'
      hint.className = hintClass
      hint.setAttribute('data-baw-more-below', '')
      hint.setAttribute('aria-label', 'Scroll for more')
      hint.hidden = true
      hint.innerHTML = markup
      hint.addEventListener('click', function (e) {
        e.preventDefault()
        var chunk = Math.max(140, Math.round(step.clientHeight * 0.55))
        if (typeof step.scrollBy === 'function') {
          step.scrollBy({ top: chunk, behavior: 'smooth' })
        } else {
          step.scrollTop += chunk
        }
      })
      inner.appendChild(hint)
      return hint
    }

    function syncMemorialStepScrollAffordance() {
      var step = els && els.step
      if (!step) return
      var inner = step.querySelector(':scope > .baw-step-inner.baw-memorial-step')
      if (!inner) return
      var cards = memorialStepGridCards(inner)
      var cardCount = cards.length
      inner.classList.toggle('has-many-cards', cardCount >= 9)
      var desk = !(window.matchMedia && window.matchMedia('(max-width: 1199px)').matches)
      var hint = inner.querySelector(':scope > [data-baw-more-below]')
      if (!desk) {
        inner.classList.remove('is-scrollable', 'has-more-below')
        if (hint) hint.hidden = true
        return
      }
      var isUrn = inner.classList.contains('baw-memorial-step--urn')
      var cardsHidden = memorialCardsClippedBelow(step, cards)
      var atBottom = step.scrollTop + step.clientHeight >= step.scrollHeight - 16
      var overflows
      if (isUrn && cardCount < 9) {
        overflows = false
      } else if (inner.classList.contains('is-scrollable') && !atBottom) {
        overflows = true
      } else {
        overflows = cardsHidden
      }
      var showMore = overflows && !atBottom
      inner.classList.toggle('is-scrollable', overflows)
      inner.classList.toggle('has-more-below', showMore)
      hint = ensureMemorialMoreBelowHint(inner, step)
      if (hint.hidden !== !showMore) hint.hidden = !showMore

      if (!step.getAttribute('data-baw-memorial-scroll-bound')) {
        step.setAttribute('data-baw-memorial-scroll-bound', '1')
        step.addEventListener('scroll', syncMemorialStepScrollAffordance, { passive: true })
        if (typeof ResizeObserver !== 'undefined') {
          var ro = new ResizeObserver(function () {
            syncMemorialStepScrollAffordance()
          })
          ro.observe(step)
          ro.observe(inner)
          if (els.footer) ro.observe(els.footer)
        }
        if (window.matchMedia) {
          window.matchMedia('(max-width: 1199px)').addEventListener('change', syncMemorialStepScrollAffordance)
        }
      }
    }

    /**
     * Continue guard for multi-urn ashes placement — opens the placement modal
     * when incomplete (errors only after Continue / Confirm).
     */

    function tryAdvanceMemorialAshesPlacement() {
      var entry = getMemorialEntry(state, 'urn')
      if (!entry || memorialFullCapacityCount(entry) < 2) {
        if (entry) {
          entry._ashesPlacementError = null
          entry._ashesPlacementModalOpen = false
        }
        return true
      }
      var M = typeof AngelAshesBookingMemorials !== 'undefined' ? AngelAshesBookingMemorials : null
      if (M && M.syncAshesPlacement) M.syncAshesPlacement(entry, null, state)
      if (M && M.ashesPlacementReady && M.ashesPlacementReady(entry)) {
        entry._ashesPlacementError = null
        entry._ashesPlacementModalOpen = false
        return true
      }
      var mode =
        M && M.normalizeAshesPlacementMode
          ? M.normalizeAshesPlacementMode(entry)
          : (entry.ashesPlacementMode === 'divided' || entry.ashesPlacementMode === 'single_urn'
            ? entry.ashesPlacementMode
            : null)
      if (mode !== 'divided' && mode !== 'single_urn') {
        entry._ashesPlacementError = 'mode'
      } else if (mode === 'single_urn' && memorialFullCapacityDistinctCount(entry) >= 2) {
        entry._ashesPlacementError = 'destination'
      } else if (mode === 'single_urn') {
        entry._ashesPlacementError = 'mode'
      } else {
        entry._ashesPlacementError = 'mode'
      }
      entry._ashesPlacementModalOpen = true
      a11yRestoreFocus =
        entry._ashesPlacementError === 'destination'
          ? '#baw-ash-destination-ask'
          : '#baw-ash-split-ask'
      render()
      return false
    }

    function memorialPhotoStepHeadline(itemLabel) {
      var template = stepCopyText(config, 'memorialAddonPhotoHeadline')
      if (template.indexOf('{item}') !== -1) {
        return memorialAddonHeadline('photo', itemLabel, config)
      }
      return copyHeadline('memorialAddonPhotoHeadline')
    }

    function memorialPhotoStepSubtitle() {
      return copyHeadline('memorialAddonPhotoSubtitle')
    }

    function photoUploadIconSvg() {
      return (
        '<svg class="baw-photo-upload__icon" width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">' +
        '<path d="M12 15.5V5" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/>' +
        '<path d="M8 8.5 12 4.5 16 8.5" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>' +
        '<path d="M5 18.5h14" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/>' +
        '</svg>'
      )
    }

    function photoChooseEmptyHtml() {
      return (
        '<span class="baw-photo-upload__empty" data-photo-upload-status>' +
        photoUploadIconSvg() +
        '<span class="baw-photo-upload__cta">Choose a photo</span>' +
        '<span class="baw-photo-upload__hint">JPG/PNG · max 8MB</span>' +
        '</span>'
      )
    }

    /** Purposeful 3:2 / 2:3 rect icons for orientation option cards (~26×17 / ~17×26). */

    function photoOrientationIconSvg(orientation) {
      if (orientation === 'portrait') {
        return (
          '<svg class="baw-photo-orient__icon" width="17" height="26" viewBox="0 0 17 26" fill="none" aria-hidden="true" focusable="false">' +
          '<rect x="1.25" y="1.25" width="14.5" height="23.5" rx="1.75" stroke="currentColor" stroke-width="1.5"/>' +
          '</svg>'
        )
      }
      return (
        '<svg class="baw-photo-orient__icon" width="26" height="17" viewBox="0 0 26 17" fill="none" aria-hidden="true" focusable="false">' +
        '<rect x="1.25" y="1.25" width="23.5" height="14.5" rx="1.75" stroke="currentColor" stroke-width="1.5"/>' +
        '</svg>'
      )
    }

    /**
     * Product-frame mockup — customer photo cover-crops behind a PNG with a
     * transparent window. Used when Theme Editor Photo mockup frame is set.
     * options.showBusy: spinner in the window. options.emptyCta / emptyHtml:
     * Choose-a-photo copy in the hole before upload. options.inputHtml: file
     * input inside the window so pointer-events hit it (not a page overlay).
     * options.windowAttrs: extra attributes on the photo pane (upload trigger).
     */

    /** Ink Photo Set dual-window — not Clay.
     *  Ink cutout is locked; do not retune (.is-ink-wide 1.15% left shift). */
    function looksLikeInkWideArtboard(url, extraText) {
      var hay = String(url || '') + ' ' + String(extraText || '')
      if (/clay/i.test(hay)) return false
      return /ink[-_. ]?photo/i.test(hay) || (/ink/i.test(hay) && /photo/i.test(hay))
    }

    /** Clay Photo Frame dual-window — not Ink, not Photo Tribute.
     *  Cutout is .is-clay-wide from the Gold 467×342 blank hole (246,46)–(422,296). */
    function looksLikeClayWideArtboard(url, extraText) {
      var hay = String(url || '') + ' ' + String(extraText || '')
      if (looksLikeInkWideArtboard(url, extraText)) return false
      if (/tribute/i.test(hay)) return false
      return /clay/i.test(hay)
    }

    /** Photo Tribute / portrait urn — never Ink/Clay right-pane cutout. */
    function looksLikePhotoTributeArtboard(url, extraText) {
      var hay = String(url || '') + ' ' + String(extraText || '')
      if (looksLikeInkWideArtboard(url, extraText)) return false
      if (/clay/i.test(hay) && /photo/i.test(hay) && !/tribute/i.test(hay)) return false
      return (
        /\bphoto\s+(tribute|memorial(?:\s+urn)?)\b/i.test(hay) ||
        /photo[-_. ]?(tribute|memorial(?:[-_ ]?urn)?)/i.test(hay) ||
        /(?:^|[-_\s])photo[-_]?urn/i.test(hay)
      )
    }

    function renderPhotoMockupPreview(src, orientation, options) {
      options = options || {}
      var frameUrl = String(options.frameUrl || '').trim()
      if (!frameUrl) return ''
      var orient = orientation === 'portrait' ? 'portrait' : 'landscape'
      var nativeWideHint = !!options.nativeWide
      var nativeTallHint = !!options.nativeTall && !nativeWideHint
      var inkWide =
        nativeWideHint &&
        (options.inkWide === true ||
          looksLikeInkWideArtboard(frameUrl, options.itemLabel))
      var clayWide =
        nativeWideHint &&
        !inkWide &&
        (options.clayWide === true ||
          looksLikeClayWideArtboard(frameUrl, options.itemLabel))
      var showBusy = !!options.showBusy
      var emptyCta = !!options.emptyCta
      var windowAttrs = options.windowAttrs ? (' ' + String(options.windowAttrs)) : ''
      var inputHtml = options.inputHtml ? String(options.inputHtml) : ''
      var busyHtml = showBusy
        ? (
          '<span class="baw-photo-upload__busy" data-photo-upload-status role="status" aria-live="polite">' +
          '<span class="baw-photo-upload__spinner" aria-hidden="true"></span>' +
          '<span class="baw-photo-upload__busy-label">Preparing your photo\\u2026</span>' +
          '</span>'
        )
        : ''
      var imgHtml = src
        ? (
          '<img class="baw-photo-mockup__photo" src="' + escapeHtml(src) + '"' +
          ' alt="Uploaded memorial photo" decoding="async" data-photo-preview-img />'
        )
        : ''
      var pendingHtml = src
        ? (
          '<span class="baw-photo-preview__pending" data-photo-preview-pending aria-hidden="true">' +
          '<span class="baw-photo-upload__spinner" aria-hidden="true"></span>' +
          '<span class="baw-photo-upload__busy-label">Preparing your photo\\u2026</span>' +
          '</span>'
        )
        : ''
      var emptyHtml = ''
      if (!src && !showBusy) {
        if (options.emptyHtml) emptyHtml = String(options.emptyHtml)
        else if (emptyCta) emptyHtml = photoChooseEmptyHtml()
      }
      var inches = photoSizeInches(options.printSize)
      var printW = orient === 'landscape' ? inches.long : inches.short
      var printH = orient === 'landscape' ? inches.short : inches.long
      return (
        '<div class="baw-photo-mockup is-' + orient +
        (src ? '' : ' is-empty') +
        (nativeWideHint ? ' is-native-wide' : '') +
        (inkWide ? ' is-ink-wide' : '') +
        (clayWide ? ' is-clay-wide' : '') +
        (nativeTallHint ? ' is-native-tall' : '') +
        '" data-photo-print-preview data-photo-mockup data-orientation="' + orient + '"' +
        ' style="--baw-print-w: ' + printW + '; --baw-print-h: ' + printH + ';">' +
        '<div class="baw-photo-mockup__window"' + windowAttrs + '>' +
        '<div class="baw-photo-mockup__print">' +
        imgHtml +
        emptyHtml +
        busyHtml +
        pendingHtml +
        '</div>' +
        inputHtml +
        '</div>' +
        '<img class="baw-photo-mockup__frame" crossorigin="anonymous" src="' +
        escapeHtml((typeof AngelAshesBookingMemorials !== 'undefined' && AngelAshesBookingMemorials.sizedShopifyImageUrl ? AngelAshesBookingMemorials.sizedShopifyImageUrl(frameUrl, AngelAshesBookingMemorials.MEMORIAL_ARTBOARD_PX || 800) : frameUrl)) +
        '" alt="" decoding="async" />' +
        '</div>'
      )
    }

    /**
     * Opaque content box of a dual-window PNG (0–1). Clay silver/gold artboards
     * often include transparent padding; Ink is tighter. Used to zoom thumbs so the
     * visible mat fills the shared rail slot, and to fit photo-step square canvases
     * to the landscape frame so --baw-cutout-* (frame-relative) stay on the hole.
     * Returns null when canvas is tainted / unavailable.
     */
    function measureOpaqueContentFrac(frameImg) {
      if (!frameImg || !(frameImg.naturalWidth > 0) || !(frameImg.naturalHeight > 0)) return null
      try {
        var natW = frameImg.naturalWidth
        var natH = frameImg.naturalHeight
        var maxSide = 160
        var ds = Math.min(1, maxSide / Math.max(natW, natH))
        var cw = Math.max(1, Math.round(natW * ds))
        var ch = Math.max(1, Math.round(natH * ds))
        var canvas = document.createElement('canvas')
        canvas.width = cw
        canvas.height = ch
        var ctx = canvas.getContext('2d', { willReadFrequently: true })
        if (!ctx) return null
        ctx.clearRect(0, 0, cw, ch)
        ctx.drawImage(frameImg, 0, 0, cw, ch)
        var data = ctx.getImageData(0, 0, cw, ch).data
        var minX = cw
        var minY = ch
        var maxX = -1
        var maxY = -1
        var x
        var y
        for (y = 0; y < ch; y++) {
          for (x = 0; x < cw; x++) {
            if (data[(y * cw + x) * 4 + 3] > 12) {
              if (x < minX) minX = x
              if (y < minY) minY = y
              if (x > maxX) maxX = x
              if (y > maxY) maxY = y
            }
          }
        }
        if (maxX < minX || maxY < minY) return null
        return {
          contentX: minX / cw,
          contentY: minY / ch,
          contentW: (maxX - minX + 1) / cw,
          contentH: (maxY - minY + 1) / ch,
        }
      } catch (err) {
        return null
      }
    }

    function clearNativeWideArtboardFit(previewRoot) {
      if (!previewRoot) return
      previewRoot.classList.remove('is-opaque-fit', 'is-padded-square')
      previewRoot.style.removeProperty('--baw-opaque-x')
      previewRoot.style.removeProperty('--baw-opaque-y')
      previewRoot.style.removeProperty('--baw-opaque-w')
      previewRoot.style.removeProperty('--baw-opaque-h')
    }

    /**
     * Square-padded Clay blanks become a landscape frame box so visible-frame
     * cutout % apply (photo-step, review, and dual-window thumbs). Landscape
     * Ink PNGs are already the frame. inThumb is kept for callers; thumbs now
     * use the same opaque-fit path so Gold/Silver share one layout box.
     */
    function applyNativeWideArtboardFit(previewRoot, frameImg, natW, natH, inThumb) {
      clearNativeWideArtboardFit(previewRoot)
      var frac = measureOpaqueContentFrac(frameImg)
      var fitW = frac && frac.contentW > 0.55 ? frac.contentW : 1
      var fitH = frac && frac.contentH > 0.55 ? frac.contentH : 1
      var fitX = frac && typeof frac.contentX === 'number' ? frac.contentX : 0
      var fitY = frac && typeof frac.contentY === 'number' ? frac.contentY : 0
      var isSquarePng = natH > 0 && natW / natH < 1.15
      var hasPad = fitW < 0.97 || fitH < 0.97
      if (isSquarePng && hasPad && !inThumb) {
        previewRoot.classList.add('is-opaque-fit')
        previewRoot.style.setProperty('--baw-opaque-x', String(fitX))
        previewRoot.style.setProperty('--baw-opaque-y', String(fitY))
        previewRoot.style.setProperty('--baw-opaque-w', String(fitW))
        previewRoot.style.setProperty('--baw-opaque-h', String(fitH))
        return { padFit: true, fitW: fitW, fitH: fitH }
      }
      if (isSquarePng && hasPad && inThumb) {
        previewRoot.classList.add('is-padded-square')
      }
      return { padFit: false, fitW: 1, fitH: 1 }
    }

    /**
     * Shared physical 4×6 print preview — always crops the original upload via
     * object-fit:cover inside an oriented frame. Never reuse a pre-cropped print master.
     * Landscape = 3/2, Portrait = 2/3.
     * options.showBusy: spinner chrome inside the same 4×6 frame (busy → ready continuity).
     * options.frameUrl: optional product-frame PNG (transparent window) instead of 4×6.
     */

    function renderPhotoPrintPreview(src, orientation, options) {
      options = options || {}
      var frameUrl = String(options.frameUrl || '').trim()
      if (frameUrl) return renderPhotoMockupPreview(src, orientation, options)
      var orient = orientation === 'portrait' ? 'portrait' : 'landscape'
      var ratio = orient === 'portrait' ? '2 / 3' : '3 / 2'
      var showBusy = !!options.showBusy
      if (!src && !showBusy) return ''
      var isPlaceholder = !src
      var frameClass =
        'baw-photo-preview__frame' +
        (isPlaceholder ? ' baw-photo-preview__frame--placeholder' : '')
      var busyHtml = showBusy
        ? (
          '<span class="baw-photo-upload__busy" data-photo-upload-status role="status" aria-live="polite">' +
          '<span class="baw-photo-upload__spinner" aria-hidden="true"></span>' +
          '<span class="baw-photo-upload__busy-label">Preparing your photo\\u2026</span>' +
          '</span>'
        )
        : ''
      var imgHtml = src
        ? (
          '<img class="baw-photo-preview__img" src="' + escapeHtml(src) + '"' +
          ' alt="Uploaded memorial photo" decoding="async" data-photo-preview-img />'
        )
        : ''
      var pendingHtml = src
        ? (
          '<span class="baw-photo-preview__pending" data-photo-preview-pending aria-hidden="true">' +
          '<span class="baw-photo-upload__spinner" aria-hidden="true"></span>' +
          '<span class="baw-photo-upload__busy-label">Preparing your photo\\u2026</span>' +
          '</span>'
        )
        : ''
      return (
        '<div class="baw-photo-preview is-' + orient +
        (isPlaceholder ? ' is-decoding' : '') +
        '" data-photo-print-preview data-orientation="' + orient + '">' +
        '<div class="baw-photo-preview__stage">' +
        '<div class="' + frameClass + '" style="aspect-ratio: ' + ratio + ';">' +
        imgHtml +
        busyHtml +
        '</div>' +
        pendingHtml +
        '</div>' +
        '</div>'
      )
    }

    /**
     * Framed upload for later steps (nameplate / engraving / summary / review)
     * when this item has a client photo. Uses the same frame resolver as the
     * photo step so Gold/Silver blanks stay TE-linked.
     */
    function resolveMockupFrameForAddon(flags, colour) {
      flags = flags || {}
      var UI = typeof AngelAshesBookingMemorialUI !== 'undefined' ? AngelAshesBookingMemorialUI : null
      var M = typeof AngelAshesBookingMemorials !== 'undefined' ? AngelAshesBookingMemorials : null
      if (UI && typeof UI.resolvePhotoStepMockupFrame === 'function') {
        var uiPhoto = UI.resolvePhotoStepMockupFrame(flags, colour)
        if (uiPhoto) return uiPhoto
      }
      if (M && typeof M.resolvePhotoStepMockupFrame === 'function') {
        var mPhoto = M.resolvePhotoStepMockupFrame(flags, colour)
        if (mPhoto) return mPhoto
      }
      if (UI && typeof UI.resolvePersonaliseMockupFrame === 'function') {
        var uiPers = UI.resolvePersonaliseMockupFrame(flags, colour)
        if (uiPers) return uiPers
      }
      if (M && typeof M.resolvePersonaliseMockupFrame === 'function') {
        return M.resolvePersonaliseMockupFrame(flags, colour)
      }
      return null
    }

    function photoMockupFromAddon(photo, flags, colour, itemLabel) {
      var url = photo && (photo.photoUrl || photo.localPreviewUrl)
        ? String(photo.photoUrl || photo.localPreviewUrl).trim()
        : ''
      if (!url) return null
      if (String(photo.choiceId || '') === 'no') return null
      flags = flags || {}
      var frame = resolveMockupFrameForAddon(flags, colour)
      if (!frame) return null
      var optsFn = bawPhotoStepHelper('photoStepMockupPreviewOpts')
      var previewOpts = optsFn ? optsFn(flags, colour) : null
      var canChoose =
        normalizePhotoOrientation(flags.photoOrientation) === 'client_choice'
      var tribute = looksLikePhotoTributeArtboard(frame, itemLabel)
      // Same as photoStepMockupPreviewOpts: client_choice / tribute → tall;
      // Ink/Clay → wide. Tall always wins so dual-window inset cannot leak.
      var nativeTall = !!(canChoose || tribute || (previewOpts && previewOpts.nativeTall))
      var nativeWide = !nativeTall && !!(previewOpts ? previewOpts.nativeWide : !canChoose)
      if (nativeTall) nativeWide = false
      var inkWide = nativeWide && (
        !!(previewOpts && previewOpts.inkWide) ||
        looksLikeInkWideArtboard(frame, itemLabel)
      )
      var clayWide = nativeWide && !inkWide && (
        !!(previewOpts && previewOpts.clayWide) ||
        looksLikeClayWideArtboard(frame, itemLabel)
      )
      return {
        url: url,
        orientation: photo.orientation === 'landscape' ? 'landscape' : 'portrait',
        frameUrl: frame,
        nativeWide: nativeWide,
        nativeTall: nativeTall,
        inkWide: inkWide,
        clayWide: clayWide,
        printSize: normalizePhotoPrintSize(flags.photoPrintSize || photo.size),
      }
    }

    function renderPersonalisePhotoMockupHtml(photo, flags, wrapClass, colour, itemLabel) {
      var mockup = photoMockupFromAddon(photo, flags, colour, itemLabel)
      if (!mockup) return ''
      var cls = wrapClass || 'baw-personalise-product baw-personalise-product--mockup'
      return (
        '<div class="' + cls + '">' +
        renderPhotoPrintPreview(mockup.url, mockup.orientation, {
          frameUrl: mockup.frameUrl,
          nativeWide: mockup.nativeWide,
          nativeTall: mockup.nativeTall,
          inkWide: mockup.inkWide,
          clayWide: mockup.clayWide,
          printSize: mockup.printSize,
          itemLabel: itemLabel,
        }) +
        '</div>'
      )
    }

    /**
     * Dual-window right-mat cutout is locked in CSS (--baw-cutout-* on
     * .is-native-wide). Ink cutout is locked (.is-ink-wide 1.15% left shift) —
     * do not retune. Clay uses .is-clay-wide from the Gold 467×342 blank
     * (hole 246,46–422,296). Photo Tribute is .is-native-tall with its own
     * 4×6 hole (770×1024, 10.94/15.71/12.99/15.58) and must never inherit
     * Ink/Clay percents. Square-padded 2032² Clay blanks are opaque-fit
     * to that landscape frame so Clay percents stay frame-relative.
     */

    /** Decode the preview image before revealing it; keep a stable stage meanwhile. */

    function sizePhotoMockupFromFrame(previewRoot, frameImg) {
      if (!previewRoot || !frameImg) return
      var natW = frameImg.naturalWidth
      var natH = frameImg.naturalHeight
      if (!(natW > 0 && natH > 0)) return
      var hintedWide = previewRoot.classList.contains('is-native-wide')
      var hintedTall = previewRoot.classList.contains('is-native-tall')
      var stepInner = previewRoot.closest('.baw-step-inner')
      var headlineEl = stepInner && stepInner.querySelector('.baw-headline')
      var stepHeadline = headlineEl ? String(headlineEl.textContent || '') : ''
      var tributeHint = looksLikePhotoTributeArtboard(frameImg.src, stepHeadline)
      // Photo Tribute stays tall even if the PNG decode is slightly wide.
      // Clay/Ink dual-window hints stay wide even if the PNG is slightly tall.
      // Tall always wins so dual-window inset cannot clip Photo Tribute.
      var nativeWide
      if (hintedTall || tributeHint) nativeWide = false
      else if (hintedWide) nativeWide = true
      else nativeWide = natW > natH
      previewRoot.classList.toggle('is-native-wide', nativeWide)
      previewRoot.classList.toggle('is-native-tall', !nativeWide)
      previewRoot.classList.toggle(
        'is-ink-wide',
        !!(
          nativeWide &&
          (previewRoot.classList.contains('is-ink-wide') ||
            looksLikeInkWideArtboard(frameImg.src, stepHeadline))
        )
      )
      previewRoot.classList.toggle(
        'is-clay-wide',
        !!(
          nativeWide &&
          !previewRoot.classList.contains('is-ink-wide') &&
          (previewRoot.classList.contains('is-clay-wide') ||
            looksLikeClayWideArtboard(frameImg.src, stepHeadline))
        )
      )
      var cs = window.getComputedStyle(previewRoot)
      var shortMax = parseFloat(cs.getPropertyValue('--baw-mockup-short')) || 200
      var longMax = parseFloat(cs.getPropertyValue('--baw-mockup-long')) || 260
      // contain to the stage / fixed thumb slot so frames never paint larger
      // than the card or square thumb (oversized boxes clipped Ink/Clay).
      // Skip fit-content personalise wrappers — their client box circularly
      // shrinks the first measure; those use CSS --baw-mockup-* + max-width.
      var containBox =
        previewRoot.closest('.baw-review-product-mockup-stage') ||
        previewRoot.closest('.baw-review-product-mockup') ||
        previewRoot.closest('.baw-summary-memorial-thumb--mockup') ||
        previewRoot.closest('.baw-photo-addon__thumb--mockup')
      if (containBox) {
        var availW = containBox.clientWidth
        var availH = containBox.clientHeight
        // Review frame+plate: reserve the in-flow plate so the frame cannot
        // size to the full 1:1 card and paint over / crowd the chip.
        var reviewStage = previewRoot.closest(
          '.baw-review-product-stage.has-mockup.has-plate'
        )
        if (reviewStage) {
          var plateEl = reviewStage.querySelector(':scope > .baw-review-product-plate')
          var stageH = reviewStage.clientHeight
          var stageW = reviewStage.clientWidth
          var plateH = plateEl && plateEl.offsetHeight > 0 ? plateEl.offsetHeight : 48
          var stackGap = 6
          var reservedH = Math.max(40, stageH - plateH - stackGap)
          if (stageW > 0) availW = Math.min(availW > 0 ? availW : stageW, stageW)
          if (reservedH > 0) {
            availH = availH > 0 ? Math.min(availH, reservedH) : reservedH
          }
        }
        if (availW > 0 && availH > 0) {
          var boxLandscape = previewRoot.classList.contains('is-landscape')
          if (nativeWide || (boxLandscape && !nativeWide)) {
            // Wide frames, or rotated tall artboards: long axis is display width.
            longMax = Math.min(longMax, availW)
            shortMax = Math.min(shortMax, availH)
          } else {
            shortMax = Math.min(shortMax, availW)
            longMax = Math.min(longMax, availH)
          }
        } else if (availW > 0 && nativeWide) {
          longMax = Math.min(longMax, availW)
        }
      } else if (nativeWide) {
        var personaliseWrap = previewRoot.closest('.baw-personalise-product--mockup')
        if (personaliseWrap) {
          var wrapMaxW = parseFloat(window.getComputedStyle(personaliseWrap).maxWidth)
          if (wrapMaxW > 0) longMax = Math.min(longMax, wrapMaxW)
        }
      }
      var scale
      var dispW
      var dispH
      var thumbSlot =
        previewRoot.closest('.baw-personalise-product--mockup') ||
        previewRoot.closest('.baw-summary-memorial-thumb--mockup') ||
        previewRoot.closest('.baw-photo-addon__thumb--mockup')
      if (nativeWide && thumbSlot) {
        // Shared visible-frame box (453×332 samples) for Gold and Silver.
        // Do not size from each PNG’s native aspect — taller Silver Files
        // sat low in a taller box (plate near the bottom of the left opening).
        // Opaque-fit clips square-padded Clay; cover crops landscape drift.
        var slotW = longMax > 0 ? longMax : 36
        var slotHCap = shortMax > 0 ? shortMax : slotW
        var canonAspect = previewRoot.classList.contains('is-clay-wide')
          ? 467 / 342
          : 453 / 332
        var sharedW = slotW
        var sharedH = sharedW / canonAspect
        if (sharedH > slotHCap && slotHCap > 0) {
          sharedH = slotHCap
          sharedW = sharedH * canonAspect
        }
        if (!(sharedW > 0 && sharedH > 0)) return
        dispW = Math.round(sharedW)
        dispH = Math.round(sharedH)
        previewRoot.classList.add('is-dual-thumb-slot')
        applyNativeWideArtboardFit(previewRoot, frameImg, natW, natH, false)
      } else if (nativeWide) {
        previewRoot.classList.remove('is-dual-thumb-slot')
        // Same display WIDTH for Gold and Silver. Height-contain only inside the
        // review 1:1 card so taller Silver artboards do not shove/clip the plate.
        // Plate chip width is unified separately (syncReviewNameplatePlateSizes).
        var targetW = longMax
        var reviewMockup = previewRoot.closest('.baw-review-product-mockup')
        if (reviewMockup) {
          // Ink/Clay review stacks: match landscape Photo Tribute frame width
          // when a ready urn is in the same review grid (CSS long is already
          // tribute×0.80 as fallback when no landscape urn is present).
          var reviewHost =
            reviewMockup.closest('.baw-review-products') ||
            reviewMockup.closest('.baw-review-layout') ||
            reviewMockup.closest('.baw-step')
          if (reviewHost) {
            var tributes = reviewHost.querySelectorAll(
              '.baw-review-product-mockup .baw-photo-mockup.is-native-tall.is-landscape'
            )
            var refW = 0
            for (var ti = 0; ti < tributes.length; ti++) {
              var tributeEl = tributes[ti]
              if (!tributeEl.classList.contains('is-frame-ready')) continue
              var tw = tributeEl.offsetWidth
              if (tw > refW) refW = tw
            }
            if (refW > 0) targetW = refW
          }
          // Never wider than the mockup stage (1:1 card).
          if (containBox && containBox.clientWidth > 0) {
            targetW = Math.min(targetW, containBox.clientWidth)
          }
        }
        var art = applyNativeWideArtboardFit(previewRoot, frameImg, natW, natH, false)
        if (previewRoot.classList.contains('is-clay-wide') && !art.padFit) {
          // Landscape Clay blanks: Gold 467×342 box; Silver 453×332 cover-crops.
          var clayAspect = 467 / 342
          dispW = targetW
          dispH = targetW / clayAspect
          if (
            shortMax > 0 &&
            dispH > shortMax + 0.5 &&
            previewRoot.closest('.baw-review-product-stage')
          ) {
            dispH = shortMax
            dispW = dispH * clayAspect
          }
          if (!(dispW > 0 && dispH > 0)) return
          dispW = Math.round(dispW)
          dispH = Math.round(dispH)
        } else if (art.padFit) {
          scale = targetW / (natW * art.fitW)
          if (
            shortMax > 0 &&
            natH * art.fitH * scale > shortMax + 0.5 &&
            previewRoot.closest('.baw-review-product-stage')
          ) {
            scale = shortMax / (natH * art.fitH)
          }
          if (!(scale > 0)) return
          dispW = Math.round(natW * art.fitW * scale)
          dispH = Math.round(natH * art.fitH * scale)
        } else {
          scale = targetW / natW
          if (
            shortMax > 0 &&
            natH * scale > shortMax + 0.5 &&
            previewRoot.closest('.baw-review-product-stage')
          ) {
            scale = shortMax / natH
          }
          if (!(scale > 0)) return
          dispW = Math.round(natW * scale)
          dispH = Math.round(natH * scale)
        }
      } else {
        previewRoot.classList.remove('is-dual-thumb-slot')
        clearNativeWideArtboardFit(previewRoot)
        scale = Math.min(shortMax / natW, longMax / natH)
        // Photo Tribute on review fills the square more than Ink/Clay —
        // tone down so visual weight matches dual-window cards.
        if (previewRoot.closest('.baw-review-product-mockup')) {
          if (previewRoot.classList.contains('is-landscape')) {
            scale *= 0.8
          } else {
            // Portrait: modest shrink (less than landscape’s ×0.80).
            scale *= 0.88
          }
        }
        if (!(scale > 0)) return
        dispW = Math.round(natW * scale)
        dispH = Math.round(natH * scale)
      }
      if (!(dispW > 0 && dispH > 0)) return
      // Box must match the displayed frame aspect — a max-height clamp with a
      // fixed width letterboxes dual-window frames and shifts the right-pane %.
      previewRoot.style.setProperty('--baw-mockup-disp-w', dispW + 'px')
      previewRoot.style.setProperty('--baw-mockup-disp-h', dispH + 'px')
      // Dual-window / Tribute cutout percents stay on CSS. Clear stale inline
      // overrides so Ink/Clay/Tribute holes cannot leak across artboards.
      previewRoot.style.removeProperty('--baw-cutout-t')
      previewRoot.style.removeProperty('--baw-cutout-r')
      previewRoot.style.removeProperty('--baw-cutout-b')
      previewRoot.style.removeProperty('--baw-cutout-l')
      // Thumbs / personalise / review: lock the used box. Photo-step upload and
      // preview modals stay CSS-fluid so frames can shrink to the panel.
      // Cutout percents unchanged (Ink 5×7 locked; Tribute 4×6 on .is-native-tall).
      if (nativeWide && previewRoot.closest('.baw-photo-preview-modal')) {
        previewRoot.style.width = '100%'
        previewRoot.style.height = 'auto'
        previewRoot.style.maxWidth = '100%'
        previewRoot.style.maxHeight = 'none'
      } else if (!previewRoot.closest('.baw-photo-upload') && !previewRoot.closest('.baw-photo-preview-modal')) {
        if (nativeWide) {
          previewRoot.style.width = dispW + 'px'
          previewRoot.style.height = dispH + 'px'
          previewRoot.style.maxWidth = dispW + 'px'
          previewRoot.style.maxHeight = dispH + 'px'
        } else if (previewRoot.classList.contains('is-landscape')) {
          // Tall artboard rotated 90°: disp-h is display width.
          previewRoot.style.width = dispH + 'px'
          previewRoot.style.height = dispW + 'px'
          previewRoot.style.maxWidth = dispH + 'px'
          previewRoot.style.maxHeight = dispW + 'px'
        } else {
          previewRoot.style.width = dispW + 'px'
          previewRoot.style.height = dispH + 'px'
          previewRoot.style.maxWidth = dispW + 'px'
          previewRoot.style.maxHeight = dispH + 'px'
        }
      }
      // After review frames settle (Gold/Silver artboards differ), re-unify plates.
      if (previewRoot.closest('.baw-review-product-mockup')) {
        scheduleSyncReviewNameplatePlateSizes(previewRoot)
      }
    }

    function wireOnePhotoPreviewDecode(previewRoot, container) {
      if (!previewRoot) return
      var frameImg = previewRoot.querySelector('.baw-photo-mockup__frame')
      if (frameImg) {
        previewRoot.classList.add('is-frame-pending')
        previewRoot.classList.remove('is-frame-ready')
        function frameReady() {
          sizePhotoMockupFromFrame(previewRoot, frameImg)
          // Only leave pending when we could measure — otherwise Gold→Silver swaps
          // can paint with fallback 148×84 and shove the photo into the gutter.
          if (frameImg.naturalWidth > 0 && frameImg.naturalHeight > 0) {
            previewRoot.classList.remove('is-frame-pending')
            previewRoot.classList.add('is-frame-ready')
          }
        }
        if (frameImg.complete && frameImg.naturalWidth > 0) {
          frameReady()
          // Metal swap: layout/CSS vars may not be final on the first measure.
          if (typeof window.requestAnimationFrame === 'function') {
            window.requestAnimationFrame(function () {
              sizePhotoMockupFromFrame(previewRoot, frameImg)
            })
          }
        } else if (frameImg.complete && !(frameImg.naturalWidth > 0)) {
          // Cached broken/empty decode — load/error will not re-fire; still
          // leave pending so review cards are not a permanent cream blank.
          previewRoot.classList.remove('is-frame-pending')
          previewRoot.classList.add('is-frame-ready')
        } else {
          frameImg.addEventListener('load', function () {
            frameReady()
            if (typeof window.requestAnimationFrame === 'function') {
              window.requestAnimationFrame(function () {
                sizePhotoMockupFromFrame(previewRoot, frameImg)
              })
            }
          }, { once: true })
          frameImg.addEventListener('error', function () {
            previewRoot.classList.remove('is-frame-pending')
            previewRoot.classList.add('is-frame-ready')
          }, { once: true })
        }
      }
      var img = previewRoot.querySelector('[data-photo-preview-img]')
      if (!img) return
      var pending = previewRoot.querySelector('[data-photo-preview-pending]')
      var uploading = !!container.querySelector('.baw-photo-upload.is-busy')
      previewRoot.classList.add('is-decoding')
      previewRoot.classList.remove('is-ready')
      if (pending && !uploading) {
        pending.removeAttribute('aria-hidden')
        pending.setAttribute('role', 'status')
        pending.setAttribute('aria-live', 'polite')
      }
      function reveal() {
        previewRoot.classList.remove('is-decoding')
        previewRoot.classList.add('is-ready')
        if (pending) {
          pending.setAttribute('aria-hidden', 'true')
          pending.removeAttribute('role')
          pending.removeAttribute('aria-live')
        }
      }
      function failSoft() {
        // Still show the frame — better a late paint than a permanent spinner.
        reveal()
      }
      if (img.complete && img.naturalWidth > 0) {
        reveal()
        return
      }
      if (typeof img.decode === 'function') {
        img.decode().then(reveal).catch(failSoft)
        return
      }
      img.addEventListener('load', reveal, { once: true })
      img.addEventListener('error', failSoft, { once: true })
    }

    function wirePhotoPreviewDecodeIn(container) {
      if (!container) return
      var roots = container.querySelectorAll('[data-photo-print-preview]')
      for (var i = 0; i < roots.length; i++) {
        wireOnePhotoPreviewDecode(roots[i], container)
      }
    }

    /** Re-measure mockup boxes after layout/metal swap without flashing pending. */
    function resizePhotoMockupsIn(container) {
      if (!container) return
      var roots = container.querySelectorAll('[data-photo-print-preview]')
      for (var i = 0; i < roots.length; i++) {
        var root = roots[i]
        var frameImg = root.querySelector('.baw-photo-mockup__frame')
        if (frameImg && frameImg.naturalWidth > 0) sizePhotoMockupFromFrame(root, frameImg)
      }
    }

    /**
     * Review grid — one shared nameplate size for Gold and Silver across
     * Photo Tribute / Ink / Clay. Per-frame sync made taller Silver dual-window
     * artboards (height-contained) get a smaller plate than Gold.
     * Prefer the current Gold visual width; scale Silver up to match.
     */
    function syncReviewNameplatePlateSizes(container) {
      if (!container || syncReviewNameplatePlateSizes._locking) return
      var stages = container.querySelectorAll('.baw-review-product-stage.has-plate')
      if (!stages.length) return
      var goldMax = 0
      var anyMax = 0
      var plates = []
      var i
      for (i = 0; i < stages.length; i++) {
        var stage = stages[i]
        var plate = stage.querySelector(':scope > .baw-review-product-plate')
        if (!plate) continue
        plates.push(plate)
        // Dock to the card/stage, not the contained mockup (88% of a tiny
        // Tribute frame made 168px chips). Stay inside the card — no overlay.
        var stageW = stage.clientWidth
        if (!(stageW > 0)) continue
        var candidate = Math.min(stageW, Math.round(stageW * 0.94))
        anyMax = Math.max(anyMax, candidate)
        if (!plate.classList.contains('is-silver')) {
          goldMax = Math.max(goldMax, candidate)
        }
      }
      var target = goldMax > 0 ? goldMax : anyMax
      if (!(target > 0) || !plates.length) return
      syncReviewNameplatePlateSizes._locking = true
      try {
        for (i = 0; i < plates.length; i++) {
          plates[i].style.width = target + 'px'
          plates[i].style.maxWidth = target + 'px'
          plates[i].style.minWidth = target + 'px'
          // Keep plate in normal flow under the frame (clear any stale absolute).
          plates[i].style.position = 'relative'
          plates[i].style.left = 'auto'
          plates[i].style.right = 'auto'
          plates[i].style.bottom = 'auto'
          plates[i].style.top = 'auto'
          plates[i].style.transform = 'none'
        }
        // Plate height changed the flex budget — re-contain frames in the card.
        resizePhotoMockupsIn(container)
      } finally {
        syncReviewNameplatePlateSizes._locking = false
      }
    }

    function scheduleSyncReviewNameplatePlateSizes(fromEl) {
      if (syncReviewNameplatePlateSizes._locking) return
      var host =
        (fromEl &&
          (fromEl.closest('.baw-review-products') ||
            fromEl.closest('.baw-review-layout') ||
            fromEl.closest('.baw-step'))) ||
        null
      if (!host) return
      if (typeof window.requestAnimationFrame !== 'function') {
        syncReviewNameplatePlateSizes(host)
        return
      }
      if (scheduleSyncReviewNameplatePlateSizes._raf) return
      scheduleSyncReviewNameplatePlateSizes._raf = window.requestAnimationFrame(function () {
        scheduleSyncReviewNameplatePlateSizes._raf = 0
        syncReviewNameplatePlateSizes(host)
      })
    }

    function wirePhotoPreviewDecode() {
      wirePhotoPreviewDecodeIn(els.step)
    }

    /** Preview disclaimer after a photo is selected — GuidanceBar, not under-preview caption.
     *  Always PHOTO_PRINT_GUIDANCE_LINE — ignores TE / stepCopy (stale section settings).
     *  LOCKED: wrapGuidanceBarHtml must not bare-throw — Continue → photo step depends on it. */

    function renderPhotoPrintGuidanceHtml(_photo, _flags) {
      var inner =
        '<p class="baw-guidance-line baw-step-footer-note baw-guidance-line--photo">' +
        escapeHtml(PHOTO_PRINT_GUIDANCE_LINE) +
        '</p>'
      if (typeof wrapGuidanceBarHtml === 'function') {
        return wrapGuidanceBarHtml(inner, { variant: 'info' })
      }
      return '<div class="baw-guidance" data-baw-guidance>' + inner + '</div>'
    }

    function photoFileLooksAllowed(file) {
      if (!file) return false
      var type = String(file.type || '').toLowerCase()
      if (type && PHOTO_UPLOAD_ACCEPT_SET[type]) return true
      // Some mobile browsers omit MIME for HEIC — fall back to extension.
      var name = String(file.name || '').toLowerCase()
      return /\\.(jpe?g|png|webp|heic|heif)$/.test(name)
    }

    function validatePhotoFileClient(file) {
      if (!file) return 'Please choose a photo to continue.'
      if (!photoFileLooksAllowed(file)) {
        return 'Please choose a JPEG, PNG, WEBP or HEIC photo.'
      }
      if (file.size > PHOTO_UPLOAD_MAX_BYTES) {
        return 'That photo is too large (max 8MB). Please choose a smaller image.'
      }
      return null
    }

    function focusPhotoUploadZone() {
      var zone =
        els.step.querySelector('[data-photo-upload-trigger]') ||
        els.step.querySelector('[data-photo-input]') ||
        els.step.querySelector('#baw-photo-input')
      if (!zone) return
      try { zone.focus({ preventScroll: false }) } catch (err) {
        try { zone.focus() } catch (err2) { /* ignore */ }
      }
    }

    /** Continue / chrome forward guard — missing upload gets inline validation + focus. */

    function tryAdvanceMemorialPhotoStep(groupKey, productId) {
      var entry = getMemorialEntry(state, groupKey)
      var pid = resolveMemorialAddonProductId(state, config, groupKey, 'photo', productId)
      focusMemorialAddonProduct(entry, pid)
      if (!entry || !needsPhotoUpload(entry)) return true
      if (entry.addons.photo.uploading) return false
      if (entry.addons.photo.photoUrl) {
        entry.addons.photo.missingUploadError = false
        return true
      }
      entry.addons.photo.missingUploadError = true
      entry.addons.photo.uploadError = false
      entry.addons.photo.uploadErrorMessage = null
      renderMemorialAddonPhotoStep(groupKey, pid)
      focusPhotoUploadZone()
      return false
    }

    function renderMemorialAddonPhotoStep(groupKey, productId) {
      var entry = getMemorialEntry(state, groupKey)
      var catalog = getMemorialCatalog(state, config)
      var pid = resolveMemorialAddonProductId(state, config, groupKey, 'photo', productId)
      focusMemorialAddonProduct(entry, pid)
      var source = memorialAddonSourceForProduct(
        state, config, groupKey, catalog, 'photo', pid
      )
      var npMetal = null
      if (typeof AngelAshesBookingMemorials !== 'undefined' && AngelAshesBookingMemorials.nameplateMetalForProduct) {
        npMetal = AngelAshesBookingMemorials.nameplateMetalForProduct(entry, pid)
      } else if (entry && entry.itemAddons && pid != null) {
        var npBucket = entry.itemAddons[String(pid)]
        var npAddon = npBucket && npBucket.nameplate
        if (npAddon && String(npAddon.choiceId || '') !== 'no') npMetal = npAddon.colour || null
      }
      var itemContext = memorialAddonItemContext(catalog, groupKey, source, npMetal)
      var flags = (source && source.flags) || {}
      var feeProduct = flags.photoFeeProduct || null
      var feeVariant = firstPricedFeeVariant(feeProduct)
      if (!entry.addons.photo) entry.addons.photo = {}

      // Print geometry travels with the selection so the summary / cart can read it
      // without re-resolving the Theme Editor block.
      entry.addons.photo.size = normalizePhotoPrintSize(flags.photoPrintSize)
      entry.addons.photo.orientation = effectivePhotoOrientation(flags, entry.addons.photo)

      // Paid print fee ($9.95 on urns / Photo Tribute): stay opt-in so we do
      // not charge or gate Continue on an upload they never asked for.
      // Included Ink / Clay frames have no priced fee — preselect Printed photo
      // so the upload stage is open. Explicit skip / No thanks still wins.
      var photoIncluded = !feeVariant || !(Number(feeVariant.price) > 0)
      if (
        photoIncluded &&
        !entry.addons.photo._photoChoiceTouched &&
        String(entry.addons.photo.choiceId || '') !== 'no' &&
        !needsPhotoUpload(entry)
      ) {
        entry.addons.photo.choiceId = 'yes'
        entry.addons.photo.choiceLabel = 'Printed photo'
        if (feeVariant) entry.addons.photo.variant = feeVariant
      }
      var selected = needsPhotoUpload(entry)
      // Heal missing fee variant after draft restore so summary/cart stay priced.
      if (selected && !entry.addons.photo.variant && feeVariant) {
        entry.addons.photo.variant = feeVariant
      }
      var priceLabel = feeVariant ? money(feeVariant.price) : ''
      // Theme Editor card copy: blank title → “Printed photo”; blank subtitle hides desc.
      var photoCardTitle = String(flags.photoCardTitle || '').trim() || 'Printed photo'
      var photoCardSubtitle = String(flags.photoCardSubtitle || '').trim()
      // Card thumb: blank product frame (mockup / personalise), not the customer
      // photo — object-fit:contain so landscape dual-window artboards stay intact.
      // LOCKED: resolvePhotoStepMockupFrame must stay ctx/module-safe (see bawPhotoStepHelper).
      var resolveFrame = bawPhotoStepHelper('resolvePhotoStepMockupFrame')
      var photoFrameThumb = resolveFrame
        ? (resolveFrame(flags, npMetal) || resolveFrame(itemContext, npMetal))
        : null
      var thumbPx =
        (typeof AngelAshesBookingMemorials !== 'undefined' &&
          AngelAshesBookingMemorials.PHOTO_ADDON_THUMB_PX) ||
        240
      var thumbSrc = photoFrameThumb || itemContext.image
      var thumb = thumbSrc
        ? (
          '<img class="baw-photo-addon__thumb baw-photo-addon__thumb--frame" src="' +
          escapeHtml(sizedShopifyImageUrl(thumbSrc, thumbPx)) +
          '" alt="' + escapeHtml(itemContext.label || 'Memorial product') + '"' +
          ' loading="eager" decoding="async" width="64" height="64" />'
        )
        : '<span class="baw-photo-addon__thumb baw-photo-addon__thumb--empty" aria-hidden="true"></span>'

      var setupHtml = selected
        ? renderPhotoSetupHtml(entry.addons.photo, flags, npMetal, itemContext.label)
        : ''

      var photoSubtitle = memorialPhotoStepSubtitle()
      var guidanceHtml = selected
        ? renderPhotoPrintGuidanceHtml(entry.addons.photo, flags)
        : ''
      var html =
        '<div class="baw-step-inner baw-step-inner--photo' + (selected ? ' is-photo-open' : '') + '">' +
        stepIntroHtml(
          memorialPhotoStepHeadline(itemContext.label),
          photoSubtitle
            ? '<div class="baw-helper"><p>' + escapeHtml(photoSubtitle) + '</p></div>'
            : ''
        ) +
        '<div class="baw-photo-step' + (selected ? ' is-open' : '') + '">' +
        '<button type="button" class="baw-photo-addon' + (selected ? ' is-selected' : '') + '"' +
        ' data-photo-addon role="checkbox" aria-checked="' + (selected ? 'true' : 'false') + '"' +
        ' aria-controls="baw-photo-setup">' +
        '<span class="baw-photo-addon__check" aria-hidden="true"></span>' +
        thumb +
        '<span class="baw-photo-addon__body">' +
        '<span class="baw-photo-addon__title">' + escapeHtml(photoCardTitle) + '</span>' +
        (photoCardSubtitle
          ? '<span class="baw-photo-addon__desc">' + escapeHtml(photoCardSubtitle) + '</span>'
          : '') +
        '</span>' +
        (priceLabel
          ? '<span class="baw-photo-addon__price">' + priceLabel + '</span>'
          : '<span class="baw-photo-addon__price baw-photo-addon__price--empty" aria-hidden="true"></span>') +
        '</button>' +
        optionalSkipButtonHtml('No thanks — continue without a photo') +
        (selected
          ? '<div id="baw-photo-setup" class="baw-photo-setup-wrap">' + setupHtml + '</div>'
          : '') +
        '</div>' +
        // GuidanceBar sits outside the upload control; mountStepFooter pins it above Continue.
        guidanceHtml +
        '<div class="baw-actions">' +
        '<button type="button" class="baw-continue" data-baw-continue>Continue</button>' +
        '</div>' +
        '</div>'

      els.step.innerHTML = html

      var addonBtn = els.step.querySelector('[data-photo-addon]')
      if (addonBtn) {
        addonBtn.addEventListener('click', function (e) {
          // Re-focus this product — renderSummary/buildLedger may have left
          // entry.addons pointing at another multi-select urn's bucket.
          e.preventDefault()
          focusMemorialAddonProduct(entry, pid)
          if (!entry.addons.photo) entry.addons.photo = {}
          entry.addons.photo._photoChoiceTouched = true
          if (selected) {
            // Deselect — drop paid line + uploaded URLs so cart never keeps an orphan photo.
            // Keep orientation / _orientationTouched so reselect restores the family's choice.
            entry.addons.photo.choiceId = null
            entry.addons.photo.choiceLabel = null
            entry.addons.photo.variant = null
            entry.addons.photo.missingUploadError = false
            resetPhotoUploadState(entry)
          } else {
            entry.addons.photo.choiceId = 'yes'
            entry.addons.photo.choiceLabel = 'Printed photo'
            entry.addons.photo.variant = feeVariant
            entry.addons.photo.missingUploadError = false
            // First enable: Theme Editor default (→ landscape if unset). Draft/manual win.
            if (
              normalizePhotoOrientation(flags.photoOrientation) === 'client_choice' &&
              !entry.addons.photo._orientationTouched
            ) {
              var existingOrient = String(entry.addons.photo.orientation || '').trim()
              if (existingOrient !== 'landscape' && existingOrient !== 'portrait') {
                entry.addons.photo.orientation = normalizePhotoOrientationDefault(
                  flags.photoOrientationDefault
                )
              }
            }
            queueMobileReveal('[data-photo-setup]', '[data-photo-upload-trigger]')
          }
          a11yRestoreFocus = '[data-photo-addon]'
          renderMemorialAddonPhotoStep(groupKey, pid)
          renderSummary()
        })
      }

      var continueBtn = els.step.querySelector('[data-baw-continue]')
      if (continueBtn) {
        continueBtn.addEventListener('click', function () { goNext() })
      }
      var photoSkip = els.step.querySelector('[data-baw-optional-skip]')
      if (photoSkip) {
        photoSkip.addEventListener('click', function () {
          // Explicit decline — same cart outcome as leaving the card unselected + Continue,
          // and bypasses “required when selected” upload validation by clearing first.
          focusMemorialAddonProduct(entry, pid)
          if (!entry.addons.photo) entry.addons.photo = {}
          entry.addons.photo.choiceId = null
          entry.addons.photo.choiceLabel = null
          entry.addons.photo.variant = null
          entry.addons.photo.missingUploadError = false
          entry.addons.photo._photoChoiceTouched = true
          resetPhotoUploadState(entry)
          renderSummary()
          goNext()
        })
      }

      wirePhotoUpload(entry, groupKey, pid)
      wirePhotoOrientationToggle(entry, groupKey, pid)
      wirePhotoPreviewDecode()
      if (typeof enhanceOptionGroup === 'function') {
        enhanceOptionGroup(
          els.step.querySelector('[data-orientation-tiles]'),
          '[data-photo-orientation]',
          'Photo orientation'
        )
      }
      if (typeof mountStepFooter === 'function') mountStepFooter()
      // Disabled Continue does not receive clicks — let the actions strip catch
      // them so a missing upload still shows inline error + focuses the zone.
      var photoActionsEl = els.footer && els.footer.querySelector('.baw-actions')
      if (photoActionsEl && !photoActionsEl.getAttribute('data-photo-validate-bound')) {
        photoActionsEl.setAttribute('data-photo-validate-bound', '1')
        photoActionsEl.addEventListener('click', function () {
          var btn = photoActionsEl.querySelector('[data-baw-continue]')
          if (btn && btn.disabled) {
            tryAdvanceMemorialPhotoStep(groupKey, pid)
          }
        })
      }
      // LOCKED — typeof guards: a missing late-bound ctx helper must not throw
      // after HTML paint, or goTo rolls back and Continue looks like a no-op.
      if (typeof syncFunnelChromePadding === 'function') syncFunnelChromePadding()
      if (typeof setContinueEnabled === 'function') {
        setContinueEnabled(
          typeof canAdvanceFrom === 'function' ? canAdvanceFrom(state.currentStepId) : true
        )
      }
      if (typeof refreshChrome === 'function') refreshChrome()
      if (typeof flushMobileReveal === 'function') flushMobileReveal()
      if (typeof restoreA11yFocus === 'function') restoreA11yFocus()
    }

    /** Clear everything derived from a file so a re-pick can't leave a stale print master. */

    function resetPhotoUploadState(entry) {
      var photo = entry && entry.addons && entry.addons.photo
      if (!photo) return
      photo.photoUrl = null
      photo.photoPrintUrl = null
      photo.composing = false
      photo.uploadError = false
      photo.uploadErrorMessage = null
      photo.missingUploadError = false
      photo.fileName = null
      photo.uploading = false
      photo.uploadGeneration = (photo.uploadGeneration || 0) + 1
      revokePhotoLocalPreview(entry)
    }

    /** Portrait / Landscape picker — only when the Product block says \`client_choice\`. */

    function wirePhotoOrientationToggle(entry, groupKey, productId) {
      var buttons = els.step.querySelectorAll('[data-photo-orientation]')
      if (!buttons || !buttons.length) return
      Array.prototype.forEach.call(buttons, function (btn) {
        btn.addEventListener('click', function () {
          focusMemorialAddonProduct(entry, productId)
          if (!entry.addons.photo) entry.addons.photo = {}
          var next = btn.getAttribute('data-photo-orientation')
          if (next === entry.addons.photo.orientation) return
          entry.addons.photo.orientation = next === 'landscape' ? 'landscape' : 'portrait'
          entry.addons.photo._orientationTouched = true
          // The print master is cropped to a specific aspect — it no longer matches.
          entry.addons.photo.photoPrintUrl = null
          a11yRestoreFocus =
            '[data-photo-orientation="' + (next === 'landscape' ? 'landscape' : 'portrait') + '"]'
          renderMemorialAddonPhotoStep(groupKey, productId)
          if (entry.addons.photo.photoUrl) composePhotoForEntry(entry, groupKey)
          renderSummary()
        })
      })
    }

    /** Whether the chosen photo add-on option requires the client to upload a file. */

    function needsPhotoUpload(entry) {
      if (!entry || !entry.addons || !entry.addons.photo) return false
      var label = (entry.addons.photo.choiceLabel || '').toLowerCase().trim()
      var id = String(entry.addons.photo.choiceId || '')
      if (!label && !id) return false
      if (id === 'no') return false
      if (label.indexOf('not') !== -1) return false
      if (label === 'no' || label.indexOf('no thanks') !== -1) return false
      return id === 'yes' || !!label
    }

    function revokePhotoLocalPreview(entry) {
      if (!entry || !entry.addons || !entry.addons.photo) return
      var url = entry.addons.photo.localPreviewUrl
      if (!url) return
      try { URL.revokeObjectURL(url) } catch (e) { /* ignore */ }
      entry.addons.photo.localPreviewUrl = null
    }

    /**
     * Open photo setup workspace.
     * DOM order: orientation (when client_choice) → stage (upload/preview +
     * Replace/Remove under the frame). Mobile: stacked in-flow. Desktop:
     * Photo Tribute (tall) keeps orient | stage. Dual-window Clay/Ink share a
     * centred preview well — no empty spacer column.
     *
     * LOCKED — Continue from the memorial grid lands here via
     * renderMemorialAddonPhotoStep → renderPhotoSetupHtml. Helpers below must
     * resolve from ctx (MemorialUI.create exports) or the module mirror; a bare
     * ReferenceError rolls goTo back to the grid and Continue looks like a no-op.
     */

    function bawPhotoStepHelper(name) {
      // typeof never throws for unbound ids (including under with(ctx)).
      if (name === 'photoStepUsesDualWindowLayout' && typeof photoStepUsesDualWindowLayout === 'function') {
        return photoStepUsesDualWindowLayout
      }
      if (name === 'photoStepMockupPreviewOpts' && typeof photoStepMockupPreviewOpts === 'function') {
        return photoStepMockupPreviewOpts
      }
      if (name === 'resolvePhotoStepMockupFrame' && typeof resolvePhotoStepMockupFrame === 'function') {
        return resolvePhotoStepMockupFrame
      }
      if (name === 'resolvePersonaliseMockupFrame' && typeof resolvePersonaliseMockupFrame === 'function') {
        return resolvePersonaliseMockupFrame
      }
      var mod = typeof AngelAshesBookingMemorialUI !== 'undefined' ? AngelAshesBookingMemorialUI : null
      return mod && typeof mod[name] === 'function' ? mod[name] : null
    }

    function renderPhotoSetupHtml(photo, flags, colour, itemLabel) {
      var canChoose =
        normalizePhotoOrientation(flags && flags.photoOrientation) === 'client_choice'
      var dualFn = bawPhotoStepHelper('photoStepUsesDualWindowLayout')
      var dualWindow = dualFn ? !!dualFn(flags, colour) : false
      var orientHtml = renderPhotoOrientationToggleHtml(photo, flags)
      var uploadParts = renderPhotoUploadParts(photo, flags, colour, itemLabel)
      var workspaceClass = 'baw-photo-workspace'
      if (canChoose) workspaceClass += ' has-orient'
      else if (dualWindow) workspaceClass += ' has-dual-window'
      var controlsHtml = ''
      if (canChoose) {
        controlsHtml = '<div class="baw-photo-workspace__controls">' + orientHtml + '</div>'
      }
      return (
        '<div class="baw-photo-setup" data-photo-setup>' +
        '<div class="' + workspaceClass + '">' +
        controlsHtml +
        uploadParts.stageHtml +
        '</div>' +
        '</div>'
      )
    }

    /** Portrait / Landscape control — \`client_choice\` only.
     *  Theme Editor default first; outlined rect icons. */

    function renderPhotoOrientationToggleHtml(photo, flags) {
      if (normalizePhotoOrientation(flags && flags.photoOrientation) !== 'client_choice') return ''
      var current = effectivePhotoOrientation(flags, photo)
      var defaultOrientation = normalizePhotoOrientationDefault(
        flags && flags.photoOrientationDefault
      )
      var options =
        defaultOrientation === 'portrait'
          ? [
              { id: 'portrait', label: 'Portrait' },
              { id: 'landscape', label: 'Landscape' },
            ]
          : [
              { id: 'landscape', label: 'Landscape' },
              { id: 'portrait', label: 'Portrait' },
            ]
      /* No visible “Photo orientation” heading — Landscape/Portrait + icons are
         self-explanatory; radiogroup aria-label covers assistive tech. */
      return (
        '<div class="baw-photo-orient">' +
        '<div class="baw-photo-orient__options" role="radiogroup" aria-label="Photo orientation" data-orientation-tiles>' +
        options.map(function (opt) {
          var selected = opt.id === current
          return (
            '<button type="button" class="baw-photo-orient__btn' + (selected ? ' is-selected' : '') + '"' +
            ' data-photo-orientation="' + opt.id + '"' +
            ' role="radio" aria-checked="' + (selected ? 'true' : 'false') + '">' +
            '<span class="baw-tile-check" aria-hidden="true"></span>' +
            photoOrientationIconSvg(opt.id) +
            '<span class="baw-photo-orient__text">' +
            '<span class="baw-photo-orient__label">' + escapeHtml(opt.label) + '</span>' +
            '</span>' +
            '</button>'
          )
        }).join('') +
        '</div>' +
        '</div>'
      )
    }

    /** Locked Theme Editor orientation (not “Let the family choose”). */
    function lockedPhotoOrientation(flags) {
      var configured = normalizePhotoOrientation(flags && flags.photoOrientation)
      if (configured === 'portrait' || configured === 'landscape') return configured
      return ''
    }

    /**
     * Upload / preview stage — zone + optional Replace/Remove under the frame.
     * Lives in \`.baw-photo-workspace\` (mobile stack + desktop stage column).
     * With a product mockup, the transparent photo window is the upload target
     * (no second dashed box below, no overlay on the Printed photo card).
     * Orientation is shown by the frame itself — no “Portrait/Landscape photo
     * required” heading above the preview.
     */

    function renderPhotoUploadParts(photo, flags, colour, itemLabel) {
      photo = photo || {}
      flags = flags || {}
      var uploading = !!photo.uploading
      var hasRemote = !!photo.photoUrl
      // Always crop from the original upload — never a pre-cropped print master.
      var previewSrc = photo.photoUrl || photo.localPreviewUrl || ''
      var orientation = effectivePhotoOrientation(flags, photo)
      var lockedOrient = lockedPhotoOrientation(flags)
      var chooseLabel = 'Choose a photo'
      var errMsg = ''
      if (!uploading && photo.uploadError) {
        errMsg = photo.uploadErrorMessage || 'We couldn\\u2019t upload that photo. Please try again.'
      } else if (!uploading && !hasRemote && photo.missingUploadError) {
        errMsg = 'Please choose a photo to continue, or deselect Printed photo.'
      }
      var errorId = 'baw-photo-upload-error'
      var statusClass = uploading
        ? ' is-busy'
        : (hasRemote ? ' is-ready' : (errMsg ? ' is-error' : ' is-empty'))

      var mockupOptsFn = bawPhotoStepHelper('photoStepMockupPreviewOpts')
      var mockupOpts = mockupOptsFn ? mockupOptsFn(flags, colour) : null
      var mockupFrame = mockupOpts ? mockupOpts.frameUrl : null
      var previewOpts = mockupOpts || {
        printSize: normalizePhotoPrintSize(flags.photoPrintSize || photo.size),
      }
      if (itemLabel) previewOpts.itemLabel = itemLabel
      if (previewOpts.nativeWide && looksLikeInkWideArtboard(previewOpts.frameUrl, itemLabel)) {
        previewOpts.inkWide = true
      } else if (previewOpts.nativeWide && looksLikeClayWideArtboard(previewOpts.frameUrl, itemLabel)) {
        previewOpts.clayWide = true
      }
      var mockupClass = mockupFrame ? ' baw-photo-upload--mockup' : ''
      var inWindowUpload = !!(mockupFrame && !uploading && !hasRemote)

      var describedBy = errMsg ? errorId : ''
      var triggerAttrs = hasRemote && !uploading
        ? ''
        : (
          ' tabindex="0" role="button"' +
          ' aria-label="' + escapeHtml(chooseLabel) + '"' +
          (describedBy ? ' aria-describedby="' + describedBy + '"' : '')
        )

      var fileInputHtml =
        '<input type="file" accept="' + PHOTO_UPLOAD_ACCEPT + '" id="baw-photo-input" data-photo-input' +
        ' class="baw-photo-upload__input' + (inWindowUpload ? ' baw-photo-upload__input--in-window' : '') + '"' +
        (inWindowUpload ? ' tabindex="-1"' : '') +
        ' />'

      var zoneInner = ''
      if (uploading) {
        previewOpts.showBusy = true
        zoneInner =
          '<div class="baw-photo-upload__ready">' +
          renderPhotoPrintPreview(previewSrc, orientation, previewOpts) +
          '</div>'
      } else if (hasRemote && previewSrc) {
        zoneInner =
          '<div class="baw-photo-upload__ready">' +
          renderPhotoPrintPreview(previewSrc, orientation, previewOpts) +
          '</div>'
      } else if (mockupFrame) {
        previewOpts.emptyHtml = photoChooseEmptyHtml()
        previewOpts.inputHtml = fileInputHtml
        previewOpts.windowAttrs =
          'data-photo-upload-trigger data-photo-upload-label' + triggerAttrs
        zoneInner = renderPhotoPrintPreview('', orientation, previewOpts)
      } else {
        zoneInner = photoChooseEmptyHtml()
      }

      var actionsHtml =
        hasRemote && !uploading
          ? (
            '<div class="baw-photo-workspace__actions">' +
            '<div class="baw-photo-upload__ready-meta">' +
            '<div class="baw-photo-upload__ready-actions">' +
            '<button type="button" class="baw-photo-upload__action" data-photo-replace>Replace</button>' +
            '<button type="button" class="baw-photo-upload__action baw-photo-upload__action--quiet" data-photo-remove>Remove</button>' +
            '</div>' +
            '</div>' +
            '</div>'
          )
          : ''

      var lockClass = lockedOrient === 'portrait'
        ? ' is-locked-portrait'
        : (lockedOrient === 'landscape' ? ' is-locked-landscape' : '')
      var zoneOpenTag = hasRemote && !uploading
        ? '<div class="baw-photo-upload__zone">'
        : (inWindowUpload
          ? '<div class="baw-photo-upload__zone">'
          : (
            '<div class="baw-photo-upload__zone" data-photo-upload-trigger data-photo-upload-label' +
            triggerAttrs + '>'
          ))
      var stageHtml =
        '<div class="baw-photo-workspace__stage">' +
        '<div class="baw-photo-upload' + statusClass + lockClass + mockupClass + '" data-photo-upload>' +
        zoneOpenTag + zoneInner + '</div>' +
        (errMsg
          ? '<p class="baw-error" id="' + errorId + '" data-photo-upload-error role="alert">' +
            escapeHtml(errMsg) +
            '</p>'
          : '') +
        (inWindowUpload ? '' : fileInputHtml) +
        '</div>' +
        actionsHtml +
        '</div>'

      return { stageHtml: stageHtml }
    }

    function openPhotoFilePicker() {
      var input = els.step.querySelector('[data-photo-input]')
      if (!input || input.disabled) return
      try { input.click() } catch (e) { /* ignore */ }
    }

    function wirePhotoUpload(entry, groupKey, productId) {
      var input = els.step.querySelector('[data-photo-input]')
      var triggers = els.step.querySelectorAll('[data-photo-upload-trigger]')
      var replaceBtn = els.step.querySelector('[data-photo-replace]')
      var removeBtn = els.step.querySelector('[data-photo-remove]')
      if (!input) return

      function livePhoto() {
        focusMemorialAddonProduct(entry, productId)
        if (!entry.addons.photo) entry.addons.photo = {}
        return entry.addons.photo
      }

      if (livePhoto().uploading) input.disabled = true

      function beginUpload(file) {
        var photo = livePhoto()
        if (!file || photo.uploading) return
        var clientErr = validatePhotoFileClient(file)
        if (clientErr) {
          photo.uploadError = true
          photo.uploadErrorMessage = clientErr
          photo.missingUploadError = false
          renderMemorialAddonPhotoStep(groupKey, productId)
          focusPhotoUploadZone()
          return
        }
        revokePhotoLocalPreview(entry)
        photo.fileName = file.name || 'photo'
        try {
          photo.localPreviewUrl = URL.createObjectURL(file)
        } catch (e) {
          photo.localPreviewUrl = null
        }
        photo.uploading = true
        photo.uploadError = false
        photo.uploadErrorMessage = null
        photo.missingUploadError = false
        photo.uploadGeneration = (photo.uploadGeneration || 0) + 1
        var generation = photo.uploadGeneration
        renderMemorialAddonPhotoStep(groupKey, productId)
        renderSummary()
        uploadPhoto(file).then(function (url) {
          var done = livePhoto()
          if (done.uploadGeneration !== generation) return
          done.uploading = false
          done.photoUrl = url
          done.photoPrintUrl = null
          done.uploadError = false
          done.uploadErrorMessage = null
          revokePhotoLocalPreview(entry)
          renderMemorialAddonPhotoStep(groupKey, productId)
          renderSummary()
          composePhotoForEntry(entry, groupKey)
        }).catch(function (err) {
          var failed = livePhoto()
          if (failed.uploadGeneration !== generation) return
          failed.uploading = false
          failed.uploadError = true
          failed.uploadErrorMessage =
            (err && err.message) || 'We couldn\\u2019t upload that photo. Please try again.'
          renderMemorialAddonPhotoStep(groupKey, productId)
          renderSummary()
        })
      }

      for (var t = 0; t < triggers.length; t++) {
        (function (trigger) {
          trigger.addEventListener('click', function (e) {
            // In-window <input type="file"> owns the click — don't preventDefault.
            if (e.target && e.target.closest && e.target.closest('[data-photo-input]')) return
            e.preventDefault()
            e.stopPropagation()
            if (livePhoto().uploading) return
            openPhotoFilePicker()
          })
          trigger.addEventListener('keydown', function (e) {
            if (e.key !== 'Enter' && e.key !== ' ') return
            e.preventDefault()
            e.stopPropagation()
            if (livePhoto().uploading) return
            openPhotoFilePicker()
          })
        })(triggers[t])
      }

      if (replaceBtn) {
        replaceBtn.addEventListener('click', function (e) {
          e.preventDefault()
          e.stopPropagation()
          openPhotoFilePicker()
        })
      }

      if (removeBtn) {
        removeBtn.addEventListener('click', function (e) {
          e.preventDefault()
          e.stopPropagation()
          // Keep the printed-photo add-on selected; only clear the file.
          livePhoto()
          resetPhotoUploadState(entry)
          a11yRestoreFocus = '[data-photo-upload-trigger], [data-photo-input]'
          renderMemorialAddonPhotoStep(groupKey, productId)
          renderSummary()
        })
      }

      input.addEventListener('change', function () {
        var file = input.files && input.files[0]
        // Allow re-picking the same file later.
        input.value = ''
        if (!file) return
        beginUpload(file)
      })
    }

    /** Ask the portal for a print master at the chosen size/orientation.
     *  Best-effort: Continue only needs \`Photo URL\`, so a compose failure never blocks
     *  the booking — ops still gets the original and can crop it by hand.
     *  Client preview always crops the original via CSS — compose does not flash a busy state. */

    function composePhotoForEntry(entry, groupKey) {
      var photo = entry && entry.addons && entry.addons.photo
      if (!photo || !photo.photoUrl) return
      var requestedFor = photo.photoUrl + '|' + photo.size + '|' + photo.orientation
      photo.composing = true
      photo.composeRequest = requestedFor

      composePhoto(photo.photoUrl, photo.size, photo.orientation).then(function (url) {
        // A replaced file or flipped orientation started a newer request — ignore this one.
        if (photo.composeRequest !== requestedFor) return
        photo.composing = false
        photo.photoPrintUrl = url
        renderSummary()
      }).catch(function () {
        if (photo.composeRequest !== requestedFor) return
        photo.composing = false
        photo.photoPrintUrl = null
        renderSummary()
      })
    }

    function needsEngravingSelected(entry) {
      if (!entry || !entry.addons || !entry.addons.engraving) return false
      var id = String(entry.addons.engraving.choiceId || '')
      if (!id || id === 'no') return false
      return true
    }

    /**
     * Deselect engraving — drop paid line from the order, keep draft text so
     * reselecting in the same session restores the family's inscription.
     */

    function clearEngravingSelection(entry) {
      if (!entry.addons.engraving) entry.addons.engraving = {}
      var eng = entry.addons.engraving
      eng.choiceId = null
      eng.choiceLabel = null
      eng.variant = null
      eng.missingNameError = false
      eng.missingLine2Error = false
    }

    /**
     * Keep fee / summary label in sync when engraving is selected.
     * Unselected → never on order (draft text may still exist for reselect).
     */

    function syncMemorialEngravingAddon(entry, priced, cardTitle) {
      if (!entry.addons.engraving) entry.addons.engraving = {}
      var eng = entry.addons.engraving
      eng.lines = normalizeNameplateLines(eng.lines)
      eng.font = null
      eng._fontTouched = undefined
      var line1 = sanitizeEngravingLine(String(eng.text || '').trim(), ENGRAVING_LINE1_MAX)
      var line2 = eng.lines === 2
        ? sanitizeEngravingLine(String(eng.textLine2 || '').trim(), ENGRAVING_LINE2_MAX)
        : ''
      eng.text = line1 || null
      eng.textLine2 = line2 || null
      if (eng.lines === 1) {
        eng.secondLineMode = null
      }
      if (!needsEngravingSelected(entry)) {
        eng.choiceLabel = null
        eng.choiceId = null
        eng.variant = null
        return
      }
      var combined = formatEngravingCombinedText(line1, line2)
      var title = String(cardTitle || '').trim() || 'Engraving'
      eng.choiceLabel = combined ? 'Engraving requested' : title
      eng.variant = priced || null
    }

    /** Seed line 1 from the pet name when the family hasn't typed engraving yet. */

    function preloadEngravingTextFromPet(engraving) {
      if (!engraving) return
      if ((engraving.text || '').trim() || (engraving.textLine2 || '').trim()) return
      var petName = state.pet && state.pet.name ? String(state.pet.name).trim() : ''
      if (petName) engraving.text = sanitizeEngravingLine(petName, ENGRAVING_LINE1_MAX)
    }

    /** Conversion open-state: One line + pet name.
     *  Keep One line unless the family explicitly picked One/Two this session
     *  (\`_linesTouched\`). Hand engraving has no font picker. */

    function ensureEngravingInscriptionDefaults(engraving) {
      if (!engraving) return
      if (engraving._linesTouched) {
        engraving.lines = normalizeNameplateLines(engraving.lines)
      } else {
        engraving.lines = 1
      }
      engraving.font = null
      engraving._fontTouched = undefined
      if (normalizeNameplateLines(engraving.lines) === 1) {
        engraving.textLine2 = null
        engraving.secondLineMode = null
      }
      preloadEngravingTextFromPet(engraving)
    }

    function memorialEngravingStepHeadline(itemLabel) {
      // Always “Add engraving for the {item}?” — never TE / stepCopy.
      return memorialAddonHeadline('engraving', itemLabel, config)
    }

    function engravingExampleVisuals() {
      return normalizeEngravingExampleVisuals(config && config.engravingExampleVisuals)
    }

    function renderEngravingHandGuidanceHtml() {
      // TE-editable; blank hides the bar. Missing config uses DEFAULT_STEP_COPY.
      var line = stepCopyText(config, 'guidanceEngravingHand')
      if (!line) return ''
      return wrapGuidanceBarHtml(
        '<p class="baw-guidance-line baw-step-footer-note baw-guidance-line--photo baw-guidance-line--engraving">' +
        escapeHtml(line) +
        '</p>',
        { variant: 'info' }
      )
    }

    function focusEngravingNameField() {
      var input = els.step && els.step.querySelector('#baw-engraving-line1')
      if (!input) return
      try { input.focus({ preventScroll: false }) } catch (err) {
        try { input.focus() } catch (err2) { /* ignore */ }
      }
    }

    function focusEngravingSecondLine() {
      var custom = els.step && els.step.querySelector('#baw-engraving-line2')
      var select = els.step && els.step.querySelector('#baw-engraving-line2-select')
      var target = custom || select
      if (!target) return
      try { target.focus({ preventScroll: false }) } catch (err) {
        try { target.focus() } catch (err2) { /* ignore */ }
      }
    }

    /** Continue / chrome forward guard — empty name when selected gets inline validation + focus.
     *  Two lines also requires a Second line. */

    function tryAdvanceMemorialEngravingStep(groupKey, productId) {
      var entry = getMemorialEntry(state, groupKey)
      var pid = resolveMemorialAddonProductId(state, config, groupKey, 'engraving', productId)
      focusMemorialAddonProduct(entry, pid)
      if (!entry || !needsEngravingSelected(entry)) {
        if (entry && entry.addons && entry.addons.engraving) {
          entry.addons.engraving.missingNameError = false
          entry.addons.engraving.missingLine2Error = false
        }
        return true
      }
      if (!entry.addons.engraving) entry.addons.engraving = {}
      var eng = entry.addons.engraving
      ensureEngravingInscriptionDefaults(eng)
      // Prefer live DOM so Continue sees the latest keystrokes.
      var input1 = els.step && els.step.querySelector('#baw-engraving-line1')
      var input2 = els.step && els.step.querySelector('#baw-engraving-line2')
      var select2 = els.step && els.step.querySelector('#baw-engraving-line2-select')
      if (input1) eng.text = sanitizeEngravingLine(input1.value, ENGRAVING_LINE1_MAX)
      if (normalizeNameplateLines(eng.lines) === 1) {
        eng.secondLineMode = null
        eng.textLine2 = null
        eng.missingLine2Error = false
      } else if (select2) {
        var line2Value = String(select2.value || '')
        if (line2Value === NAMEPLATE_SECOND_LINE_CUSTOM_VALUE) {
          eng.secondLineMode = 'custom'
          eng.textLine2 = input2
            ? sanitizeEngravingLine(input2.value, ENGRAVING_LINE2_MAX)
            : null
        } else if (line2Value) {
          eng.secondLineMode = 'suggestion'
          eng.textLine2 = sanitizeEngravingLine(line2Value, ENGRAVING_LINE2_MAX)
        } else {
          eng.secondLineMode = null
          eng.textLine2 = null
        }
      } else if (input2) {
        eng.secondLineMode = 'custom'
        eng.textLine2 = sanitizeEngravingLine(input2.value, ENGRAVING_LINE2_MAX)
      }
      eng.font = null
      eng._fontTouched = undefined
      if (!String(eng.text || '').trim()) {
        eng.missingNameError = true
        eng.missingLine2Error = false
        renderMemorialAddonEngravingStep(groupKey, pid)
        focusEngravingNameField()
        return false
      }
      eng.missingNameError = false
      if (
        normalizeNameplateLines(eng.lines) === 2 &&
        !String(eng.textLine2 || '').trim()
      ) {
        eng.missingLine2Error = true
        renderMemorialAddonEngravingStep(groupKey, pid)
        focusEngravingSecondLine()
        return false
      }
      eng.missingLine2Error = false
      return true
    }

    function renderEngravingCharCounter(count, max, id) {
      return (
        '<p class="baw-field-hint baw-engraving-counter" id="' + id + '" aria-live="polite">' +
        count + ' / ' + max +
        '</p>'
      )
    }

    /** One/Two line cards — same chrome as nameplate (engraving data attrs). */

    function renderEngravingLayoutHtml(engraving) {
      engraving = engraving || {}
      var lines = normalizeNameplateLines(engraving.lines)
      engraving.lines = lines
      return (
        '<div class="baw-nameplate-controls">' +
        '<div class="baw-nameplate-control" role="group" aria-label="Lines">' +
        '<div class="baw-nameplate-layout">' +
        [{ id: 1, label: 'One line' }, { id: 2, label: 'Two lines' }].map(function (opt) {
          var selected = opt.id === lines
          return (
            '<button type="button" class="baw-nameplate-layout__btn' + (selected ? ' is-selected' : '') + '"' +
            ' data-engraving-lines="' + opt.id + '" aria-pressed="' + (selected ? 'true' : 'false') + '"' +
            ' aria-label="' + escapeHtml(opt.label) + '">' +
            '<span class="baw-tile-check" aria-hidden="true"></span>' +
            '<span class="baw-nameplate-layout__label">' + escapeHtml(opt.label) + '</span>' +
            '</button>'
          )
        }).join('') +
        '</div></div></div>'
      )
    }

    /** Text-only inscription preview (no plate metals — engraving is on the urn).
     *  Uses \`.baw-nameplate-preview\` chrome so layoutNameplateDock can pin the
     *  same full-width cream band as nameplate personalise.
     *  Caption under preview is only the example link (when TE image is set) —
     *  hand-engraving warning lives solely in the guidance bar. */

    function renderEngravingInlinePreviewHtml(engraving, exampleVisuals) {
      engraving = engraving || {}
      exampleVisuals = exampleVisuals || null
      var lines = normalizeNameplateLines(engraving.lines)
      // Preview-only Lavanderia script — no Print/Script picker on engraving.
      var fontDef = nameplateFontDef('lavanderia')
      var line1 = String(engraving.text || '').trim()
      var line2 = lines === 2 ? String(engraving.textLine2 || '').trim() : ''
      var fontStyle = 'font-family: ' + fontDef.cssFamily + ';'
      var captionHtml = ''
      if (exampleVisuals && exampleVisuals.imageUrl) {
        captionHtml =
          '<p class="baw-field-hint baw-field-hint--quiet baw-nameplate-preview__caption baw-engraving-preview__caption">' +
          '<button type="button" class="baw-inline-link baw-engraving-example-link" data-engraving-example-link>' +
          escapeHtml(exampleVisuals.linkLabel || 'See an engraving example') +
          '</button></p>'
      }
      return (
        '<div class="baw-nameplate-preview baw-engraving-preview" aria-live="polite" data-engraving-live-preview>' +
        '<div class="baw-engraving-text-preview baw-engraving-text-preview--inline">' +
        '<p class="baw-engraving-text-preview__line baw-engraving-text-preview__line--name baw-font-' +
        fontDef.id + (line1 ? '' : ' is-empty') + '"' +
        ' data-engraving-preview-line1 style="' + fontStyle + '">' +
        escapeHtml(line1 || 'Your inscription') +
        '</p>' +
        (lines === 2
          ? '<p class="baw-engraving-text-preview__line baw-font-' + fontDef.id +
            (line2 ? '' : ' is-empty') + '"' +
            ' data-engraving-preview-line2 style="' + fontStyle + '">' +
            escapeHtml(line2 || 'Second line') +
            '</p>'
          : '') +
        '</div>' +
        captionHtml +
        '</div>'
      )
    }

    function updateEngravingInlinePreview(engraving) {
      if (!els.step || !engraving) return
      var preview = els.step.querySelector('[data-engraving-live-preview]')
      if (!preview) return
      var fontDef = nameplateFontDef('lavanderia')
      var line1 = String(engraving.text || '').trim()
      var line1El = preview.querySelector('[data-engraving-preview-line1]')
      if (line1El) {
        line1El.textContent = line1 || 'Your inscription'
        line1El.classList.toggle('is-empty', !line1)
        line1El.className =
          'baw-engraving-text-preview__line baw-engraving-text-preview__line--name baw-font-' +
          fontDef.id + (line1 ? '' : ' is-empty')
        line1El.style.fontFamily = fontDef.cssFamily
      }
      var line2El = preview.querySelector('[data-engraving-preview-line2]')
      if (line2El) {
        var line2 = String(engraving.textLine2 || '').trim()
        line2El.textContent = line2 || 'Second line'
        line2El.classList.toggle('is-empty', !line2)
        line2El.className =
          'baw-engraving-text-preview__line baw-font-' +
          fontDef.id + (line2 ? '' : ' is-empty')
        line2El.style.fontFamily = fontDef.cssFamily
      }
    }

    function renderEngravingSetupHtml(engraving, exampleVisuals) {
      ensureEngravingInscriptionDefaults(engraving)
      var lines = normalizeNameplateLines(engraving.lines)
      var line1 = engraving.text || ''
      var line2 = engraving.textLine2 || ''
      var suggestions = engravingSecondLineSuggestions(config)
      var secondMode = resolveNameplateSecondLineMode(engraving, suggestions)
      engraving.secondLineMode = secondMode
      var nameError = !!engraving.missingNameError && !String(line1).trim()
      var nameErrorId = 'baw-engraving-line1-error'
      var line2Error = !!engraving.missingLine2Error && !String(line2).trim()
      var line2ErrorId = 'baw-engraving-line2-error'

      var secondLineHtml = ''
      if (lines === 2) {
        var selectValue = secondMode === 'custom'
          ? NAMEPLATE_SECOND_LINE_CUSTOM_VALUE
          : (secondMode === 'suggestion' && line2 ? line2 : '')
        var optionsHtml =
          '<option value=""' + (selectValue === '' ? ' selected' : '') +
          '>Choose a second line\\u2026</option>' +
          suggestions.map(function (suggestion) {
            return (
              '<option value="' + escapeHtml(suggestion) + '"' +
              (selectValue === suggestion ? ' selected' : '') + '>' +
              escapeHtml(suggestion) +
              '</option>'
            )
          }).join('') +
          '<option value="' + NAMEPLATE_SECOND_LINE_CUSTOM_VALUE + '"' +
          (selectValue === NAMEPLATE_SECOND_LINE_CUSTOM_VALUE ? ' selected' : '') +
          '>Write your own</option>'

        secondLineHtml =
          '<div class="baw-nameplate-second-line baw-field--engraving-line2">' +
          '<label class="baw-label" for="baw-engraving-line2-select">Second line</label>' +
          '<select class="baw-select" id="baw-engraving-line2-select" data-engraving-line2-select' +
          ' aria-describedby="' +
          (line2Error ? line2ErrorId + ' ' : '') +
          'baw-engraving-line2-count"' +
          (line2Error ? ' aria-invalid="true"' : '') +
          '>' +
          optionsHtml +
          '</select>' +
          (secondMode === 'custom'
            ? '<input type="text" class="baw-input" id="baw-engraving-line2" data-engraving-line2' +
              ' maxlength="' + ENGRAVING_LINE2_MAX + '"' +
              ' autocomplete="off" spellcheck="false"' +
              ' aria-label="Custom second line" placeholder="e.g. Forever loved" value="' +
              escapeHtml(line2) + '"' +
              ' aria-describedby="' +
              (line2Error ? line2ErrorId + ' ' : '') +
              'baw-engraving-line2-count"' +
              (line2Error ? ' aria-invalid="true"' : '') +
              ' />'
            : '') +
          renderEngravingCharCounter(line2.length, ENGRAVING_LINE2_MAX, 'baw-engraving-line2-count') +
          (line2Error
            ? '<p class="baw-error" id="' + line2ErrorId + '" data-engraving-line2-error role="alert">' +
              'Please choose a second line.' +
              '</p>'
            : '') +
          '</div>'
      }

      return (
        '<div class="baw-photo-setup baw-engraving-setup" data-engraving-setup>' +
        renderEngravingLayoutHtml(engraving) +
        '<div class="baw-field baw-field--nameplate-text baw-field--engraving-text">' +
        '<div class="baw-nameplate-first-line">' +
        '<label class="baw-label" for="baw-engraving-line1">First line</label>' +
        '<input type="text" class="baw-input" id="baw-engraving-line1" data-engraving-line1' +
        ' maxlength="' + ENGRAVING_LINE1_MAX + '"' +
        ' autocomplete="off" spellcheck="false" placeholder="e.g. Bella" value="' +
        escapeHtml(line1) + '"' +
        (nameError
          ? ' aria-invalid="true" aria-describedby="' + nameErrorId + ' baw-engraving-line1-count"'
          : ' aria-describedby="baw-engraving-line1-count"') +
        ' />' +
        renderEngravingCharCounter(line1.length, ENGRAVING_LINE1_MAX, 'baw-engraving-line1-count') +
        (nameError
          ? '<p class="baw-error" id="' + nameErrorId + '" role="alert">' +
            'Please enter the name you would like engraved.' +
            '</p>'
          : '') +
        '</div>' +
        secondLineHtml +
        '</div>' +
        renderEngravingInlinePreviewHtml(engraving, exampleVisuals) +
        '</div>'
      )
    }

    function renderMemorialAddonEngravingStep(groupKey, productId) {
      var entry = getMemorialEntry(state, groupKey)
      var pid = resolveMemorialAddonProductId(state, config, groupKey, 'engraving', productId)
      focusMemorialAddonProduct(entry, pid)
      if (!entry.addons.engraving) entry.addons.engraving = {}
      var engraving = entry.addons.engraving
      // Migrate any leftover multiline single-field drafts in-session.
      if (engraving.text && !engraving.textLine2 && String(engraving.text).indexOf('\\n') !== -1) {
        var splitEng = splitLegacyEngravingText(engraving.text)
        engraving.text = splitEng.line1 || null
        engraving.textLine2 = splitEng.line2 || null
      }
      if (engraving.text) engraving.text = sanitizeEngravingLine(engraving.text, ENGRAVING_LINE1_MAX)
      if (engraving.textLine2) {
        engraving.textLine2 = sanitizeEngravingLine(engraving.textLine2, ENGRAVING_LINE2_MAX)
      }
      ensureEngravingInscriptionDefaults(engraving)

      var catalog = getMemorialCatalog(state, config)
      var source = memorialAddonSourceForProduct(
        state, config, groupKey, catalog, 'engraving', pid
      )
      var npMetal = null
      if (typeof AngelAshesBookingMemorials !== 'undefined' && AngelAshesBookingMemorials.nameplateMetalForProduct) {
        npMetal = AngelAshesBookingMemorials.nameplateMetalForProduct(entry, pid)
      } else if (entry && entry.itemAddons && pid != null) {
        var npBucket = entry.itemAddons[String(pid)]
        var npAddon = npBucket && npBucket.nameplate
        if (npAddon && String(npAddon.choiceId || '') !== 'no') npMetal = npAddon.colour || null
      }
      var itemContext = memorialAddonItemContext(catalog, groupKey, source, npMetal)
      var flags = (source && source.flags) || {}
      var feeProduct = flags.engravingFeeProduct || null
      var priced = firstPricedFeeVariant(feeProduct)
      var selected = needsEngravingSelected(entry)
      // Heal missing fee variant after draft restore so summary/cart stay priced.
      if (selected && !engraving.variant && priced) {
        engraving.variant = priced
      }
      // Theme Editor card copy: blank title → “Engraving”; blank subtitle → default desc.
      var engravingCardTitle = String(flags.engravingCardTitle || '').trim() || 'Engraving'
      var engravingCardSubtitle =
        String(flags.engravingCardSubtitle || '').trim() || ENGRAVING_CARD_SUBTITLE_DEFAULT
      syncMemorialEngravingAddon(entry, priced, engravingCardTitle)

      var exampleVisuals = engravingExampleVisuals()
      var priceLabel = priced ? money(priced.price) : ''
      var mockupThumb = renderPersonalisePhotoMockupHtml(
        entry.addons && entry.addons.photo,
        flags,
        'baw-photo-addon__thumb baw-photo-addon__thumb--mockup',
        npMetal,
        itemContext && itemContext.label
      )
      var engravingThumbSrc = itemContext.personaliseImage || itemContext.image
      var thumb = mockupThumb
        ? mockupThumb
        : engravingThumbSrc
          ? (
            '<img class="baw-photo-addon__thumb" src="' + escapeHtml((typeof AngelAshesBookingMemorials !== 'undefined' && AngelAshesBookingMemorials.sizedShopifyImageUrl ? AngelAshesBookingMemorials.sizedShopifyImageUrl(engravingThumbSrc, AngelAshesBookingMemorials.PHOTO_ADDON_THUMB_PX || 240) : engravingThumbSrc)) + '"' +
            ' alt="' + escapeHtml(itemContext.label || 'Memorial product') + '"' +
            ' loading="eager" decoding="async" width="64" height="64" />'
          )
          : '<span class="baw-photo-addon__thumb baw-photo-addon__thumb--empty" aria-hidden="true"></span>'

      var setupHtml = selected ? renderEngravingSetupHtml(engraving, exampleVisuals) : ''
      var guidanceHtml = selected ? renderEngravingHandGuidanceHtml() : ''
      // No step subtitle on engraving ask — omit .baw-helper entirely so H1→card
      // spacing matches photo ask / other single-headline steps (no empty paragraph,
      // no reserved gap). Stale TE “Optional — leave blank…” cannot reappear.
      var html =
        '<div class="baw-step-inner baw-step-inner--photo baw-step-inner--engraving' +
        (selected ? ' is-photo-open' : '') + '">' +
        stepIntroHtml(memorialEngravingStepHeadline(itemContext.label), '') +
        '<div class="baw-photo-step' + (selected ? ' is-open' : '') + '">' +
        '<button type="button" class="baw-photo-addon' + (selected ? ' is-selected' : '') + '"' +
        ' data-engraving-addon role="checkbox" aria-checked="' + (selected ? 'true' : 'false') + '"' +
        ' aria-controls="baw-engraving-setup">' +
        '<span class="baw-photo-addon__check" aria-hidden="true"></span>' +
        thumb +
        '<span class="baw-photo-addon__body">' +
        '<span class="baw-photo-addon__title">' + escapeHtml(engravingCardTitle) + '</span>' +
        '<span class="baw-photo-addon__desc">' + escapeHtml(engravingCardSubtitle) + '</span>' +
        '</span>' +
        (priceLabel
          ? '<span class="baw-photo-addon__price">' + priceLabel + '</span>'
          : '<span class="baw-photo-addon__price baw-photo-addon__price--empty" aria-hidden="true"></span>') +
        '</button>' +
        // Decline under the Engraving card — hidden once the add-on is selected.
        (selected ? '' : optionalSkipButtonHtml('No thanks — continue without engraving')) +
        (selected
          ? '<div id="baw-engraving-setup" class="baw-photo-setup-wrap">' + setupHtml + '</div>'
          : '') +
        '</div>' +
        // GuidanceBar sits outside the fields; mountStepFooter pins it above Continue.
        guidanceHtml +
        '<div class="baw-actions">' +
        '<button type="button" class="baw-continue" data-baw-continue>Continue</button>' +
        '</div>' +
        '</div>'

      els.step.innerHTML = html
      if (typeof wirePhotoPreviewDecodeIn === 'function') wirePhotoPreviewDecodeIn(els.step)

      var addonBtn = els.step.querySelector('[data-engraving-addon]')
      if (addonBtn) {
        addonBtn.addEventListener('click', function () {
          // Re-focus this product — renderSummary/buildLedger may have left
          // entry.addons pointing at another multi-select urn's bucket.
          focusMemorialAddonProduct(entry, pid)
          if (!entry.addons.engraving) entry.addons.engraving = {}
          var liveEng = entry.addons.engraving
          if (selected) {
            // Deselect — drop paid line; keep draft text for reselect.
            clearEngravingSelection(entry)
          } else {
            liveEng.choiceId = 'yes'
            liveEng.choiceLabel = engravingCardTitle
            liveEng.variant = priced
            liveEng.missingNameError = false
            ensureEngravingInscriptionDefaults(liveEng)
            syncMemorialEngravingAddon(entry, priced, engravingCardTitle)
            queueMobileReveal('[data-engraving-setup]', '#baw-engraving-line1')
          }
          a11yRestoreFocus = '[data-engraving-addon]'
          renderMemorialAddonEngravingStep(groupKey, pid)
          renderSummary()
          if (typeof scheduleDraftSave === 'function') scheduleDraftSave()
        })
      }

      var exampleLinkBtn = els.step.querySelector('[data-engraving-example-link]')
      if (exampleLinkBtn) {
        exampleLinkBtn.addEventListener('click', function () {
          openEngravingExampleModal(exampleVisuals, '[data-engraving-example-link]')
        })
      }

      function liveEngraving() {
        focusMemorialAddonProduct(entry, pid)
        if (!entry.addons.engraving) entry.addons.engraving = {}
        return entry.addons.engraving
      }

      function syncEngravingCounter(input, counterId, max) {
        var counter = els.step.querySelector('#' + counterId)
        if (!counter || !input) return
        counter.textContent = String(input.value || '').length + ' / ' + max
      }

      function syncEngravingLine2Counter(length) {
        var counter = els.step.querySelector('#baw-engraving-line2-count')
        if (!counter) return
        counter.textContent = String(length || 0) + ' / ' + ENGRAVING_LINE2_MAX
      }

      function onEngravingInput(e) {
        var liveEng = liveEngraving()
        var input1 = els.step.querySelector('#baw-engraving-line1')
        var input2 = els.step.querySelector('#baw-engraving-line2')
        var target = e && e.target
        if (target && (target === input1 || target === input2)) {
          var max = target === input1 ? ENGRAVING_LINE1_MAX : ENGRAVING_LINE2_MAX
          var cleaned = sanitizeEngravingLine(target.value, max)
          if (cleaned !== target.value) {
            var start = target.selectionStart
            target.value = cleaned
            try {
              var pos = Math.min(cleaned.length, typeof start === 'number' ? start : cleaned.length)
              target.setSelectionRange(pos, pos)
            } catch (err) { /* ignore */ }
          }
        }
        liveEng.text = input1 ? sanitizeEngravingLine(input1.value, ENGRAVING_LINE1_MAX) : ''
        if (input2) {
          liveEng._line2Touched = true
          liveEng.secondLineMode = 'custom'
          liveEng.textLine2 = sanitizeEngravingLine(input2.value, ENGRAVING_LINE2_MAX)
        }
        if (String(liveEng.text || '').trim()) liveEng.missingNameError = false
        if (String(liveEng.textLine2 || '').trim()) liveEng.missingLine2Error = false
        syncEngravingCounter(input1, 'baw-engraving-line1-count', ENGRAVING_LINE1_MAX)
        if (input2) syncEngravingCounter(input2, 'baw-engraving-line2-count', ENGRAVING_LINE2_MAX)
        // Clear inline error chrome without a full re-render while typing.
        if (!liveEng.missingNameError && input1) {
          input1.removeAttribute('aria-invalid')
          var errEl = els.step.querySelector('#baw-engraving-line1-error')
          if (errEl && errEl.parentNode) errEl.parentNode.removeChild(errEl)
        }
        if (!liveEng.missingLine2Error) {
          var select2El = els.step.querySelector('#baw-engraving-line2-select')
          ;[select2El, input2].forEach(function (el) {
            if (!el) return
            el.removeAttribute('aria-invalid')
          })
          var err2El = els.step.querySelector('#baw-engraving-line2-error')
          if (err2El && err2El.parentNode) err2El.parentNode.removeChild(err2El)
        }
        syncMemorialEngravingAddon(entry, priced, engravingCardTitle)
        updateEngravingInlinePreview(liveEng)
        setContinueEnabled(canAdvanceFrom(state.currentStepId))
        renderSummary()
        if (typeof scheduleDraftSave === 'function') scheduleDraftSave()
      }

      var input1El = els.step.querySelector('#baw-engraving-line1')
      var input2El = els.step.querySelector('#baw-engraving-line2')
      if (input1El) input1El.addEventListener('input', onEngravingInput)
      if (input2El) input2El.addEventListener('input', onEngravingInput)

      var line2Select = els.step.querySelector('[data-engraving-line2-select]')
      if (line2Select) {
        line2Select.addEventListener('change', function () {
          var liveEng = liveEngraving()
          var previousMode = liveEng.secondLineMode
          var value = String(line2Select.value || '')
          liveEng._line2Touched = true
          if (value === NAMEPLATE_SECOND_LINE_CUSTOM_VALUE) {
            liveEng.secondLineMode = 'custom'
            // Fresh custom field — don't carry a suggestion string into the input.
            liveEng.textLine2 = null
            a11yRestoreFocus = '#baw-engraving-line2'
            renderMemorialAddonEngravingStep(groupKey, pid)
            renderSummary()
            if (typeof scheduleDraftSave === 'function') scheduleDraftSave()
            return
          }
          if (value) {
            liveEng.secondLineMode = 'suggestion'
            liveEng.textLine2 = sanitizeEngravingLine(value, ENGRAVING_LINE2_MAX)
            liveEng.missingLine2Error = false
            if (previousMode === 'custom') {
              renderMemorialAddonEngravingStep(groupKey, pid)
              renderSummary()
              if (typeof scheduleDraftSave === 'function') scheduleDraftSave()
              return
            }
            syncEngravingLine2Counter(String(liveEng.textLine2 || '').length)
            syncMemorialEngravingAddon(entry, priced, engravingCardTitle)
            updateEngravingInlinePreview(liveEng)
            setContinueEnabled(canAdvanceFrom(state.currentStepId))
            renderSummary()
            if (typeof scheduleDraftSave === 'function') scheduleDraftSave()
            return
          }
          liveEng.secondLineMode = null
          liveEng.textLine2 = null
          liveEng._line2Touched = false
          if (previousMode === 'custom') {
            renderMemorialAddonEngravingStep(groupKey, pid)
            renderSummary()
            if (typeof scheduleDraftSave === 'function') scheduleDraftSave()
            return
          }
          syncEngravingLine2Counter(0)
          syncMemorialEngravingAddon(entry, priced, engravingCardTitle)
          updateEngravingInlinePreview(liveEng)
          setContinueEnabled(canAdvanceFrom(state.currentStepId))
          renderSummary()
          if (typeof scheduleDraftSave === 'function') scheduleDraftSave()
        })
      }

      // Bind One/Two directly on the buttons (nameplate stickiness lesson).
      Array.prototype.forEach.call(
        els.step.querySelectorAll('button.baw-nameplate-layout__btn[data-engraving-lines]'),
        function (btn) {
          btn.addEventListener('click', function (e) {
            e.preventDefault()
            var liveEng = liveEngraving()
            var nextLines = normalizeNameplateLines(btn.getAttribute('data-engraving-lines'))
            liveEng._linesTouched = true
            var currentLines = normalizeNameplateLines(liveEng.lines)
            if (currentLines === nextLines && liveEng.lines === nextLines &&
                btn.classList.contains('is-selected')) {
              return
            }
            Array.prototype.forEach.call(
              els.step.querySelectorAll('button.baw-nameplate-layout__btn[data-engraving-lines]'),
              function (b) {
                var on = normalizeNameplateLines(b.getAttribute('data-engraving-lines')) === nextLines
                b.classList.toggle('is-selected', on)
                b.setAttribute('aria-pressed', on ? 'true' : 'false')
              }
            )
            liveEng.lines = nextLines
            if (nextLines === 1) {
              liveEng.textLine2 = null
              liveEng.secondLineMode = null
              liveEng._line2Touched = false
              liveEng.missingLine2Error = false
              a11yRestoreFocus =
                'button.baw-nameplate-layout__btn[data-engraving-lines="1"]'
            } else {
              a11yRestoreFocus = '#baw-engraving-line2-select'
            }
            renderMemorialAddonEngravingStep(groupKey, pid)
            renderSummary()
            if (typeof scheduleDraftSave === 'function') scheduleDraftSave()
          })
        }
      )

      var continueBtn = els.step.querySelector('[data-baw-continue]')
      if (continueBtn) {
        continueBtn.addEventListener('click', function () { goNext() })
      }
      var engravingSkip = els.step.querySelector('[data-baw-optional-skip]')
      if (engravingSkip) {
        engravingSkip.addEventListener('click', function () {
          focusMemorialAddonProduct(entry, pid)
          clearEngravingSelection(entry)
          renderSummary()
          goNext()
        })
      }

      mountStepFooter()
      // Disabled Continue does not receive clicks — let the actions strip catch them
      // so empty-name validation can still focus the field.
      var actionsEl = els.footer && els.footer.querySelector('.baw-actions')
      if (actionsEl && !actionsEl.getAttribute('data-engraving-validate-bound')) {
        actionsEl.setAttribute('data-engraving-validate-bound', '1')
        actionsEl.addEventListener('click', function () {
          var btn = actionsEl.querySelector('[data-baw-continue]')
          if (btn && btn.disabled) {
            tryAdvanceMemorialEngravingStep(groupKey, pid)
          }
        })
      }
      // Pin engraving preview in the same cream dock as nameplate (full-width band).
      if (selected) layoutNameplateDock()
      syncFunnelChromePadding()
      // Two-lines: after densify CSS, ensure Second line is fully in the scroll
      // band (not half-hidden under the cream dock). Double-rAF waits for layout.
      if (selected && normalizeNameplateLines(engraving.lines) === 2) {
        var ensureEngravingL2Visible = function () {
          var scroll = els.step && els.step.querySelector(
            '.baw-step-inner--engraving.is-nameplate-docked > .baw-nameplate-scroll'
          )
          var target = els.step && els.step.querySelector('.baw-nameplate-second-line')
          if (!scroll || !target) return
          var sRect = scroll.getBoundingClientRect()
          var tRect = target.getBoundingClientRect()
          var pad = 8
          if (tRect.bottom > sRect.bottom - pad) {
            scroll.scrollTop += (tRect.bottom - (sRect.bottom - pad))
          } else if (tRect.top < sRect.top + pad) {
            scroll.scrollTop -= ((sRect.top + pad) - tRect.top)
          }
        }
        window.requestAnimationFrame(function () {
          window.requestAnimationFrame(ensureEngravingL2Visible)
        })
      }
      setContinueEnabled(canAdvanceFrom(state.currentStepId))
      refreshChrome()
      flushMobileReveal()
      restoreA11yFocus()
      renderSummary()
    }

    /* ------------------------------- return steps ------------------------------- */

    function uploadPhoto(file) {
      if (!config.photoUploadUrl) {
        return Promise.reject(new Error(
          'Photo upload is not configured for this storefront. Please contact Angel Ashes, or deselect Printed photo to continue without one.'
        ))
      }
      var prepare =
        typeof preparePhotoForUpload === 'function'
          ? preparePhotoForUpload
          : (typeof AngelAshesBookingMemorialUI !== 'undefined' &&
              AngelAshesBookingMemorialUI.preparePhotoForUpload)
      if (typeof prepare !== 'function') {
        prepare = function (nextFile) { return Promise.resolve(nextFile) }
      }
      return Promise.resolve(prepare(file)).then(function (prepared) {
        var form = new FormData()
        form.append('file', prepared)
        var controller = typeof AbortController === 'function' ? new AbortController() : null
        var timeoutMs =
          typeof PHOTO_UPLOAD_TIMEOUT_MS === 'number' ? PHOTO_UPLOAD_TIMEOUT_MS : 45000
        var timeoutId = null
        if (controller) {
          timeoutId = setTimeout(function () {
            try { controller.abort() } catch (e) { /* ignore */ }
          }, timeoutMs)
        }
        var fetchOpts = { method: 'POST', body: form }
        if (controller) fetchOpts.signal = controller.signal
        return fetch(config.photoUploadUrl, fetchOpts).then(function (res) {
          if (timeoutId) clearTimeout(timeoutId)
          return res.text().then(function (text) {
            var data = null
            try { data = text ? JSON.parse(text) : null } catch (e) { data = null }
            if (!res.ok) {
              var msg = (data && data.error) || ('Upload failed (' + res.status + '). Please try again.')
              if (res.status === 401 || res.status === 403) {
                msg = isBookingThemeEditorPreview()
                  ? 'Photo upload isn\\u2019t available in Theme Editor preview. Test on the live storefront after the portal update is deployed, or call us for help.'
                  : 'Photo upload is temporarily unavailable from this site. Please try again later, or call us and we can help.'
              } else if (res.status === 503) {
                // Portal responds 503 “Photo uploads are not configured.” when the ops
                // app has no blob storage token — an ops config gap, not a CORS/network
                // problem, so say so plainly instead of blaming the connection.
                msg = 'Photo uploads are temporarily unavailable — our photo service isn\\u2019t switched on yet. Deselect Printed photo to continue, and we\\u2019ll collect your photo by email.'
              } else if (res.status === 413) {
                msg = (data && data.error) || 'That photo is too large (max 8MB). Please choose a smaller image.'
              } else if (res.status === 415) {
                msg = (data && data.error) || 'Please choose a JPEG, PNG, WEBP or HEIC photo.'
              } else if (res.status === 429) {
                msg = (data && data.error) || 'Too many uploads just now. Please wait a moment and try again.'
              }
              throw new Error(msg)
            }
            if (!data || !data.url) {
              throw new Error('Upload did not return a photo URL. Please try again.')
            }
            return data.url
          })
        }, function (err) {
          if (timeoutId) clearTimeout(timeoutId)
          var aborted =
            (err && err.name === 'AbortError') ||
            (controller && controller.signal && controller.signal.aborted)
          if (aborted) {
            throw new Error(
              'That photo is taking too long to upload. Check your connection and try again, or choose a smaller image.'
            )
          }
          throw new Error(
            isBookingThemeEditorPreview()
              ? 'Photo upload can\\u2019t run in Theme Editor preview (the editor often blocks or can\\u2019t reach the upload service). Test on the live storefront, or call us for help.'
              : 'We couldn\\u2019t reach the photo upload service. Check your connection and try again, or deselect Printed photo to continue without one.'
          )
        })
      })
    }

    /** Compose endpoint lives beside the upload endpoint on the same ops portal, so it is
     *  derived rather than configured twice in the Theme Editor. */

    function composePhoto(sourceUrl, size, orientation) {
      var url = photoComposeUrl()
      if (!url) return Promise.reject(new Error('Compose endpoint is not configured.'))
      return fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ sourceUrl: sourceUrl, size: size, orientation: orientation }),
      }).then(function (res) {
        return res.text().then(function (text) {
          var data = null
          try { data = text ? JSON.parse(text) : null } catch (e) { data = null }
          if (!res.ok || !data || !data.url) {
            throw new Error((data && data.error) || ('Compose failed (' + res.status + ').'))
          }
          return data.url
        })
      })
    }

    /** Shopify Theme Editor preview only. A plain iframe is NOT enough — the storefront
     *  is embedded in all sorts of frames (apps, previews, page builders), and treating
     *  those as “editor preview” produced misleading upload errors on the live site. */`
  var INSTALL_EXPORTS = [
    "clearAshesPlacementModalHost",
    "memorialUrnVesselCount",
    "memorialUrnDistinctCount",
    "memorialMoney",
    "memorialOptionFaceHtml",
    "memorialOptionTrayHtml",
    "memorialOrderQtyHtml",
    "memorialProductCardHtml",
    "openMemorialOverlay",
    "renderMemorialItemStep",
    "tryAdvanceMemorialAshesPlacement",
    "memorialPhotoStepHeadline",
    "memorialPhotoStepSubtitle",
    "photoUploadIconSvg",
    "photoOrientationIconSvg",
    "renderPhotoMockupPreview",
    "renderPhotoPrintPreview",
    "photoMockupFromAddon",
    "renderPersonalisePhotoMockupHtml",
    "sizePhotoMockupFromFrame",
    "wirePhotoPreviewDecodeIn",
    "resizePhotoMockupsIn",
    "syncReviewNameplatePlateSizes",
    "wirePhotoPreviewDecode",
    "renderPhotoPrintGuidanceHtml",
    "photoFileLooksAllowed",
    "validatePhotoFileClient",
    "focusPhotoUploadZone",
    "tryAdvanceMemorialPhotoStep",
    "renderMemorialAddonPhotoStep",
    "resetPhotoUploadState",
    "wirePhotoOrientationToggle",
    "needsPhotoUpload",
    "revokePhotoLocalPreview",
    "renderPhotoSetupHtml",
    "renderPhotoOrientationToggleHtml",
    "renderPhotoUploadParts",
    "openPhotoFilePicker",
    "wirePhotoUpload",
    "composePhotoForEntry",
    "needsEngravingSelected",
    "clearEngravingSelection",
    "syncMemorialEngravingAddon",
    "preloadEngravingTextFromPet",
    "ensureEngravingInscriptionDefaults",
    "memorialEngravingStepHeadline",
    "engravingExampleVisuals",
    "renderEngravingHandGuidanceHtml",
    "focusEngravingNameField",
    "tryAdvanceMemorialEngravingStep",
    "renderEngravingCharCounter",
    "renderEngravingLayoutHtml",
    "renderEngravingInlinePreviewHtml",
    "updateEngravingInlinePreview",
    "renderEngravingSetupHtml",
    "renderMemorialAddonEngravingStep",
    "uploadPhoto",
    "composePhoto"
  ]

  function install(ctx) {
    var api = {}
    // Sloppy-mode runner: with (ctx) resolves free identifiers to the live
    // wizard context (state, els, render, helpers). Function declarations in
    // INSTALL_SRC become install-locals and are exported onto api.
    var body = 'with (ctx) {\n' + INSTALL_SRC + '\n'
    for (var i = 0; i < INSTALL_EXPORTS.length; i++) {
      var name = INSTALL_EXPORTS[i]
      body += 'api[' + JSON.stringify(name) + '] = ' + name + ';\n'
    }
    body += '}'
    var runner = new Function('ctx', 'api', body)
    runner(ctx, api)
    return api
  }

  global.AngelAshesBookingMemorialUI = {
    create: create,
    install: install,
  }
})(typeof window !== 'undefined' ? window : this)
